import type { TranslationLanguage } from '@common/ts/types';

/**
 * Strings rendered to the respondent across the chat app — error pages,
 * ErrorBoundary fallback, send-message / video-recording toasts, and a
 * few miscellaneous chat UI toasts. Localised across all 65 canonical
 * TranslationLanguage codes.
 *
 * Consumers look up via `getRespondentUiTranslation(languageCode)`,
 * which falls back to English for an unknown/legacy language code.
 *
 * The `satisfies Record<TranslationLanguage, RespondentUiTranslation>`
 * constraint means adding a new canonical language or renaming a key
 * fails tsc until every block is updated — this is our compile-time
 * guard against drift (TELV1-238).
 */
export interface RespondentUiTranslation {
  // Error pages (ChatRedirectedErrorPage)
  telletDoesNotExistTitle: string;
  projectDoesNotExistTitle: string;
  projectDoesNotExistMessage: string;
  maximumParticipantsTitle: string;
  maximumParticipantsMessage: string;
  somethingMissingTitle: string;
  somethingMissingMessage: string;
  projectCompletedTitle: string;
  projectCompletedMessage: string;
  projectInDraftOrReadyStatusTitle: string;
  projectInDraftOrReadyStatusMessage: string;
  // Quota-full redirect countdown
  redirectingIn: string;
  seconds: string;
  cancel: string;
  returnToPanel: string;
  // ErrorBoundary fallback
  errorBoundaryTitle: string;
  errorBoundaryMessage: string;
  errorBoundaryReloadButton: string;
  // useSendMessage hook toasts
  messageProcessingError: string;
  microphoneProblemTextChat: string;
  microphoneProblemVoiceChat: string;
  responseTimeoutError: string;
  noResponseError: string;
  connectionInterrupted: string;
  // useVideoRecording hook toasts
  videoStartError: string;
  videoCameraNotSupported: string;
  videoPauseError: string;
  videoResumeError: string;
  videoStopError: string;
  videoSwitchCameraError: string;
  videoSendError: string;
  // Miscellaneous chat toasts
  welcomeBack: string;
  unsupportedFileType: string;
  fileTooLarge: string;
  audioCloseError: string;
}

export const RESPONDENT_UI_TRANSLATION = {
  af: {
    telletDoesNotExistTitle: 'Tellet bestaan nie',
    projectDoesNotExistTitle: 'Projek bestaan nie',
    projectDoesNotExistMessage: 'Ons is jammer, maar hierdie projek bestaan nie.',
    maximumParticipantsTitle: 'Maksimum aantal deelnemers',
    maximumParticipantsMessage:
      'Hierdie studie het sy maksimum aantal reaksies bereik en aanvaar nie meer nuwe reaksies nie.',
    somethingMissingTitle: 'Iets ontbreek',
    somethingMissingMessage: 'Hierdie bladsy is nie beskikbaar nie of bestaan nie.',
    projectCompletedTitle: 'Projek is voltooi',
    projectCompletedMessage: 'Hierdie studie is voltooi en aanvaar nie meer nuwe reaksies nie.',
    projectInDraftOrReadyStatusTitle: 'Projek is nie gepubliseer nie',
    projectInDraftOrReadyStatusMessage:
      'Omdat die studie nog nie gepubliseer is nie, aanvaar dit nie reaksies nie.',
    redirectingIn: 'Herlei oor',
    seconds: 'sekondes...',
    cancel: 'Kanselleer',
    returnToPanel: 'Keer terug na paneel',
    errorBoundaryTitle: 'Iets het verkeerd geloop',
    errorBoundaryMessage:
      "'n Onverwagte fout het voorgekom. Herlaai asseblief die bladsy om voort te gaan.",
    errorBoundaryReloadButton: 'Herlaai bladsy',
    messageProcessingError:
      'Oeps! Dit lyk of ons probleme het om jou boodskap te verwerk. Probeer dit asseblief weer stuur.',
    microphoneProblemTextChat:
      "Dit lyk of daar 'n probleem met jou mikrofoon is. Tik asseblief jou antwoorde met die sleutelbordknoppie in.",
    microphoneProblemVoiceChat:
      "Dit lyk of daar 'n probleem met jou mikrofoon is. Gaan asseblief jou blaaier-toestemmings na en probeer weer.",
    responseTimeoutError: 'Reaksie neem te lank. Probeer asseblief weer.',
    noResponseError: 'Geen reaksie opgespoor nie. Probeer asseblief weer.',
    connectionInterrupted: 'Verbinding onderbreek. Jy kan jou boodskap weer stuur.',
    videoStartError: 'Kon nie video-opname begin nie. Probeer asseblief weer.',
    videoCameraNotSupported:
      "Kamera word nie ondersteun nie. Probeer asseblief met 'n ander blaaier.",
    videoPauseError: 'Kon nie video-opname onderbreek nie.',
    videoResumeError: 'Kon nie video-opname hervat nie.',
    videoStopError: 'Kon nie video-opname stop nie. Probeer asseblief weer.',
    videoSwitchCameraError: 'Kon nie kamera wissel nie. Probeer asseblief weer.',
    videoSendError: 'Kon nie video-boodskap stuur nie.',
    welcomeBack: 'Welkom terug!',
    unsupportedFileType:
      "Lêertipe word nie ondersteun nie. Laai asseblief 'n JPG-, PNG-, GIF- of MP4-lêer op.",
    fileTooLarge: 'Hierdie lêer is te groot. Die maksimum grootte is 200 MB.',
    audioCloseError: 'Fout met sluit van oudio:',
  },
  am: {
    telletDoesNotExistTitle: 'Tellet የለም',
    projectDoesNotExistTitle: 'ፕሮጀክቱ የለም',
    projectDoesNotExistMessage: 'ይቅርታ፣ ይህ ፕሮጀክት የለም።',
    maximumParticipantsTitle: 'ከፍተኛ የተሳታፊዎች ብዛት',
    maximumParticipantsMessage: 'ይህ ጥናት ከፍተኛ የምላሾች ብዛቱን ደርሷል እና አዲስ ምላሾችን አይቀበልም።',
    somethingMissingTitle: 'አንድ ነገር ይጎድላል',
    somethingMissingMessage: 'ይህ ገጽ የሌለ ወይም የማይገኝ ነው።',
    projectCompletedTitle: 'ፕሮጀክቱ ተጠናቋል',
    projectCompletedMessage: 'ይህ ጥናት ተጠናቋል እና አዲስ ምላሾችን አይቀበልም።',
    projectInDraftOrReadyStatusTitle: 'ፕሮጀክቱ አልታተመም',
    projectInDraftOrReadyStatusMessage: 'ጥናቱ ገና ስላልታተመ ምላሾችን አይቀበልም።',
    redirectingIn: 'እንደገና የሚዘዋወረው በ',
    seconds: 'ሰከንዶች...',
    cancel: 'ሰርዝ',
    returnToPanel: 'ወደ ፓነል ተመለስ',
    errorBoundaryTitle: 'አንድ ስህተት ተፈጠረ',
    errorBoundaryMessage: 'ያልተጠበቀ ስህተት ተፈጥሯል። እባክዎ ለመቀጠል ገጹን ደግመው ይጫኑ።',
    errorBoundaryReloadButton: 'ገጹን እንደገና ጫን',
    messageProcessingError: 'ይቅርታ! መልእክትዎን ለማስኬድ ችግር እያጋጠመን ይመስላል። እባክዎ እንደገና ለመላክ ይሞክሩ።',
    microphoneProblemTextChat: 'ከማይክሮፎንዎ ጋር ችግር ያለ ይመስላል። እባክዎ መልሶችዎን የቁልፍ ሰሌዳውን ቁልፍ በመጠቀም ይተይቡ።',
    microphoneProblemVoiceChat: 'ከማይክሮፎንዎ ጋር ችግር ያለ ይመስላል። እባክዎ የአሳሽዎን ፍቃዶች ያረጋግጡ እና እንደገና ይሞክሩ።',
    responseTimeoutError: 'ምላሹ በጣም ረጅም ጊዜ ወስዷል። እባክዎ እንደገና ይሞክሩ።',
    noResponseError: 'ምንም ምላሽ አልተገኘም። እባክዎ እንደገና ይሞክሩ።',
    connectionInterrupted: 'ግንኙነቱ ተቋርጧል። መልእክትዎን እንደገና መላክ ይችላሉ።',
    videoStartError: 'የቪዲዮ ቀረጻ መጀመር አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoCameraNotSupported: 'ካሜራ አይደገፍም። እባክዎ በተለየ አሳሽ ይሞክሩ።',
    videoPauseError: 'የቪዲዮ ቀረጻን ማቆም አልተሳካም።',
    videoResumeError: 'የቪዲዮ ቀረጻን መቀጠል አልተሳካም።',
    videoStopError: 'የቪዲዮ ቀረጻን ማቆም አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoSwitchCameraError: 'ካሜራ መቀየር አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoSendError: 'የቪዲዮ መልእክት መላክ አልተሳካም።',
    welcomeBack: 'እንኳን ደህና መጡ!',
    unsupportedFileType: 'የማይደገፍ የፋይል ዓይነት። እባክዎ JPG፣ PNG፣ GIF ወይም MP4 ፋይል ይጫኑ።',
    fileTooLarge: 'ይህ ፋይል በጣም ትልቅ ነው። ከፍተኛው መጠን 200 MB ነው።',
    audioCloseError: 'ድምፅን በመዝጋት ላይ ስህተት:',
  },
  ar: {
    telletDoesNotExistTitle: 'Tellet غير موجود',
    projectDoesNotExistTitle: 'المشروع غير موجود',
    projectDoesNotExistMessage: 'نأسف، هذا المشروع غير موجود.',
    maximumParticipantsTitle: 'الحد الأقصى لعدد المشاركين',
    maximumParticipantsMessage:
      'وصلت هذه الدراسة إلى الحد الأقصى من الردود ولم تعد تقبل ردودًا جديدة.',
    somethingMissingTitle: 'هناك شيء مفقود',
    somethingMissingMessage: 'هذه الصفحة غير متاحة أو غير موجودة.',
    projectCompletedTitle: 'اكتمل المشروع',
    projectCompletedMessage: 'اكتملت هذه الدراسة ولم تعد تقبل ردودًا جديدة.',
    projectInDraftOrReadyStatusTitle: 'المشروع غير منشور',
    projectInDraftOrReadyStatusMessage: 'نظرًا لأن الدراسة لم تُنشر بعد، فهي لا تقبل الردود.',
    redirectingIn: 'إعادة التوجيه خلال',
    seconds: 'ثوانٍ...',
    cancel: 'إلغاء',
    returnToPanel: 'العودة إلى اللوحة',
    errorBoundaryTitle: 'حدث خطأ ما',
    errorBoundaryMessage: 'حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة للمتابعة.',
    errorBoundaryReloadButton: 'إعادة تحميل الصفحة',
    messageProcessingError:
      'عذرًا! يبدو أننا نواجه مشكلة في معالجة رسالتك. يرجى محاولة إعادة إرسالها.',
    microphoneProblemTextChat:
      'يبدو أن هناك مشكلة في الميكروفون. يرجى كتابة إجاباتك باستخدام زر لوحة المفاتيح.',
    microphoneProblemVoiceChat:
      'يبدو أن هناك مشكلة في الميكروفون. يرجى التحقق من أذونات المتصفح والمحاولة مرة أخرى.',
    responseTimeoutError: 'الاستجابة تستغرق وقتًا طويلاً. يرجى المحاولة مرة أخرى.',
    noResponseError: 'لم يتم اكتشاف أي استجابة. يرجى المحاولة مرة أخرى.',
    connectionInterrupted: 'انقطع الاتصال. يمكنك إعادة إرسال رسالتك.',
    videoStartError: 'فشل بدء تسجيل الفيديو. يرجى المحاولة مرة أخرى.',
    videoCameraNotSupported: 'الكاميرا غير مدعومة. يرجى المحاولة بمتصفح آخر.',
    videoPauseError: 'فشل إيقاف تسجيل الفيديو مؤقتًا.',
    videoResumeError: 'فشل استئناف تسجيل الفيديو.',
    videoStopError: 'فشل إيقاف تسجيل الفيديو. يرجى المحاولة مرة أخرى.',
    videoSwitchCameraError: 'فشل تبديل الكاميرا. يرجى المحاولة مرة أخرى.',
    videoSendError: 'فشل إرسال رسالة الفيديو.',
    welcomeBack: 'مرحبًا بعودتك!',
    unsupportedFileType: 'نوع الملف غير مدعوم. يرجى تحميل ملف JPG أو PNG أو GIF أو MP4.',
    fileTooLarge: 'هذا الملف كبير جدًا. الحد الأقصى للحجم هو 200 ميغابايت.',
    audioCloseError: 'خطأ في إغلاق الصوت:',
  },
  hy: {
    telletDoesNotExistTitle: 'Tellet-ը գոյություն չունի',
    projectDoesNotExistTitle: 'Նախագիծը գոյություն չունի',
    projectDoesNotExistMessage: 'Ցավում ենք, բայց այս նախագիծը գոյություն չունի։',
    maximumParticipantsTitle: 'Մասնակիցների առավելագույն քանակ',
    maximumParticipantsMessage:
      'Այս ուսումնասիրությունը հասել է իր պատասխանների առավելագույն քանակին և այլևս նոր պատասխաններ չի ընդունում։',
    somethingMissingTitle: 'Ինչ-որ բան բացակայում է',
    somethingMissingMessage: 'Այս էջը անհասանելի է կամ գոյություն չունի։',
    projectCompletedTitle: 'Նախագիծն ավարտված է',
    projectCompletedMessage:
      'Այս ուսումնասիրությունն ավարտված է և այլևս նոր պատասխաններ չի ընդունում։',
    projectInDraftOrReadyStatusTitle: 'Նախագիծը հրապարակված չէ',
    projectInDraftOrReadyStatusMessage:
      'Քանի որ ուսումնասիրությունը դեռ չի հրապարակվել, այն չի ընդունում պատասխաններ։',
    redirectingIn: 'Վերահղում',
    seconds: 'վայրկյանից...',
    cancel: 'Չեղարկել',
    returnToPanel: 'Վերադառնալ վահանակ',
    errorBoundaryTitle: 'Ինչ-որ բան սխալ ընթացավ',
    errorBoundaryMessage:
      'Անսպասելի սխալ տեղի ունեցավ։ Շարունակելու համար խնդրում ենք վերաբեռնել էջը։',
    errorBoundaryReloadButton: 'Վերաբեռնել էջը',
    messageProcessingError:
      'Ուփս։ Կարծես թե մենք խնդիրներ ունենք ձեր հաղորդագրությունը մշակելու հետ։ Խնդրում ենք փորձել այն կրկին ուղարկել։',
    microphoneProblemTextChat:
      'Կարծես ձեր խոսափողի հետ խնդիր կա։ Խնդրում ենք մուտքագրել ձեր պատասխանները՝ օգտագործելով ստեղնաշարի կոճակը։',
    microphoneProblemVoiceChat:
      'Կարծես ձեր խոսափողի հետ խնդիր կա։ Խնդրում ենք ստուգել ձեր բրաուզերի թույլտվությունները և փորձել կրկին։',
    responseTimeoutError: 'Պատասխանը չափազանց երկար է տևում։ Խնդրում ենք փորձել կրկին։',
    noResponseError: 'Պատասխան չի հայտնաբերվել։ Խնդրում ենք փորձել կրկին։',
    connectionInterrupted: 'Կապն ընդհատվել է։ Դուք կարող եք կրկին ուղարկել ձեր հաղորդագրությունը։',
    videoStartError: 'Չհաջողվեց սկսել տեսանյութի ձայնագրումը։ Խնդրում ենք փորձել կրկին։',
    videoCameraNotSupported: 'Տեսախցիկն աջակցվում չէ։ Խնդրում ենք փորձել այլ բրաուզերով։',
    videoPauseError: 'Չհաջողվեց դադարեցնել տեսանյութի ձայնագրումը։',
    videoResumeError: 'Չհաջողվեց վերսկսել տեսանյութի ձայնագրումը։',
    videoStopError: 'Չհաջողվեց դադարեցնել տեսանյութի ձայնագրումը։ Խնդրում ենք փորձել կրկին։',
    videoSwitchCameraError: 'Չհաջողվեց փոխարկել տեսախցիկը։ Խնդրում ենք փորձել կրկին։',
    videoSendError: 'Չհաջողվեց ուղարկել տեսահաղորդագրությունը։',
    welcomeBack: 'Բարի վերադարձ։',
    unsupportedFileType:
      'Ֆայլի տեսակն աջակցվում չէ։ Խնդրում ենք վերբեռնել JPG, PNG, GIF կամ MP4 ֆայլ։',
    fileTooLarge: 'Այս ֆայլը չափազանց մեծ է։ Առավելագույն չափը 200 ՄԲ է։',
    audioCloseError: 'Աուդիոն փակելու սխալ՝',
  },
  az: {
    telletDoesNotExistTitle: 'Tellet mövcud deyil',
    projectDoesNotExistTitle: 'Layihə mövcud deyil',
    projectDoesNotExistMessage: 'Üzr istəyirik, bu layihə mövcud deyil.',
    maximumParticipantsTitle: 'Maksimum iştirakçı sayı',
    maximumParticipantsMessage:
      'Bu araşdırma maksimum cavab sayına çatıb və artıq yeni cavablar qəbul etmir.',
    somethingMissingTitle: 'Nəsə əskikdir',
    somethingMissingMessage: 'Bu səhifə əlçatan deyil və ya mövcud deyil.',
    projectCompletedTitle: 'Layihə tamamlanıb',
    projectCompletedMessage: 'Bu araşdırma tamamlanıb və artıq yeni cavablar qəbul etmir.',
    projectInDraftOrReadyStatusTitle: 'Layihə dərc edilməyib',
    projectInDraftOrReadyStatusMessage: 'Araşdırma hələ dərc edilmədiyi üçün cavablar qəbul etmir.',
    redirectingIn: 'Yönləndirilir',
    seconds: 'saniyə...',
    cancel: 'Ləğv et',
    returnToPanel: 'Panelə qayıt',
    errorBoundaryTitle: 'Nəsə səhv getdi',
    errorBoundaryMessage:
      'Gözlənilməz xəta baş verdi. Davam etmək üçün zəhmət olmasa səhifəni yenidən yükləyin.',
    errorBoundaryReloadButton: 'Səhifəni yenidən yüklə',
    messageProcessingError:
      'Oops! Görünür, mesajınızı emal etməkdə problem yaşayırıq. Zəhmət olmasa yenidən göndərməyə cəhd edin.',
    microphoneProblemTextChat:
      'Mikrofonunuzda problem var kimi görünür. Zəhmət olmasa cavablarınızı klaviatura düyməsindən istifadə edərək yazın.',
    microphoneProblemVoiceChat:
      'Mikrofonunuzda problem var kimi görünür. Zəhmət olmasa brauzer icazələrinizi yoxlayın və yenidən cəhd edin.',
    responseTimeoutError: 'Cavab çox uzun çəkir. Zəhmət olmasa yenidən cəhd edin.',
    noResponseError: 'Cavab aşkar edilmədi. Zəhmət olmasa yenidən cəhd edin.',
    connectionInterrupted: 'Bağlantı kəsildi. Mesajınızı yenidən göndərə bilərsiniz.',
    videoStartError: 'Video yazılışını başlatmaq alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoCameraNotSupported: 'Kamera dəstəklənmir. Zəhmət olmasa fərqli brauzerlə cəhd edin.',
    videoPauseError: 'Video yazılışını dayandırmaq alınmadı.',
    videoResumeError: 'Video yazılışını davam etdirmək alınmadı.',
    videoStopError: 'Video yazılışını dayandırmaq alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoSwitchCameraError: 'Kameranı dəyişdirmək alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoSendError: 'Video mesaj göndərmək alınmadı.',
    welcomeBack: 'Xoş gəlmisiniz!',
    unsupportedFileType:
      'Dəstəklənməyən fayl növü. Zəhmət olmasa JPG, PNG, GIF və ya MP4 faylı yükləyin.',
    fileTooLarge: 'Bu fayl çox böyükdür. Maksimum ölçü 200 MB-dır.',
    audioCloseError: 'Audio bağlanarkən xəta:',
  },
  be: {
    telletDoesNotExistTitle: 'Tellet не існуе',
    projectDoesNotExistTitle: 'Праект не існуе',
    projectDoesNotExistMessage: 'Выбачайце, але гэты праект не існуе.',
    maximumParticipantsTitle: 'Максімальная колькасць удзельнікаў',
    maximumParticipantsMessage:
      'Гэта даследаванне дасягнула максімальнай колькасці адказаў і больш не прымае новыя адказы.',
    somethingMissingTitle: 'Нечага не хапае',
    somethingMissingMessage: 'Гэта старонка недаступная або не існуе.',
    projectCompletedTitle: 'Праект завершаны',
    projectCompletedMessage: 'Гэта даследаванне завершана і больш не прымае новыя адказы.',
    projectInDraftOrReadyStatusTitle: 'Праект не апублікаваны',
    projectInDraftOrReadyStatusMessage:
      'Паколькі даследаванне яшчэ не апублікавана, яно не прымае адказаў.',
    redirectingIn: 'Перанакіраванне праз',
    seconds: 'секунд...',
    cancel: 'Адмяніць',
    returnToPanel: 'Вярнуцца на панэль',
    errorBoundaryTitle: 'Нешта пайшло не так',
    errorBoundaryMessage:
      'Адбылася нечаканая памылка. Калі ласка, перазагрузіце старонку, каб працягнуць.',
    errorBoundaryReloadButton: 'Перазагрузіць старонку',
    messageProcessingError:
      'Ой! Падобна, у нас праблемы з апрацоўкай вашага паведамлення. Калі ласка, паспрабуйце адправіць яго зноў.',
    microphoneProblemTextChat:
      'Здаецца, з вашым мікрафонам ёсць праблема. Калі ласка, уводзьце свае адказы з дапамогай клавіятуры.',
    microphoneProblemVoiceChat:
      'Здаецца, з вашым мікрафонам ёсць праблема. Калі ласка, праверце дазволы браўзера і паспрабуйце зноў.',
    responseTimeoutError: 'Адказ займае занадта шмат часу. Калі ласка, паспрабуйце зноў.',
    noResponseError: 'Адказ не выяўлены. Калі ласка, паспрабуйце зноў.',
    connectionInterrupted: 'Злучэнне перарвана. Вы можаце паўторна адправіць сваё паведамленне.',
    videoStartError: 'Не ўдалося пачаць запіс відэа. Калі ласка, паспрабуйце зноў.',
    videoCameraNotSupported:
      'Камера не падтрымліваецца. Калі ласка, паспрабуйце з іншым браўзерам.',
    videoPauseError: 'Не ўдалося прыпыніць запіс відэа.',
    videoResumeError: 'Не ўдалося аднавіць запіс відэа.',
    videoStopError: 'Не ўдалося спыніць запіс відэа. Калі ласка, паспрабуйце зноў.',
    videoSwitchCameraError: 'Не ўдалося пераключыць камеру. Калі ласка, паспрабуйце зноў.',
    videoSendError: 'Не ўдалося адправіць відэапаведамленне.',
    welcomeBack: 'З вяртаннем!',
    unsupportedFileType:
      'Непадтрымоўваемы тып файла. Калі ласка, загрузіце файл JPG, PNG, GIF або MP4.',
    fileTooLarge: 'Гэты файл занадта вялікі. Максімальны памер — 200 МБ.',
    audioCloseError: 'Памылка закрыцця аўдыя:',
  },
  bn: {
    telletDoesNotExistTitle: 'Tellet বিদ্যমান নেই',
    projectDoesNotExistTitle: 'প্রকল্প বিদ্যমান নেই',
    projectDoesNotExistMessage: 'আমরা দুঃখিত, কিন্তু এই প্রকল্পটি বিদ্যমান নেই।',
    maximumParticipantsTitle: 'সর্বাধিক অংশগ্রহণকারীর সংখ্যা',
    maximumParticipantsMessage:
      'এই গবেষণাটি তার সর্বাধিক প্রতিক্রিয়ার সংখ্যায় পৌঁছেছে এবং আর নতুন প্রতিক্রিয়া গ্রহণ করছে না।',
    somethingMissingTitle: 'কিছু একটা অনুপস্থিত',
    somethingMissingMessage: 'এই পৃষ্ঠাটি অনুপলব্ধ বা বিদ্যমান নেই।',
    projectCompletedTitle: 'প্রকল্প সম্পন্ন হয়েছে',
    projectCompletedMessage: 'এই গবেষণাটি সম্পন্ন হয়েছে এবং আর নতুন প্রতিক্রিয়া গ্রহণ করছে না।',
    projectInDraftOrReadyStatusTitle: 'প্রকল্প প্রকাশিত হয়নি',
    projectInDraftOrReadyStatusMessage:
      'যেহেতু গবেষণাটি এখনও প্রকাশিত হয়নি, এটি প্রতিক্রিয়া গ্রহণ করছে না।',
    redirectingIn: 'পুনঃনির্দেশিত হচ্ছে',
    seconds: 'সেকেন্ডে...',
    cancel: 'বাতিল করুন',
    returnToPanel: 'প্যানেলে ফিরে যান',
    errorBoundaryTitle: 'কিছু ভুল হয়েছে',
    errorBoundaryMessage:
      'একটি অপ্রত্যাশিত ত্রুটি ঘটেছে। চালিয়ে যেতে অনুগ্রহ করে পৃষ্ঠাটি পুনরায় লোড করুন।',
    errorBoundaryReloadButton: 'পৃষ্ঠা পুনরায় লোড করুন',
    messageProcessingError:
      'ওহো! মনে হচ্ছে আপনার বার্তা প্রক্রিয়া করতে আমাদের সমস্যা হচ্ছে। অনুগ্রহ করে আবার পাঠানোর চেষ্টা করুন।',
    microphoneProblemTextChat:
      'মনে হচ্ছে আপনার মাইক্রোফোনে সমস্যা আছে। অনুগ্রহ করে কীবোর্ড বোতাম ব্যবহার করে আপনার উত্তর টাইপ করুন।',
    microphoneProblemVoiceChat:
      'মনে হচ্ছে আপনার মাইক্রোফোনে সমস্যা আছে। অনুগ্রহ করে আপনার ব্রাউজারের অনুমতি যাচাই করুন এবং আবার চেষ্টা করুন।',
    responseTimeoutError: 'প্রতিক্রিয়া অনেক সময় নিচ্ছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    noResponseError: 'কোন প্রতিক্রিয়া সনাক্ত করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
    connectionInterrupted: 'সংযোগ বিঘ্নিত হয়েছে। আপনি আপনার বার্তা পুনরায় পাঠাতে পারেন।',
    videoStartError: 'ভিডিও রেকর্ডিং শুরু করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoCameraNotSupported:
      'ক্যামেরা সমর্থিত নয়। অনুগ্রহ করে একটি ভিন্ন ব্রাউজার দিয়ে চেষ্টা করুন।',
    videoPauseError: 'ভিডিও রেকর্ডিং বিরতি দিতে ব্যর্থ।',
    videoResumeError: 'ভিডিও রেকর্ডিং পুনরায় শুরু করতে ব্যর্থ।',
    videoStopError: 'ভিডিও রেকর্ডিং বন্ধ করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoSwitchCameraError: 'ক্যামেরা পরিবর্তন করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoSendError: 'ভিডিও বার্তা পাঠাতে ব্যর্থ।',
    welcomeBack: 'ফিরে আসার জন্য স্বাগতম!',
    unsupportedFileType:
      'অসমর্থিত ফাইল প্রকার। অনুগ্রহ করে একটি JPG, PNG, GIF, বা MP4 ফাইল আপলোড করুন।',
    fileTooLarge: 'এই ফাইলটি খুব বড়। সর্বোচ্চ আকার ২০০ MB।',
    audioCloseError: 'অডিও বন্ধ করতে ত্রুটি:',
  },
  bs: {
    telletDoesNotExistTitle: 'Tellet ne postoji',
    projectDoesNotExistTitle: 'Projekat ne postoji',
    projectDoesNotExistMessage: 'Žao nam je, ali ovaj projekat ne postoji.',
    maximumParticipantsTitle: 'Maksimalan broj učesnika',
    maximumParticipantsMessage:
      'Ova studija je dostigla maksimalan broj odgovora i više ne prima nove odgovore.',
    somethingMissingTitle: 'Nešto nedostaje',
    somethingMissingMessage: 'Ova stranica je nedostupna ili ne postoji.',
    projectCompletedTitle: 'Projekat je završen',
    projectCompletedMessage: 'Ova studija je završena i više ne prima nove odgovore.',
    projectInDraftOrReadyStatusTitle: 'Projekat nije objavljen',
    projectInDraftOrReadyStatusMessage: 'Pošto studija još nije objavljena, ne prima odgovore.',
    redirectingIn: 'Preusmjeravanje za',
    seconds: 'sekundi...',
    cancel: 'Otkaži',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane greške. Molimo vas da osvježite stranicu kako biste nastavili.',
    errorBoundaryReloadButton: 'Osvježi stranicu',
    messageProcessingError:
      'Ups! Izgleda da imamo problema s obradom vaše poruke. Molimo pokušajte je ponovo poslati.',
    microphoneProblemTextChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo ukucajte svoje odgovore koristeći dugme tastature.',
    microphoneProblemVoiceChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo provjerite dozvole preglednika i pokušajte ponovo.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo pokušajte ponovo.',
    noResponseError: 'Nije otkriven odgovor. Molimo pokušajte ponovo.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovo poslati svoju poruku.',
    videoStartError: 'Pokretanje video snimanja nije uspjelo. Molimo pokušajte ponovo.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo pokušajte s drugim preglednikom.',
    videoPauseError: 'Pauziranje video snimanja nije uspjelo.',
    videoResumeError: 'Nastavak video snimanja nije uspio.',
    videoStopError: 'Zaustavljanje video snimanja nije uspjelo. Molimo pokušajte ponovo.',
    videoSwitchCameraError: 'Promjena kamere nije uspjela. Molimo pokušajte ponovo.',
    videoSendError: 'Slanje video poruke nije uspjelo.',
    welcomeBack: 'Dobrodošli nazad!',
    unsupportedFileType:
      'Nepodržana vrsta datoteke. Molimo prenesite JPG, PNG, GIF ili MP4 datoteku.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Greška pri zatvaranju zvuka:',
  },
  bg: {
    telletDoesNotExistTitle: 'Tellet не съществува',
    projectDoesNotExistTitle: 'Проектът не съществува',
    projectDoesNotExistMessage: 'Съжаляваме, но този проект не съществува.',
    maximumParticipantsTitle: 'Максимален брой участници',
    maximumParticipantsMessage:
      'Това проучване достигна максималния си брой отговори и вече не приема нови отговори.',
    somethingMissingTitle: 'Нещо липсва',
    somethingMissingMessage: 'Тази страница е недостъпна или не съществува.',
    projectCompletedTitle: 'Проектът е завършен',
    projectCompletedMessage: 'Това проучване е завършено и вече не приема нови отговори.',
    projectInDraftOrReadyStatusTitle: 'Проектът не е публикуван',
    projectInDraftOrReadyStatusMessage:
      'Тъй като проучването все още не е публикувано, то не приема отговори.',
    redirectingIn: 'Пренасочване след',
    seconds: 'секунди...',
    cancel: 'Отказ',
    returnToPanel: 'Връщане към панела',
    errorBoundaryTitle: 'Нещо се обърка',
    errorBoundaryMessage:
      'Възникна неочаквана грешка. Моля, презаредете страницата, за да продължите.',
    errorBoundaryReloadButton: 'Презареди страницата',
    messageProcessingError:
      'Опа! Изглежда имаме проблем с обработката на вашето съобщение. Моля, опитайте да го изпратите отново.',
    microphoneProblemTextChat:
      'Изглежда, че има проблем с вашия микрофон. Моля, въведете отговорите си, използвайки бутона за клавиатура.',
    microphoneProblemVoiceChat:
      'Изглежда, че има проблем с вашия микрофон. Моля, проверете разрешенията на браузъра си и опитайте отново.',
    responseTimeoutError: 'Отговорът отнема твърде дълго. Моля, опитайте отново.',
    noResponseError: 'Не е открит отговор. Моля, опитайте отново.',
    connectionInterrupted: 'Връзката е прекъсната. Можете да изпратите отново съобщението си.',
    videoStartError: 'Неуспешно стартиране на видеозапис. Моля, опитайте отново.',
    videoCameraNotSupported: 'Камерата не се поддържа. Моля, опитайте с друг браузър.',
    videoPauseError: 'Неуспешно поставяне на пауза на видеозаписа.',
    videoResumeError: 'Неуспешно възобновяване на видеозаписа.',
    videoStopError: 'Неуспешно спиране на видеозаписа. Моля, опитайте отново.',
    videoSwitchCameraError: 'Неуспешна смяна на камерата. Моля, опитайте отново.',
    videoSendError: 'Неуспешно изпращане на видеосъобщение.',
    welcomeBack: 'Добре дошли отново!',
    unsupportedFileType: 'Неподдържан тип файл. Моля, качете JPG, PNG, GIF или MP4 файл.',
    fileTooLarge: 'Този файл е твърде голям. Максималният размер е 200 MB.',
    audioCloseError: 'Грешка при затваряне на аудио:',
  },
  yue: {
    telletDoesNotExistTitle: 'Tellet 並唔存在',
    projectDoesNotExistTitle: '項目並唔存在',
    projectDoesNotExistMessage: '好對唔住，呢個項目並唔存在。',
    maximumParticipantsTitle: '參與者數目已達上限',
    maximumParticipantsMessage: '呢個研究已經達到最大回應數目，唔再接受新嘅回應。',
    somethingMissingTitle: '搵唔到嘢',
    somethingMissingMessage: '呢一頁無法使用或者唔存在。',
    projectCompletedTitle: '項目已完成',
    projectCompletedMessage: '呢個研究已經完成，唔再接受新嘅回應。',
    projectInDraftOrReadyStatusTitle: '項目未發布',
    projectInDraftOrReadyStatusMessage: '因為研究重未發布，所以唔接受回應。',
    redirectingIn: '重新導向仲有',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出咗啲問題',
    errorBoundaryMessage: '發生咗預料之外嘅錯誤。請重新載入頁面以繼續。',
    errorBoundaryReloadButton: '重新載入頁面',
    messageProcessingError: '哎呀！好似處理你嘅訊息時出咗問題。請試下重新發送。',
    microphoneProblemTextChat: '你嘅咪高峰好似有問題。請用鍵盤掣打字輸入你嘅答案。',
    microphoneProblemVoiceChat: '你嘅咪高峰好似有問題。請檢查你嘅瀏覽器權限然後再試。',
    responseTimeoutError: '回應時間太長。請再試一次。',
    noResponseError: '偵測唔到回應。請再試一次。',
    connectionInterrupted: '連接中斷。你可以重新發送你嘅訊息。',
    videoStartError: '無法開始錄影。請再試一次。',
    videoCameraNotSupported: '唔支援相機。請用另一個瀏覽器試下。',
    videoPauseError: '無法暫停錄影。',
    videoResumeError: '無法繼續錄影。',
    videoStopError: '無法停止錄影。請再試一次。',
    videoSwitchCameraError: '無法切換相機。請再試一次。',
    videoSendError: '無法傳送影片訊息。',
    welcomeBack: '歡迎返嚟！',
    unsupportedFileType: '唔支援嘅檔案類型。請上傳 JPG、PNG、GIF 或 MP4 檔案。',
    fileTooLarge: '呢個檔案太大。最大限制係 200 MB。',
    audioCloseError: '關閉音訊時出錯：',
  },
  ca: {
    telletDoesNotExistTitle: 'Tellet no existeix',
    projectDoesNotExistTitle: 'El projecte no existeix',
    projectDoesNotExistMessage: 'Ho sentim, però aquest projecte no existeix.',
    maximumParticipantsTitle: 'Nombre màxim de participants',
    maximumParticipantsMessage:
      "Aquest estudi ha arribat al nombre màxim de respostes i ja no n'accepta de noves.",
    somethingMissingTitle: 'Falta alguna cosa',
    somethingMissingMessage: 'Aquesta pàgina no està disponible o no existeix.',
    projectCompletedTitle: "El projecte s'ha completat",
    projectCompletedMessage: "Aquest estudi s'ha completat i ja no accepta noves respostes.",
    projectInDraftOrReadyStatusTitle: 'El projecte no està publicat',
    projectInDraftOrReadyStatusMessage:
      "Com que l'estudi encara no s'ha publicat, no accepta respostes.",
    redirectingIn: 'Redirigint en',
    seconds: 'segons...',
    cancel: 'Cancel·la',
    returnToPanel: 'Tornar al panell',
    errorBoundaryTitle: 'Alguna cosa ha anat malament',
    errorBoundaryMessage:
      "S'ha produït un error inesperat. Si us plau, torna a carregar la pàgina per continuar.",
    errorBoundaryReloadButton: 'Torna a carregar la pàgina',
    messageProcessingError:
      'Vaja! Sembla que tenim problemes per processar el teu missatge. Si us plau, prova de reenviar-lo.',
    microphoneProblemTextChat:
      'Sembla que hi ha un problema amb el teu micròfon. Si us plau, escriu les teves respostes amb el botó del teclat.',
    microphoneProblemVoiceChat:
      'Sembla que hi ha un problema amb el teu micròfon. Si us plau, comprova els permisos del navegador i torna-ho a provar.',
    responseTimeoutError: 'La resposta triga massa. Si us plau, torna-ho a provar.',
    noResponseError: "No s'ha detectat cap resposta. Si us plau, torna-ho a provar.",
    connectionInterrupted: 'Connexió interrompuda. Pots tornar a enviar el teu missatge.',
    videoStartError: "No s'ha pogut iniciar la gravació de vídeo. Si us plau, torna-ho a provar.",
    videoCameraNotSupported:
      'La càmera no és compatible. Si us plau, prova amb un altre navegador.',
    videoPauseError: "No s'ha pogut pausar la gravació de vídeo.",
    videoResumeError: "No s'ha pogut reprendre la gravació de vídeo.",
    videoStopError: "No s'ha pogut aturar la gravació de vídeo. Si us plau, torna-ho a provar.",
    videoSwitchCameraError: "No s'ha pogut canviar de càmera. Si us plau, torna-ho a provar.",
    videoSendError: "No s'ha pogut enviar el missatge de vídeo.",
    welcomeBack: 'Benvingut de nou!',
    unsupportedFileType:
      'Tipus de fitxer no compatible. Si us plau, puja un fitxer JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Aquest fitxer és massa gran. La mida màxima és de 200 MB.',
    audioCloseError: "Error en tancar l'àudio:",
  },
  zh: {
    telletDoesNotExistTitle: 'Tellet 不存在',
    projectDoesNotExistTitle: '项目不存在',
    projectDoesNotExistMessage: '很抱歉，此项目不存在。',
    maximumParticipantsTitle: '参与者数量已达上限',
    maximumParticipantsMessage: '此研究已达到最大回复数量，不再接受新的回复。',
    somethingMissingTitle: '找不到内容',
    somethingMissingMessage: '此页面不可用或不存在。',
    projectCompletedTitle: '项目已完成',
    projectCompletedMessage: '此研究已完成，不再接受新的回复。',
    projectInDraftOrReadyStatusTitle: '项目未发布',
    projectInDraftOrReadyStatusMessage: '由于研究尚未发布，因此不接受回复。',
    redirectingIn: '重定向倒计时',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出现问题',
    errorBoundaryMessage: '发生意外错误。请重新加载页面以继续。',
    errorBoundaryReloadButton: '重新加载页面',
    messageProcessingError: '糟糕！我们在处理您的消息时遇到问题。请尝试重新发送。',
    microphoneProblemTextChat: '您的麦克风似乎有问题。请使用键盘按钮输入您的回答。',
    microphoneProblemVoiceChat: '您的麦克风似乎有问题。请检查您的浏览器权限并重试。',
    responseTimeoutError: '响应时间过长。请重试。',
    noResponseError: '未检测到响应。请重试。',
    connectionInterrupted: '连接中断。您可以重新发送您的消息。',
    videoStartError: '无法开始视频录制。请重试。',
    videoCameraNotSupported: '不支持摄像头。请尝试使用其他浏览器。',
    videoPauseError: '无法暂停视频录制。',
    videoResumeError: '无法恢复视频录制。',
    videoStopError: '无法停止视频录制。请重试。',
    videoSwitchCameraError: '无法切换摄像头。请重试。',
    videoSendError: '无法发送视频消息。',
    welcomeBack: '欢迎回来！',
    unsupportedFileType: '不支持的文件类型。请上传 JPG、PNG、GIF 或 MP4 文件。',
    fileTooLarge: '此文件过大。最大限制为 200 MB。',
    audioCloseError: '关闭音频时出错：',
  },
  'zh-TW': {
    telletDoesNotExistTitle: 'Tellet 不存在',
    projectDoesNotExistTitle: '專案不存在',
    projectDoesNotExistMessage: '很抱歉，此專案不存在。',
    maximumParticipantsTitle: '參與者數量已達上限',
    maximumParticipantsMessage: '此研究已達到最大回覆數量，不再接受新的回覆。',
    somethingMissingTitle: '找不到內容',
    somethingMissingMessage: '此頁面無法使用或不存在。',
    projectCompletedTitle: '專案已完成',
    projectCompletedMessage: '此研究已完成，不再接受新的回覆。',
    projectInDraftOrReadyStatusTitle: '專案尚未發布',
    projectInDraftOrReadyStatusMessage: '由於研究尚未發布，因此不接受回覆。',
    redirectingIn: '重新導向倒數',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出現問題',
    errorBoundaryMessage: '發生意外錯誤。請重新載入頁面以繼續。',
    errorBoundaryReloadButton: '重新載入頁面',
    messageProcessingError: '糟糕！我們在處理您的訊息時遇到問題。請嘗試重新傳送。',
    microphoneProblemTextChat: '您的麥克風似乎有問題。請使用鍵盤按鈕輸入您的回答。',
    microphoneProblemVoiceChat: '您的麥克風似乎有問題。請檢查您的瀏覽器權限並重試。',
    responseTimeoutError: '回應時間過長。請重試。',
    noResponseError: '未偵測到回應。請重試。',
    connectionInterrupted: '連線中斷。您可以重新傳送您的訊息。',
    videoStartError: '無法開始影片錄製。請重試。',
    videoCameraNotSupported: '不支援攝影機。請嘗試使用其他瀏覽器。',
    videoPauseError: '無法暫停影片錄製。',
    videoResumeError: '無法繼續影片錄製。',
    videoStopError: '無法停止影片錄製。請重試。',
    videoSwitchCameraError: '無法切換攝影機。請重試。',
    videoSendError: '無法傳送影片訊息。',
    welcomeBack: '歡迎回來！',
    unsupportedFileType: '不支援的檔案類型。請上傳 JPG、PNG、GIF 或 MP4 檔案。',
    fileTooLarge: '此檔案過大。大小上限為 200 MB。',
    audioCloseError: '關閉音訊時發生錯誤：',
  },
  hr: {
    telletDoesNotExistTitle: 'Tellet ne postoji',
    projectDoesNotExistTitle: 'Projekt ne postoji',
    projectDoesNotExistMessage: 'Žao nam je, ali ovaj projekt ne postoji.',
    maximumParticipantsTitle: 'Maksimalan broj sudionika',
    maximumParticipantsMessage:
      'Ova studija dosegla je maksimalan broj odgovora i više ne prihvaća nove odgovore.',
    somethingMissingTitle: 'Nešto nedostaje',
    somethingMissingMessage: 'Ova stranica nije dostupna ili ne postoji.',
    projectCompletedTitle: 'Projekt je završen',
    projectCompletedMessage: 'Ova studija je završena i više ne prihvaća nove odgovore.',
    projectInDraftOrReadyStatusTitle: 'Projekt nije objavljen',
    projectInDraftOrReadyStatusMessage:
      'Budući da studija još nije objavljena, ne prihvaća odgovore.',
    redirectingIn: 'Preusmjeravanje za',
    seconds: 'sekundi...',
    cancel: 'Odustani',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane pogreške. Molimo ponovno učitajte stranicu za nastavak.',
    errorBoundaryReloadButton: 'Ponovno učitaj stranicu',
    messageProcessingError:
      'Ups! Čini se da imamo problema s obradom vaše poruke. Molimo pokušajte je ponovno poslati.',
    microphoneProblemTextChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo upišite svoje odgovore pomoću gumba tipkovnice.',
    microphoneProblemVoiceChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo provjerite dopuštenja preglednika i pokušajte ponovno.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo pokušajte ponovno.',
    noResponseError: 'Nije otkriven odgovor. Molimo pokušajte ponovno.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovno poslati svoju poruku.',
    videoStartError: 'Pokretanje video snimanja nije uspjelo. Molimo pokušajte ponovno.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo pokušajte s drugim preglednikom.',
    videoPauseError: 'Pauziranje video snimanja nije uspjelo.',
    videoResumeError: 'Nastavak video snimanja nije uspio.',
    videoStopError: 'Zaustavljanje video snimanja nije uspjelo. Molimo pokušajte ponovno.',
    videoSwitchCameraError: 'Promjena kamere nije uspjela. Molimo pokušajte ponovno.',
    videoSendError: 'Slanje videoporuke nije uspjelo.',
    welcomeBack: 'Dobrodošli natrag!',
    unsupportedFileType:
      'Nepodržana vrsta datoteke. Molimo prenesite JPG, PNG, GIF ili MP4 datoteku.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Pogreška pri zatvaranju zvuka:',
  },
  cs: {
    telletDoesNotExistTitle: 'Tellet neexistuje',
    projectDoesNotExistTitle: 'Projekt neexistuje',
    projectDoesNotExistMessage: 'Omlouváme se, ale tento projekt neexistuje.',
    maximumParticipantsTitle: 'Maximální počet účastníků',
    maximumParticipantsMessage:
      'Tato studie dosáhla maximálního počtu odpovědí a již nepřijímá nové odpovědi.',
    somethingMissingTitle: 'Něco chybí',
    somethingMissingMessage: 'Tato stránka není k dispozici nebo neexistuje.',
    projectCompletedTitle: 'Projekt je dokončen',
    projectCompletedMessage: 'Tato studie byla dokončena a již nepřijímá nové odpovědi.',
    projectInDraftOrReadyStatusTitle: 'Projekt není publikován',
    projectInDraftOrReadyStatusMessage:
      'Protože studie ještě nebyla publikována, nepřijímá odpovědi.',
    redirectingIn: 'Přesměrování za',
    seconds: 'sekund...',
    cancel: 'Zrušit',
    returnToPanel: 'Zpět na panel',
    errorBoundaryTitle: 'Něco se pokazilo',
    errorBoundaryMessage:
      'Došlo k neočekávané chybě. Pro pokračování prosím znovu načtěte stránku.',
    errorBoundaryReloadButton: 'Znovu načíst stránku',
    messageProcessingError:
      'Jejda! Zdá se, že máme potíže se zpracováním vaší zprávy. Zkuste ji prosím odeslat znovu.',
    microphoneProblemTextChat:
      'Zdá se, že je problém s vaším mikrofonem. Napište prosím své odpovědi pomocí tlačítka klávesnice.',
    microphoneProblemVoiceChat:
      'Zdá se, že je problém s vaším mikrofonem. Zkontrolujte prosím oprávnění prohlížeče a zkuste to znovu.',
    responseTimeoutError: 'Odpověď trvá příliš dlouho. Zkuste to prosím znovu.',
    noResponseError: 'Nebyla zjištěna žádná odpověď. Zkuste to prosím znovu.',
    connectionInterrupted: 'Spojení bylo přerušeno. Můžete znovu odeslat svou zprávu.',
    videoStartError: 'Spuštění nahrávání videa se nezdařilo. Zkuste to prosím znovu.',
    videoCameraNotSupported: 'Kamera není podporována. Zkuste to prosím s jiným prohlížečem.',
    videoPauseError: 'Pozastavení nahrávání videa se nezdařilo.',
    videoResumeError: 'Obnovení nahrávání videa se nezdařilo.',
    videoStopError: 'Zastavení nahrávání videa se nezdařilo. Zkuste to prosím znovu.',
    videoSwitchCameraError: 'Přepnutí kamery se nezdařilo. Zkuste to prosím znovu.',
    videoSendError: 'Odeslání videozprávy se nezdařilo.',
    welcomeBack: 'Vítejte zpět!',
    unsupportedFileType:
      'Nepodporovaný typ souboru. Nahrajte prosím soubor JPG, PNG, GIF nebo MP4.',
    fileTooLarge: 'Tento soubor je příliš velký. Maximální velikost je 200 MB.',
    audioCloseError: 'Chyba při zavírání zvuku:',
  },
  da: {
    telletDoesNotExistTitle: 'Tellet findes ikke',
    projectDoesNotExistTitle: 'Projektet findes ikke',
    projectDoesNotExistMessage: 'Vi beklager, men dette projekt findes ikke.',
    maximumParticipantsTitle: 'Maksimalt antal deltagere',
    maximumParticipantsMessage:
      'Denne undersøgelse har nået sit maksimale antal svar og accepterer ikke længere nye svar.',
    somethingMissingTitle: 'Noget mangler',
    somethingMissingMessage: 'Denne side er ikke tilgængelig eller findes ikke.',
    projectCompletedTitle: 'Projektet er afsluttet',
    projectCompletedMessage: 'Denne undersøgelse er afsluttet og accepterer ikke længere nye svar.',
    projectInDraftOrReadyStatusTitle: 'Projektet er ikke publiceret',
    projectInDraftOrReadyStatusMessage:
      'Da undersøgelsen endnu ikke er publiceret, accepterer den ikke svar.',
    redirectingIn: 'Omdirigerer om',
    seconds: 'sekunder...',
    cancel: 'Annuller',
    returnToPanel: 'Tilbage til panelet',
    errorBoundaryTitle: 'Noget gik galt',
    errorBoundaryMessage: 'Der opstod en uventet fejl. Genindlæs venligst siden for at fortsætte.',
    errorBoundaryReloadButton: 'Genindlæs siden',
    messageProcessingError:
      'Ups! Det ser ud til, at vi har problemer med at behandle din besked. Prøv venligst at sende den igen.',
    microphoneProblemTextChat:
      'Det ser ud til, at der er et problem med din mikrofon. Skriv venligst dine svar ved hjælp af tastaturknappen.',
    microphoneProblemVoiceChat:
      'Det ser ud til, at der er et problem med din mikrofon. Tjek venligst dine browsertilladelser, og prøv igen.',
    responseTimeoutError: 'Svaret tager for lang tid. Prøv venligst igen.',
    noResponseError: 'Intet svar registreret. Prøv venligst igen.',
    connectionInterrupted: 'Forbindelsen blev afbrudt. Du kan sende din besked igen.',
    videoStartError: 'Kunne ikke starte videooptagelse. Prøv venligst igen.',
    videoCameraNotSupported: 'Kameraet understøttes ikke. Prøv venligst med en anden browser.',
    videoPauseError: 'Kunne ikke sætte videooptagelse på pause.',
    videoResumeError: 'Kunne ikke genoptage videooptagelse.',
    videoStopError: 'Kunne ikke stoppe videooptagelse. Prøv venligst igen.',
    videoSwitchCameraError: 'Kunne ikke skifte kamera. Prøv venligst igen.',
    videoSendError: 'Kunne ikke sende videobesked.',
    welcomeBack: 'Velkommen tilbage!',
    unsupportedFileType:
      'Ikke-understøttet filtype. Upload venligst en JPG-, PNG-, GIF- eller MP4-fil.',
    fileTooLarge: 'Denne fil er for stor. Den maksimale størrelse er 200 MB.',
    audioCloseError: 'Fejl ved lukning af lyd:',
  },
  nl: {
    telletDoesNotExistTitle: 'Tellet bestaat niet',
    projectDoesNotExistTitle: 'Project bestaat niet',
    projectDoesNotExistMessage: 'Sorry, dit project bestaat niet.',
    maximumParticipantsTitle: 'Maximum aantal deelnemers',
    maximumParticipantsMessage:
      'Dit onderzoek heeft het maximale aantal reacties bereikt en accepteert geen nieuwe reacties meer.',
    somethingMissingTitle: 'Er ontbreekt iets',
    somethingMissingMessage: 'Deze pagina is niet beschikbaar of bestaat niet.',
    projectCompletedTitle: 'Project is voltooid',
    projectCompletedMessage: 'Dit onderzoek is voltooid en accepteert geen nieuwe reacties meer.',
    projectInDraftOrReadyStatusTitle: 'Project is niet gepubliceerd',
    projectInDraftOrReadyStatusMessage:
      'Omdat het onderzoek nog niet is gepubliceerd, worden er geen reacties geaccepteerd.',
    redirectingIn: 'Doorverwijzen over',
    seconds: 'seconden...',
    cancel: 'Annuleren',
    returnToPanel: 'Terug naar panel',
    errorBoundaryTitle: 'Er is iets misgegaan',
    errorBoundaryMessage:
      'Er is een onverwachte fout opgetreden. Laad de pagina opnieuw om door te gaan.',
    errorBoundaryReloadButton: 'Pagina opnieuw laden',
    messageProcessingError:
      'Oeps! Het lijkt erop dat we problemen hebben met het verwerken van je bericht. Probeer het opnieuw te versturen.',
    microphoneProblemTextChat:
      'Er lijkt een probleem te zijn met je microfoon. Typ je antwoorden met de toetsenbordknop.',
    microphoneProblemVoiceChat:
      'Er lijkt een probleem te zijn met je microfoon. Controleer je browserrechten en probeer het opnieuw.',
    responseTimeoutError: 'Reactie duurt te lang. Probeer het opnieuw.',
    noResponseError: 'Geen reactie ontvangen. Probeer het opnieuw.',
    connectionInterrupted: 'Verbinding onderbroken. Je kunt je bericht opnieuw versturen.',
    videoStartError: 'Kan video-opname niet starten. Probeer het opnieuw.',
    videoCameraNotSupported: 'Camera wordt niet ondersteund. Probeer het met een andere browser.',
    videoPauseError: 'Kan video-opname niet pauzeren.',
    videoResumeError: 'Kan video-opname niet hervatten.',
    videoStopError: 'Kan video-opname niet stoppen. Probeer het opnieuw.',
    videoSwitchCameraError: 'Kan camera niet wisselen. Probeer het opnieuw.',
    videoSendError: 'Kan videobericht niet versturen.',
    welcomeBack: 'Welkom terug!',
    unsupportedFileType:
      'Niet-ondersteund bestandstype. Upload een JPG-, PNG-, GIF- of MP4-bestand.',
    fileTooLarge: 'Dit bestand is te groot. De maximale grootte is 200 MB.',
    audioCloseError: 'Fout bij sluiten van audio:',
  },
  en: {
    telletDoesNotExistTitle: 'Tellet does not exist',
    projectDoesNotExistTitle: 'Project does not exist',
    projectDoesNotExistMessage: "We're sorry but this project does not exist.",
    maximumParticipantsTitle: 'Maximum amount of participants',
    maximumParticipantsMessage:
      'This study has reached its maximum amount of responses and is no longer accepting new responses.',
    somethingMissingTitle: 'Something missing',
    somethingMissingMessage: 'This page is unavailable or does not exist.',
    projectCompletedTitle: 'Project is completed',
    projectCompletedMessage:
      'This study has been completed and is no longer accepting new responses.',
    projectInDraftOrReadyStatusTitle: 'Project is not published',
    projectInDraftOrReadyStatusMessage:
      'Because study has not been published yet, it is not accepting responses.',
    redirectingIn: 'Redirecting in',
    seconds: 'seconds...',
    cancel: 'Cancel',
    returnToPanel: 'Return to Panel',
    errorBoundaryTitle: 'Something went wrong',
    errorBoundaryMessage: 'An unexpected error occurred. Please reload the page to continue.',
    errorBoundaryReloadButton: 'Reload page',
    messageProcessingError:
      "Oops! Looks like we're having trouble processing your message. Please try resending it.",
    microphoneProblemTextChat:
      'It seems there is a problem with your microphone. Please type your answers using the keyboard button.',
    microphoneProblemVoiceChat:
      'It seems there is a problem with your microphone. Please check your browser permissions and try again.',
    responseTimeoutError: 'Response taking too long. Please try again.',
    noResponseError: 'No response detected. Please try again.',
    connectionInterrupted: 'Connection interrupted. You can resend your message.',
    videoStartError: 'Failed to start video recording. Please try again.',
    videoCameraNotSupported: 'Camera is not supported. Please try with a different browser.',
    videoPauseError: 'Failed to pause video recording.',
    videoResumeError: 'Failed to resume video recording.',
    videoStopError: 'Failed to stop video recording. Please try again.',
    videoSwitchCameraError: 'Failed to switch camera. Please try again.',
    videoSendError: 'Failed to send video message.',
    welcomeBack: 'Welcome back!',
    unsupportedFileType: 'Unsupported file type. Please upload a JPG, PNG, GIF, or MP4 file.',
    fileTooLarge: 'This file is too large. The maximum size is 200 MB.',
    audioCloseError: 'Error closing audio:',
  },
  et: {
    telletDoesNotExistTitle: 'Telletit ei ole olemas',
    projectDoesNotExistTitle: 'Projekti ei ole olemas',
    projectDoesNotExistMessage: 'Vabandame, kuid seda projekti ei ole olemas.',
    maximumParticipantsTitle: 'Osalejate maksimaalne arv',
    maximumParticipantsMessage:
      'See uuring on saavutanud maksimaalse vastuste arvu ega võta enam uusi vastuseid vastu.',
    somethingMissingTitle: 'Midagi on puudu',
    somethingMissingMessage: 'See leht ei ole saadaval või ei eksisteeri.',
    projectCompletedTitle: 'Projekt on lõpetatud',
    projectCompletedMessage: 'See uuring on lõpetatud ja ei võta enam uusi vastuseid vastu.',
    projectInDraftOrReadyStatusTitle: 'Projekt ei ole avaldatud',
    projectInDraftOrReadyStatusMessage:
      'Kuna uuringut pole veel avaldatud, ei võta see vastuseid vastu.',
    redirectingIn: 'Ümbersuunamine',
    seconds: 'sekundi pärast...',
    cancel: 'Tühista',
    returnToPanel: 'Tagasi paneeli',
    errorBoundaryTitle: 'Midagi läks valesti',
    errorBoundaryMessage: 'Ilmnes ootamatu viga. Jätkamiseks laadige leht uuesti.',
    errorBoundaryReloadButton: 'Laadi leht uuesti',
    messageProcessingError:
      'Oih! Tundub, et meil on probleeme teie sõnumi töötlemisega. Palun proovige see uuesti saata.',
    microphoneProblemTextChat:
      'Tundub, et teie mikrofoniga on probleem. Palun kirjutage oma vastused klaviatuurinupu abil.',
    microphoneProblemVoiceChat:
      'Tundub, et teie mikrofoniga on probleem. Palun kontrollige oma brauseri lubasid ja proovige uuesti.',
    responseTimeoutError: 'Vastus võtab liiga kaua aega. Palun proovige uuesti.',
    noResponseError: 'Vastust ei tuvastatud. Palun proovige uuesti.',
    connectionInterrupted: 'Ühendus katkes. Saate oma sõnumi uuesti saata.',
    videoStartError: 'Videosalvestuse käivitamine ebaõnnestus. Palun proovige uuesti.',
    videoCameraNotSupported: 'Kaamera ei ole toetatud. Palun proovige teise brauseriga.',
    videoPauseError: 'Videosalvestuse peatamine ebaõnnestus.',
    videoResumeError: 'Videosalvestuse jätkamine ebaõnnestus.',
    videoStopError: 'Videosalvestuse peatamine ebaõnnestus. Palun proovige uuesti.',
    videoSwitchCameraError: 'Kaamera vahetamine ebaõnnestus. Palun proovige uuesti.',
    videoSendError: 'Videosõnumi saatmine ebaõnnestus.',
    welcomeBack: 'Tere tulemast tagasi!',
    unsupportedFileType:
      'Mittetoetatud failitüüp. Palun laadige üles JPG-, PNG-, GIF- või MP4-fail.',
    fileTooLarge: 'See fail on liiga suur. Maksimaalne suurus on 200 MB.',
    audioCloseError: 'Viga heli sulgemisel:',
  },
  fi: {
    telletDoesNotExistTitle: 'Tellet ei ole olemassa',
    projectDoesNotExistTitle: 'Projektia ei ole olemassa',
    projectDoesNotExistMessage: 'Pahoittelut, mutta tätä projektia ei ole olemassa.',
    maximumParticipantsTitle: 'Osallistujien enimmäismäärä',
    maximumParticipantsMessage:
      'Tämä tutkimus on saavuttanut vastausten enimmäismäärän eikä ota enää uusia vastauksia vastaan.',
    somethingMissingTitle: 'Jotain puuttuu',
    somethingMissingMessage: 'Tämä sivu ei ole saatavilla tai sitä ei ole olemassa.',
    projectCompletedTitle: 'Projekti on valmis',
    projectCompletedMessage: 'Tämä tutkimus on valmistunut eikä ota enää uusia vastauksia vastaan.',
    projectInDraftOrReadyStatusTitle: 'Projektia ei ole julkaistu',
    projectInDraftOrReadyStatusMessage:
      'Koska tutkimusta ei ole vielä julkaistu, se ei ota vastauksia vastaan.',
    redirectingIn: 'Uudelleenohjaus',
    seconds: 'sekunnin kuluttua...',
    cancel: 'Peruuta',
    returnToPanel: 'Palaa paneeliin',
    errorBoundaryTitle: 'Jotain meni pieleen',
    errorBoundaryMessage: 'Tapahtui odottamaton virhe. Lataa sivu uudelleen jatkaaksesi.',
    errorBoundaryReloadButton: 'Lataa sivu uudelleen',
    messageProcessingError:
      'Hups! Näyttää siltä, että viestisi käsittelyssä on ongelmia. Yritä lähettää se uudelleen.',
    microphoneProblemTextChat:
      'Mikrofonissasi näyttää olevan ongelma. Kirjoita vastauksesi näppäimistöpainikkeella.',
    microphoneProblemVoiceChat:
      'Mikrofonissasi näyttää olevan ongelma. Tarkista selaimen käyttöoikeudet ja yritä uudelleen.',
    responseTimeoutError: 'Vastaus kestää liian kauan. Yritä uudelleen.',
    noResponseError: 'Vastausta ei havaittu. Yritä uudelleen.',
    connectionInterrupted: 'Yhteys katkesi. Voit lähettää viestisi uudelleen.',
    videoStartError: 'Videotallennuksen aloittaminen epäonnistui. Yritä uudelleen.',
    videoCameraNotSupported: 'Kameraa ei tueta. Yritä toisella selaimella.',
    videoPauseError: 'Videotallennuksen keskeyttäminen epäonnistui.',
    videoResumeError: 'Videotallennuksen jatkaminen epäonnistui.',
    videoStopError: 'Videotallennuksen lopettaminen epäonnistui. Yritä uudelleen.',
    videoSwitchCameraError: 'Kameran vaihto epäonnistui. Yritä uudelleen.',
    videoSendError: 'Videoviestin lähettäminen epäonnistui.',
    welcomeBack: 'Tervetuloa takaisin!',
    unsupportedFileType: 'Tiedostotyyppiä ei tueta. Lataa JPG-, PNG-, GIF- tai MP4-tiedosto.',
    fileTooLarge: 'Tämä tiedosto on liian suuri. Enimmäiskoko on 200 Mt.',
    audioCloseError: 'Virhe äänen sulkemisessa:',
  },
  fr: {
    telletDoesNotExistTitle: "Tellet n'existe pas",
    projectDoesNotExistTitle: "Le projet n'existe pas",
    projectDoesNotExistMessage: "Nous sommes désolés, mais ce projet n'existe pas.",
    maximumParticipantsTitle: 'Nombre maximum de participants',
    maximumParticipantsMessage:
      "Cette étude a atteint son nombre maximum de réponses et n'accepte plus de nouvelles réponses.",
    somethingMissingTitle: 'Quelque chose manque',
    somethingMissingMessage: "Cette page est indisponible ou n'existe pas.",
    projectCompletedTitle: 'Le projet est terminé',
    projectCompletedMessage: "Cette étude est terminée et n'accepte plus de nouvelles réponses.",
    projectInDraftOrReadyStatusTitle: "Le projet n'est pas publié",
    projectInDraftOrReadyStatusMessage:
      "Étant donné que l'étude n'a pas encore été publiée, elle n'accepte pas de réponses.",
    redirectingIn: 'Redirection dans',
    seconds: 'secondes...',
    cancel: 'Annuler',
    returnToPanel: 'Retour au panel',
    errorBoundaryTitle: 'Une erreur est survenue',
    errorBoundaryMessage:
      "Une erreur inattendue s'est produite. Veuillez recharger la page pour continuer.",
    errorBoundaryReloadButton: 'Recharger la page',
    messageProcessingError:
      'Oups ! Il semble que nous ayons des difficultés à traiter votre message. Veuillez essayer de le renvoyer.',
    microphoneProblemTextChat:
      "Il semble y avoir un problème avec votre microphone. Veuillez taper vos réponses à l'aide du bouton clavier.",
    microphoneProblemVoiceChat:
      'Il semble y avoir un problème avec votre microphone. Veuillez vérifier les autorisations de votre navigateur et réessayer.',
    responseTimeoutError: 'La réponse prend trop de temps. Veuillez réessayer.',
    noResponseError: 'Aucune réponse détectée. Veuillez réessayer.',
    connectionInterrupted: 'Connexion interrompue. Vous pouvez renvoyer votre message.',
    videoStartError: "Impossible de démarrer l'enregistrement vidéo. Veuillez réessayer.",
    videoCameraNotSupported:
      "La caméra n'est pas prise en charge. Veuillez essayer avec un autre navigateur.",
    videoPauseError: "Impossible de mettre en pause l'enregistrement vidéo.",
    videoResumeError: "Impossible de reprendre l'enregistrement vidéo.",
    videoStopError: "Impossible d'arrêter l'enregistrement vidéo. Veuillez réessayer.",
    videoSwitchCameraError: 'Impossible de changer de caméra. Veuillez réessayer.',
    videoSendError: "Impossible d'envoyer le message vidéo.",
    welcomeBack: 'Bon retour !',
    unsupportedFileType:
      'Type de fichier non pris en charge. Veuillez télécharger un fichier JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Ce fichier est trop volumineux. La taille maximale est de 200 Mo.',
    audioCloseError: "Erreur lors de la fermeture de l'audio :",
  },
  gl: {
    telletDoesNotExistTitle: 'Tellet non existe',
    projectDoesNotExistTitle: 'O proxecto non existe',
    projectDoesNotExistMessage: 'Sentímolo, pero este proxecto non existe.',
    maximumParticipantsTitle: 'Número máximo de participantes',
    maximumParticipantsMessage:
      'Este estudo acadou o seu número máximo de respostas e xa non acepta novas respostas.',
    somethingMissingTitle: 'Falta algo',
    somethingMissingMessage: 'Esta páxina non está dispoñible ou non existe.',
    projectCompletedTitle: 'O proxecto está completado',
    projectCompletedMessage: 'Este estudo foi completado e xa non acepta novas respostas.',
    projectInDraftOrReadyStatusTitle: 'O proxecto non está publicado',
    projectInDraftOrReadyStatusMessage:
      'Dado que o estudo aínda non foi publicado, non está aceptando respostas.',
    redirectingIn: 'Redirixindo en',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Volver ao panel',
    errorBoundaryTitle: 'Algo saíu mal',
    errorBoundaryMessage: 'Produciuse un erro inesperado. Recarga a páxina para continuar.',
    errorBoundaryReloadButton: 'Recargar páxina',
    messageProcessingError:
      'Vaia! Parece que temos problemas para procesar a túa mensaxe. Por favor, tenta enviala de novo.',
    microphoneProblemTextChat:
      'Parece que hai un problema co teu micrófono. Por favor, escribe as túas respostas usando o botón do teclado.',
    microphoneProblemVoiceChat:
      'Parece que hai un problema co teu micrófono. Por favor, comproba os permisos do teu navegador e téntao de novo.',
    responseTimeoutError: 'A resposta está tardando demasiado. Téntao de novo.',
    noResponseError: 'Non se detectou ningunha resposta. Téntao de novo.',
    connectionInterrupted: 'Conexión interrompida. Podes reenviar a túa mensaxe.',
    videoStartError: 'Non se puido iniciar a gravación de vídeo. Téntao de novo.',
    videoCameraNotSupported: 'A cámara non é compatible. Téntao cun navegador diferente.',
    videoPauseError: 'Non se puido pausar a gravación de vídeo.',
    videoResumeError: 'Non se puido retomar a gravación de vídeo.',
    videoStopError: 'Non se puido deter a gravación de vídeo. Téntao de novo.',
    videoSwitchCameraError: 'Non se puido cambiar de cámara. Téntao de novo.',
    videoSendError: 'Non se puido enviar a mensaxe de vídeo.',
    welcomeBack: 'Benvido de novo!',
    unsupportedFileType:
      'Tipo de ficheiro non compatible. Por favor, sobe un ficheiro JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Este ficheiro é demasiado grande. O tamaño máximo é de 200 MB.',
    audioCloseError: 'Erro ao pechar o audio:',
  },
  de: {
    telletDoesNotExistTitle: 'Tellet existiert nicht',
    projectDoesNotExistTitle: 'Projekt existiert nicht',
    projectDoesNotExistMessage: 'Es tut uns leid, aber dieses Projekt existiert nicht.',
    maximumParticipantsTitle: 'Maximale Teilnehmerzahl',
    maximumParticipantsMessage:
      'Diese Studie hat die maximale Anzahl an Antworten erreicht und nimmt keine neuen Antworten mehr an.',
    somethingMissingTitle: 'Etwas fehlt',
    somethingMissingMessage: 'Diese Seite ist nicht verfügbar oder existiert nicht.',
    projectCompletedTitle: 'Projekt ist abgeschlossen',
    projectCompletedMessage:
      'Diese Studie wurde abgeschlossen und nimmt keine neuen Antworten mehr an.',
    projectInDraftOrReadyStatusTitle: 'Projekt ist nicht veröffentlicht',
    projectInDraftOrReadyStatusMessage:
      'Da die Studie noch nicht veröffentlicht wurde, werden keine Antworten angenommen.',
    redirectingIn: 'Weiterleitung in',
    seconds: 'Sekunden...',
    cancel: 'Abbrechen',
    returnToPanel: 'Zurück zum Panel',
    errorBoundaryTitle: 'Etwas ist schiefgelaufen',
    errorBoundaryMessage:
      'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu, um fortzufahren.',
    errorBoundaryReloadButton: 'Seite neu laden',
    messageProcessingError:
      'Hoppla! Anscheinend haben wir Probleme, Ihre Nachricht zu verarbeiten. Bitte versuchen Sie, sie erneut zu senden.',
    microphoneProblemTextChat:
      'Es scheint ein Problem mit Ihrem Mikrofon zu geben. Bitte tippen Sie Ihre Antworten über die Tastaturschaltfläche ein.',
    microphoneProblemVoiceChat:
      'Es scheint ein Problem mit Ihrem Mikrofon zu geben. Bitte überprüfen Sie Ihre Browser-Berechtigungen und versuchen Sie es erneut.',
    responseTimeoutError: 'Die Antwort dauert zu lange. Bitte versuchen Sie es erneut.',
    noResponseError: 'Keine Antwort erkannt. Bitte versuchen Sie es erneut.',
    connectionInterrupted: 'Verbindung unterbrochen. Sie können Ihre Nachricht erneut senden.',
    videoStartError:
      'Videoaufzeichnung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    videoCameraNotSupported:
      'Kamera wird nicht unterstützt. Bitte versuchen Sie es mit einem anderen Browser.',
    videoPauseError: 'Videoaufzeichnung konnte nicht pausiert werden.',
    videoResumeError: 'Videoaufzeichnung konnte nicht fortgesetzt werden.',
    videoStopError:
      'Videoaufzeichnung konnte nicht gestoppt werden. Bitte versuchen Sie es erneut.',
    videoSwitchCameraError: 'Kamera konnte nicht gewechselt werden. Bitte versuchen Sie es erneut.',
    videoSendError: 'Videonachricht konnte nicht gesendet werden.',
    welcomeBack: 'Willkommen zurück!',
    unsupportedFileType:
      'Nicht unterstützter Dateityp. Bitte laden Sie eine JPG-, PNG-, GIF- oder MP4-Datei hoch.',
    fileTooLarge: 'Diese Datei ist zu groß. Die maximale Größe beträgt 200 MB.',
    audioCloseError: 'Fehler beim Schließen des Audios:',
  },
  el: {
    telletDoesNotExistTitle: 'Το Tellet δεν υπάρχει',
    projectDoesNotExistTitle: 'Το έργο δεν υπάρχει',
    projectDoesNotExistMessage: 'Λυπούμαστε, αλλά αυτό το έργο δεν υπάρχει.',
    maximumParticipantsTitle: 'Μέγιστος αριθμός συμμετεχόντων',
    maximumParticipantsMessage:
      'Αυτή η μελέτη έχει φτάσει στον μέγιστο αριθμό απαντήσεων και δεν δέχεται πλέον νέες απαντήσεις.',
    somethingMissingTitle: 'Κάτι λείπει',
    somethingMissingMessage: 'Αυτή η σελίδα δεν είναι διαθέσιμη ή δεν υπάρχει.',
    projectCompletedTitle: 'Το έργο έχει ολοκληρωθεί',
    projectCompletedMessage:
      'Αυτή η μελέτη έχει ολοκληρωθεί και δεν δέχεται πλέον νέες απαντήσεις.',
    projectInDraftOrReadyStatusTitle: 'Το έργο δεν έχει δημοσιευτεί',
    projectInDraftOrReadyStatusMessage:
      'Επειδή η μελέτη δεν έχει δημοσιευτεί ακόμη, δεν δέχεται απαντήσεις.',
    redirectingIn: 'Ανακατεύθυνση σε',
    seconds: 'δευτερόλεπτα...',
    cancel: 'Ακύρωση',
    returnToPanel: 'Επιστροφή στο Panel',
    errorBoundaryTitle: 'Κάτι πήγε στραβά',
    errorBoundaryMessage:
      'Παρουσιάστηκε ένα απρόσμενο σφάλμα. Παρακαλώ επαναφορτώστε τη σελίδα για να συνεχίσετε.',
    errorBoundaryReloadButton: 'Επαναφόρτωση σελίδας',
    messageProcessingError:
      'Ουπς! Φαίνεται ότι αντιμετωπίζουμε πρόβλημα με την επεξεργασία του μηνύματός σας. Παρακαλώ προσπαθήστε να το στείλετε ξανά.',
    microphoneProblemTextChat:
      'Φαίνεται ότι υπάρχει πρόβλημα με το μικρόφωνό σας. Παρακαλώ πληκτρολογήστε τις απαντήσεις σας χρησιμοποιώντας το κουμπί του πληκτρολογίου.',
    microphoneProblemVoiceChat:
      'Φαίνεται ότι υπάρχει πρόβλημα με το μικρόφωνό σας. Παρακαλώ ελέγξτε τα δικαιώματα του προγράμματος περιήγησής σας και δοκιμάστε ξανά.',
    responseTimeoutError: 'Η απόκριση καθυστερεί πολύ. Παρακαλώ δοκιμάστε ξανά.',
    noResponseError: 'Δεν εντοπίστηκε απόκριση. Παρακαλώ δοκιμάστε ξανά.',
    connectionInterrupted: 'Η σύνδεση διακόπηκε. Μπορείτε να στείλετε ξανά το μήνυμά σας.',
    videoStartError: 'Η εκκίνηση της εγγραφής βίντεο απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoCameraNotSupported:
      'Η κάμερα δεν υποστηρίζεται. Παρακαλώ δοκιμάστε με διαφορετικό πρόγραμμα περιήγησης.',
    videoPauseError: 'Η παύση της εγγραφής βίντεο απέτυχε.',
    videoResumeError: 'Η συνέχιση της εγγραφής βίντεο απέτυχε.',
    videoStopError: 'Η διακοπή της εγγραφής βίντεο απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoSwitchCameraError: 'Η αλλαγή κάμερας απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoSendError: 'Η αποστολή του μηνύματος βίντεο απέτυχε.',
    welcomeBack: 'Καλώς ήρθατε ξανά!',
    unsupportedFileType:
      'Μη υποστηριζόμενος τύπος αρχείου. Παρακαλώ ανεβάστε ένα αρχείο JPG, PNG, GIF ή MP4.',
    fileTooLarge: 'Αυτό το αρχείο είναι πολύ μεγάλο. Το μέγιστο μέγεθος είναι 200 MB.',
    audioCloseError: 'Σφάλμα κατά το κλείσιμο του ήχου:',
  },
  iw: {
    telletDoesNotExistTitle: 'Tellet לא קיים',
    projectDoesNotExistTitle: 'הפרויקט לא קיים',
    projectDoesNotExistMessage: 'אנו מצטערים אך הפרויקט הזה לא קיים.',
    maximumParticipantsTitle: 'מספר המשתתפים המרבי',
    maximumParticipantsMessage: 'מחקר זה הגיע למספר התשובות המרבי ואינו מקבל תשובות חדשות.',
    somethingMissingTitle: 'משהו חסר',
    somethingMissingMessage: 'דף זה אינו זמין או אינו קיים.',
    projectCompletedTitle: 'הפרויקט הושלם',
    projectCompletedMessage: 'מחקר זה הושלם ואינו מקבל תשובות חדשות.',
    projectInDraftOrReadyStatusTitle: 'הפרויקט לא פורסם',
    projectInDraftOrReadyStatusMessage: 'מכיוון שהמחקר עדיין לא פורסם, הוא אינו מקבל תשובות.',
    redirectingIn: 'הפניה בעוד',
    seconds: 'שניות...',
    cancel: 'ביטול',
    returnToPanel: 'חזרה לפאנל',
    errorBoundaryTitle: 'משהו השתבש',
    errorBoundaryMessage: 'אירעה שגיאה לא צפויה. אנא טען מחדש את הדף כדי להמשיך.',
    errorBoundaryReloadButton: 'טען דף מחדש',
    messageProcessingError: 'אופס! נראה שאנחנו מתקשים לעבד את ההודעה שלך. אנא נסה לשלוח אותה שוב.',
    microphoneProblemTextChat:
      'נראה שיש בעיה עם המיקרופון שלך. אנא הקלד את תשובותיך באמצעות לחצן המקלדת.',
    microphoneProblemVoiceChat:
      'נראה שיש בעיה עם המיקרופון שלך. אנא בדוק את הרשאות הדפדפן שלך ונסה שוב.',
    responseTimeoutError: 'התגובה אורכת זמן רב מדי. אנא נסה שוב.',
    noResponseError: 'לא זוהתה תגובה. אנא נסה שוב.',
    connectionInterrupted: 'החיבור נקטע. תוכל לשלוח את ההודעה שלך שוב.',
    videoStartError: 'הפעלת הקלטת הווידאו נכשלה. אנא נסה שוב.',
    videoCameraNotSupported: 'המצלמה אינה נתמכת. אנא נסה עם דפדפן אחר.',
    videoPauseError: 'השהיית הקלטת הווידאו נכשלה.',
    videoResumeError: 'חידוש הקלטת הווידאו נכשל.',
    videoStopError: 'עצירת הקלטת הווידאו נכשלה. אנא נסה שוב.',
    videoSwitchCameraError: 'החלפת המצלמה נכשלה. אנא נסה שוב.',
    videoSendError: 'שליחת הודעת הווידאו נכשלה.',
    welcomeBack: 'ברוך שובך!',
    unsupportedFileType: 'סוג קובץ לא נתמך. אנא העלה קובץ JPG, PNG, GIF או MP4.',
    fileTooLarge: 'הקובץ גדול מדי. הגודל המרבי הוא 200 MB.',
    audioCloseError: 'שגיאה בסגירת האודיו:',
  },
  hi: {
    telletDoesNotExistTitle: 'Tellet मौजूद नहीं है',
    projectDoesNotExistTitle: 'प्रोजेक्ट मौजूद नहीं है',
    projectDoesNotExistMessage: 'हमें खेद है, लेकिन यह प्रोजेक्ट मौजूद नहीं है।',
    maximumParticipantsTitle: 'प्रतिभागियों की अधिकतम संख्या',
    maximumParticipantsMessage:
      'इस अध्ययन ने प्रतिक्रियाओं की अधिकतम संख्या तक पहुँच गया है और अब नई प्रतिक्रियाएँ स्वीकार नहीं कर रहा है।',
    somethingMissingTitle: 'कुछ गायब है',
    somethingMissingMessage: 'यह पेज उपलब्ध नहीं है या मौजूद नहीं है।',
    projectCompletedTitle: 'प्रोजेक्ट पूरा हो गया है',
    projectCompletedMessage:
      'यह अध्ययन पूरा हो चुका है और अब नई प्रतिक्रियाएँ स्वीकार नहीं कर रहा है।',
    projectInDraftOrReadyStatusTitle: 'प्रोजेक्ट प्रकाशित नहीं है',
    projectInDraftOrReadyStatusMessage:
      'क्योंकि अध्ययन अभी तक प्रकाशित नहीं हुआ है, यह प्रतिक्रियाएँ स्वीकार नहीं कर रहा है।',
    redirectingIn: 'रीडायरेक्ट हो रहा है',
    seconds: 'सेकंड में...',
    cancel: 'रद्द करें',
    returnToPanel: 'पैनल पर वापस जाएँ',
    errorBoundaryTitle: 'कुछ गलत हो गया',
    errorBoundaryMessage: 'एक अप्रत्याशित त्रुटि हुई। जारी रखने के लिए कृपया पेज को पुनः लोड करें।',
    errorBoundaryReloadButton: 'पेज पुनः लोड करें',
    messageProcessingError:
      'अरे! लगता है हमें आपका संदेश प्रोसेस करने में समस्या हो रही है। कृपया इसे फिर से भेजने का प्रयास करें।',
    microphoneProblemTextChat:
      'ऐसा लगता है कि आपके माइक्रोफ़ोन में समस्या है। कृपया कीबोर्ड बटन का उपयोग करके अपने उत्तर टाइप करें।',
    microphoneProblemVoiceChat:
      'ऐसा लगता है कि आपके माइक्रोफ़ोन में समस्या है। कृपया अपने ब्राउज़र की अनुमतियाँ जाँचें और पुनः प्रयास करें।',
    responseTimeoutError: 'प्रतिक्रिया में बहुत अधिक समय लग रहा है। कृपया पुनः प्रयास करें।',
    noResponseError: 'कोई प्रतिक्रिया नहीं मिली। कृपया पुनः प्रयास करें।',
    connectionInterrupted: 'कनेक्शन बाधित हुआ। आप अपना संदेश फिर से भेज सकते हैं।',
    videoStartError: 'वीडियो रिकॉर्डिंग शुरू करने में विफल। कृपया पुनः प्रयास करें।',
    videoCameraNotSupported: 'कैमरा समर्थित नहीं है। कृपया किसी अन्य ब्राउज़र से प्रयास करें।',
    videoPauseError: 'वीडियो रिकॉर्डिंग रोकने में विफल।',
    videoResumeError: 'वीडियो रिकॉर्डिंग फिर से शुरू करने में विफल।',
    videoStopError: 'वीडियो रिकॉर्डिंग बंद करने में विफल। कृपया पुनः प्रयास करें।',
    videoSwitchCameraError: 'कैमरा बदलने में विफल। कृपया पुनः प्रयास करें।',
    videoSendError: 'वीडियो संदेश भेजने में विफल।',
    welcomeBack: 'वापसी पर स्वागत है!',
    unsupportedFileType: 'असमर्थित फ़ाइल प्रकार। कृपया JPG, PNG, GIF, या MP4 फ़ाइल अपलोड करें।',
    fileTooLarge: 'यह फ़ाइल बहुत बड़ी है। अधिकतम आकार 200 MB है।',
    audioCloseError: 'ऑडियो बंद करने में त्रुटि:',
  },
  hu: {
    telletDoesNotExistTitle: 'A Tellet nem létezik',
    projectDoesNotExistTitle: 'A projekt nem létezik',
    projectDoesNotExistMessage: 'Sajnáljuk, de ez a projekt nem létezik.',
    maximumParticipantsTitle: 'Résztvevők maximális száma',
    maximumParticipantsMessage:
      'Ez a tanulmány elérte a válaszok maximális számát, és már nem fogad el új válaszokat.',
    somethingMissingTitle: 'Valami hiányzik',
    somethingMissingMessage: 'Ez az oldal nem elérhető, vagy nem létezik.',
    projectCompletedTitle: 'A projekt befejeződött',
    projectCompletedMessage: 'Ez a tanulmány befejeződött, és már nem fogad el új válaszokat.',
    projectInDraftOrReadyStatusTitle: 'A projekt nincs közzétéve',
    projectInDraftOrReadyStatusMessage:
      'Mivel a tanulmány még nem került közzétételre, nem fogad el válaszokat.',
    redirectingIn: 'Átirányítás',
    seconds: 'másodperc múlva...',
    cancel: 'Mégse',
    returnToPanel: 'Vissza a panelhez',
    errorBoundaryTitle: 'Valami hiba történt',
    errorBoundaryMessage: 'Váratlan hiba történt. A folytatáshoz töltse be újra az oldalt.',
    errorBoundaryReloadButton: 'Oldal újratöltése',
    messageProcessingError:
      'Hoppá! Úgy tűnik, problémába ütköztünk az üzenete feldolgozása közben. Kérjük, próbálja meg újraküldeni.',
    microphoneProblemTextChat:
      'Úgy tűnik, probléma van a mikrofonjával. Kérjük, gépelje be a válaszait a billentyűzet gomb segítségével.',
    microphoneProblemVoiceChat:
      'Úgy tűnik, probléma van a mikrofonjával. Kérjük, ellenőrizze a böngésző engedélyeit, és próbálja újra.',
    responseTimeoutError: 'A válasz túl sokáig tart. Kérjük, próbálja újra.',
    noResponseError: 'Nem érkezett válasz. Kérjük, próbálja újra.',
    connectionInterrupted: 'A kapcsolat megszakadt. Újraküldheti az üzenetét.',
    videoStartError: 'A videofelvétel indítása nem sikerült. Kérjük, próbálja újra.',
    videoCameraNotSupported: 'A kamera nem támogatott. Kérjük, próbálja egy másik böngészővel.',
    videoPauseError: 'A videofelvétel szüneteltetése nem sikerült.',
    videoResumeError: 'A videofelvétel folytatása nem sikerült.',
    videoStopError: 'A videofelvétel leállítása nem sikerült. Kérjük, próbálja újra.',
    videoSwitchCameraError: 'A kameraváltás nem sikerült. Kérjük, próbálja újra.',
    videoSendError: 'A videoüzenet küldése nem sikerült.',
    welcomeBack: 'Üdvözöljük újra!',
    unsupportedFileType:
      'Nem támogatott fájltípus. Kérjük, töltsön fel JPG, PNG, GIF vagy MP4 fájlt.',
    fileTooLarge: 'Ez a fájl túl nagy. A maximális méret 200 MB.',
    audioCloseError: 'Hiba a hang bezárásakor:',
  },
  is: {
    telletDoesNotExistTitle: 'Tellet er ekki til',
    projectDoesNotExistTitle: 'Verkefnið er ekki til',
    projectDoesNotExistMessage: 'Því miður, þetta verkefni er ekki til.',
    maximumParticipantsTitle: 'Hámarksfjöldi þátttakenda',
    maximumParticipantsMessage:
      'Þessi rannsókn hefur náð hámarksfjölda svara og tekur ekki lengur við nýjum svörum.',
    somethingMissingTitle: 'Eitthvað vantar',
    somethingMissingMessage: 'Þessi síða er ekki tiltæk eða er ekki til.',
    projectCompletedTitle: 'Verkefninu er lokið',
    projectCompletedMessage: 'Þessari rannsókn er lokið og hún tekur ekki lengur við nýjum svörum.',
    projectInDraftOrReadyStatusTitle: 'Verkefnið hefur ekki verið birt',
    projectInDraftOrReadyStatusMessage:
      'Þar sem rannsóknin hefur ekki verið birt ennþá, tekur hún ekki við svörum.',
    redirectingIn: 'Áframsent eftir',
    seconds: 'sekúndur...',
    cancel: 'Hætta við',
    returnToPanel: 'Fara aftur á Panel',
    errorBoundaryTitle: 'Eitthvað fór úrskeiðis',
    errorBoundaryMessage:
      'Óvænt villa kom upp. Vinsamlegast endurhladið síðuna til að halda áfram.',
    errorBoundaryReloadButton: 'Endurhlaða síðu',
    messageProcessingError:
      'Úbbs! Það virðist sem við eigum í vandræðum með að vinna úr skilaboðunum þínum. Vinsamlegast reyndu að senda þau aftur.',
    microphoneProblemTextChat:
      'Það virðist vera vandamál með hljóðnemann þinn. Vinsamlegast sláðu inn svörin þín með lyklaborðshnappnum.',
    microphoneProblemVoiceChat:
      'Það virðist vera vandamál með hljóðnemann þinn. Vinsamlegast athugaðu heimildir vafrans og reyndu aftur.',
    responseTimeoutError: 'Svarið tekur of langan tíma. Vinsamlegast reyndu aftur.',
    noResponseError: 'Ekkert svar greint. Vinsamlegast reyndu aftur.',
    connectionInterrupted: 'Tenging rofin. Þú getur sent skilaboðin þín aftur.',
    videoStartError: 'Ekki tókst að hefja myndbandsupptöku. Vinsamlegast reyndu aftur.',
    videoCameraNotSupported: 'Myndavélin er ekki studd. Vinsamlegast reyndu með öðrum vafra.',
    videoPauseError: 'Ekki tókst að gera hlé á myndbandsupptöku.',
    videoResumeError: 'Ekki tókst að halda áfram myndbandsupptöku.',
    videoStopError: 'Ekki tókst að stöðva myndbandsupptöku. Vinsamlegast reyndu aftur.',
    videoSwitchCameraError: 'Ekki tókst að skipta um myndavél. Vinsamlegast reyndu aftur.',
    videoSendError: 'Ekki tókst að senda myndbandsskilaboð.',
    welcomeBack: 'Velkomin aftur!',
    unsupportedFileType:
      'Óstudd skráartegund. Vinsamlegast hladdu upp JPG-, PNG-, GIF- eða MP4-skrá.',
    fileTooLarge: 'Þessi skrá er of stór. Hámarksstærð er 200 MB.',
    audioCloseError: 'Villa við að loka hljóði:',
  },
  it: {
    telletDoesNotExistTitle: 'Tellet non esiste',
    projectDoesNotExistTitle: 'Il progetto non esiste',
    projectDoesNotExistMessage: 'Ci dispiace, ma questo progetto non esiste.',
    maximumParticipantsTitle: 'Numero massimo di partecipanti',
    maximumParticipantsMessage:
      'Questo studio ha raggiunto il numero massimo di risposte e non accetta più nuove risposte.',
    somethingMissingTitle: 'Manca qualcosa',
    somethingMissingMessage: 'Questa pagina non è disponibile o non esiste.',
    projectCompletedTitle: 'Il progetto è completato',
    projectCompletedMessage: 'Questo studio è stato completato e non accetta più nuove risposte.',
    projectInDraftOrReadyStatusTitle: 'Il progetto non è pubblicato',
    projectInDraftOrReadyStatusMessage:
      'Poiché lo studio non è ancora stato pubblicato, non accetta risposte.',
    redirectingIn: 'Reindirizzamento tra',
    seconds: 'secondi...',
    cancel: 'Annulla',
    returnToPanel: 'Torna al Panel',
    errorBoundaryTitle: 'Qualcosa è andato storto',
    errorBoundaryMessage:
      'Si è verificato un errore imprevisto. Ricarica la pagina per continuare.',
    errorBoundaryReloadButton: 'Ricarica pagina',
    messageProcessingError:
      'Ops! Sembra che stiamo avendo problemi a elaborare il tuo messaggio. Prova a inviarlo di nuovo.',
    microphoneProblemTextChat:
      'Sembra esserci un problema con il tuo microfono. Digita le tue risposte utilizzando il pulsante della tastiera.',
    microphoneProblemVoiceChat:
      'Sembra esserci un problema con il tuo microfono. Controlla i permessi del browser e riprova.',
    responseTimeoutError: 'La risposta sta impiegando troppo tempo. Riprova.',
    noResponseError: 'Nessuna risposta rilevata. Riprova.',
    connectionInterrupted: 'Connessione interrotta. Puoi inviare di nuovo il tuo messaggio.',
    videoStartError: 'Impossibile avviare la registrazione video. Riprova.',
    videoCameraNotSupported: 'La fotocamera non è supportata. Prova con un browser diverso.',
    videoPauseError: 'Impossibile mettere in pausa la registrazione video.',
    videoResumeError: 'Impossibile riprendere la registrazione video.',
    videoStopError: 'Impossibile interrompere la registrazione video. Riprova.',
    videoSwitchCameraError: 'Impossibile cambiare fotocamera. Riprova.',
    videoSendError: 'Impossibile inviare il messaggio video.',
    welcomeBack: 'Bentornato!',
    unsupportedFileType: 'Tipo di file non supportato. Carica un file JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Questo file è troppo grande. La dimensione massima è di 200 MB.',
    audioCloseError: "Errore nella chiusura dell'audio:",
  },
  ja: {
    telletDoesNotExistTitle: 'Telletは存在しません',
    projectDoesNotExistTitle: 'プロジェクトは存在しません',
    projectDoesNotExistMessage: '申し訳ありませんが、このプロジェクトは存在しません。',
    maximumParticipantsTitle: '参加者の上限',
    maximumParticipantsMessage:
      'この調査は回答数の上限に達したため、新しい回答は受け付けていません。',
    somethingMissingTitle: '何かが見つかりません',
    somethingMissingMessage: 'このページは利用できないか、存在しません。',
    projectCompletedTitle: 'プロジェクトは完了しました',
    projectCompletedMessage: 'この調査は完了し、新しい回答は受け付けていません。',
    projectInDraftOrReadyStatusTitle: 'プロジェクトは公開されていません',
    projectInDraftOrReadyStatusMessage:
      '調査がまだ公開されていないため、回答を受け付けていません。',
    redirectingIn: 'リダイレクトまで',
    seconds: '秒...',
    cancel: 'キャンセル',
    returnToPanel: 'パネルに戻る',
    errorBoundaryTitle: '問題が発生しました',
    errorBoundaryMessage:
      '予期しないエラーが発生しました。続行するにはページを再読み込みしてください。',
    errorBoundaryReloadButton: 'ページを再読み込み',
    messageProcessingError:
      'おっと！メッセージの処理に問題が発生しているようです。もう一度送信してみてください。',
    microphoneProblemTextChat:
      'マイクに問題があるようです。キーボードボタンを使って回答を入力してください。',
    microphoneProblemVoiceChat:
      'マイクに問題があるようです。ブラウザの権限を確認して、もう一度お試しください。',
    responseTimeoutError: '応答に時間がかかりすぎています。もう一度お試しください。',
    noResponseError: '応答が検出されませんでした。もう一度お試しください。',
    connectionInterrupted: '接続が中断されました。メッセージを再送信できます。',
    videoStartError: 'ビデオ録画の開始に失敗しました。もう一度お試しください。',
    videoCameraNotSupported: 'カメラはサポートされていません。別のブラウザでお試しください。',
    videoPauseError: 'ビデオ録画の一時停止に失敗しました。',
    videoResumeError: 'ビデオ録画の再開に失敗しました。',
    videoStopError: 'ビデオ録画の停止に失敗しました。もう一度お試しください。',
    videoSwitchCameraError: 'カメラの切り替えに失敗しました。もう一度お試しください。',
    videoSendError: 'ビデオメッセージの送信に失敗しました。',
    welcomeBack: 'おかえりなさい！',
    unsupportedFileType:
      'サポートされていないファイル形式です。JPG、PNG、GIF、またはMP4ファイルをアップロードしてください。',
    fileTooLarge: 'このファイルは大きすぎます。最大サイズは200 MBです。',
    audioCloseError: 'オーディオを閉じる際のエラー：',
  },
  ka: {
    telletDoesNotExistTitle: 'Tellet არ არსებობს',
    projectDoesNotExistTitle: 'პროექტი არ არსებობს',
    projectDoesNotExistMessage: 'ბოდიშს გიხდით, მაგრამ ეს პროექტი არ არსებობს.',
    maximumParticipantsTitle: 'მონაწილეთა მაქსიმალური რაოდენობა',
    maximumParticipantsMessage:
      'ამ კვლევამ მიაღწია პასუხების მაქსიმალურ რაოდენობას და აღარ იღებს ახალ პასუხებს.',
    somethingMissingTitle: 'რაღაც აკლია',
    somethingMissingMessage: 'ეს გვერდი მიუწვდომელია ან არ არსებობს.',
    projectCompletedTitle: 'პროექტი დასრულებულია',
    projectCompletedMessage: 'ეს კვლევა დასრულდა და აღარ იღებს ახალ პასუხებს.',
    projectInDraftOrReadyStatusTitle: 'პროექტი არ არის გამოქვეყნებული',
    projectInDraftOrReadyStatusMessage:
      'ვინაიდან კვლევა ჯერ არ გამოქვეყნებულა, ის არ იღებს პასუხებს.',
    redirectingIn: 'გადამისამართება',
    seconds: 'წამში...',
    cancel: 'გაუქმება',
    returnToPanel: 'დაბრუნება პანელზე',
    errorBoundaryTitle: 'რაღაც არასწორად წავიდა',
    errorBoundaryMessage: 'მოხდა მოულოდნელი შეცდომა. გასაგრძელებლად გთხოვთ განაახლოთ გვერდი.',
    errorBoundaryReloadButton: 'გვერდის განახლება',
    messageProcessingError:
      'ოპა! როგორც ჩანს, თქვენი შეტყობინების დამუშავებასთან დაკავშირებით პრობლემა გვაქვს. გთხოვთ, სცადოთ ხელახლა გაგზავნა.',
    microphoneProblemTextChat:
      'როგორც ჩანს, თქვენს მიკროფონთან პრობლემაა. გთხოვთ, აკრიფოთ თქვენი პასუხები კლავიატურის ღილაკის გამოყენებით.',
    microphoneProblemVoiceChat:
      'როგორც ჩანს, თქვენს მიკროფონთან პრობლემაა. გთხოვთ, შეამოწმოთ თქვენი ბრაუზერის ნებართვები და სცადოთ ხელახლა.',
    responseTimeoutError: 'პასუხი ძალიან დიდხანს გრძელდება. გთხოვთ, სცადოთ ხელახლა.',
    noResponseError: 'პასუხი ვერ მოიძებნა. გთხოვთ, სცადოთ ხელახლა.',
    connectionInterrupted: 'კავშირი შეწყდა. შეგიძლიათ ხელახლა გაგზავნოთ თქვენი შეტყობინება.',
    videoStartError: 'ვიდეო ჩაწერის დაწყება ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoCameraNotSupported: 'კამერა არ არის მხარდაჭერილი. გთხოვთ, სცადოთ სხვა ბრაუზერით.',
    videoPauseError: 'ვიდეო ჩაწერის შეჩერება ვერ მოხერხდა.',
    videoResumeError: 'ვიდეო ჩაწერის გაგრძელება ვერ მოხერხდა.',
    videoStopError: 'ვიდეო ჩაწერის შეწყვეტა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoSwitchCameraError: 'კამერის გადართვა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoSendError: 'ვიდეო შეტყობინების გაგზავნა ვერ მოხერხდა.',
    welcomeBack: 'მოგესალმებით ისევ!',
    unsupportedFileType: 'მხარდაუჭერელი ფაილის ტიპი. გთხოვთ, ატვირთოთ JPG, PNG, GIF ან MP4 ფაილი.',
    fileTooLarge: 'ეს ფაილი ძალიან დიდია. მაქსიმალური ზომაა 200 MB.',
    audioCloseError: 'შეცდომა აუდიოს დახურვისას:',
  },
  kn: {
    telletDoesNotExistTitle: 'Tellet ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ',
    projectDoesNotExistTitle: 'ಯೋಜನೆ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ',
    projectDoesNotExistMessage: 'ಕ್ಷಮಿಸಿ, ಈ ಯೋಜನೆ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ.',
    maximumParticipantsTitle: 'ಗರಿಷ್ಠ ಭಾಗವಹಿಸುವವರ ಸಂಖ್ಯೆ',
    maximumParticipantsMessage:
      'ಈ ಅಧ್ಯಯನವು ಗರಿಷ್ಠ ಪ್ರತಿಕ್ರಿಯೆಗಳನ್ನು ತಲುಪಿದೆ ಮತ್ತು ಇನ್ನು ಮುಂದೆ ಹೊಸ ಪ್ರತಿಕ್ರಿಯೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತಿಲ್ಲ.',
    somethingMissingTitle: 'ಏನೋ ಕಾಣೆಯಾಗಿದೆ',
    somethingMissingMessage: 'ಈ ಪುಟ ಲಭ್ಯವಿಲ್ಲ ಅಥವಾ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ.',
    projectCompletedTitle: 'ಯೋಜನೆ ಪೂರ್ಣಗೊಂಡಿದೆ',
    projectCompletedMessage:
      'ಈ ಅಧ್ಯಯನವು ಪೂರ್ಣಗೊಂಡಿದೆ ಮತ್ತು ಇನ್ನು ಮುಂದೆ ಹೊಸ ಪ್ರತಿಕ್ರಿಯೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತಿಲ್ಲ.',
    projectInDraftOrReadyStatusTitle: 'ಯೋಜನೆ ಪ್ರಕಟಿಸಲಾಗಿಲ್ಲ',
    projectInDraftOrReadyStatusMessage:
      'ಅಧ್ಯಯನವನ್ನು ಇನ್ನೂ ಪ್ರಕಟಿಸಲಾಗಿಲ್ಲದ ಕಾರಣ, ಇದು ಪ್ರತಿಕ್ರಿಯೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತಿಲ್ಲ.',
    redirectingIn: 'ಮರುನಿರ್ದೇಶಿಸಲಾಗುತ್ತಿದೆ',
    seconds: 'ಸೆಕೆಂಡುಗಳಲ್ಲಿ...',
    cancel: 'ರದ್ದುಮಾಡಿ',
    returnToPanel: 'ಪ್ಯಾನೆಲ್‌ಗೆ ಹಿಂತಿರುಗಿ',
    errorBoundaryTitle: 'ಏನೋ ತಪ್ಪಾಗಿದೆ',
    errorBoundaryMessage: 'ಅನಿರೀಕ್ಷಿತ ದೋಷ ಸಂಭವಿಸಿದೆ. ಮುಂದುವರಿಸಲು ದಯವಿಟ್ಟು ಪುಟವನ್ನು ಮರುಲೋಡ್ ಮಾಡಿ.',
    errorBoundaryReloadButton: 'ಪುಟವನ್ನು ಮರುಲೋಡ್ ಮಾಡಿ',
    messageProcessingError:
      'ಅಯ್ಯೋ! ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುವಲ್ಲಿ ನಮಗೆ ತೊಂದರೆಯಾಗುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಅದನ್ನು ಮತ್ತೆ ಕಳುಹಿಸಲು ಪ್ರಯತ್ನಿಸಿ.',
    microphoneProblemTextChat:
      'ನಿಮ್ಮ ಮೈಕ್ರೊಫೋನ್‌ನಲ್ಲಿ ಸಮಸ್ಯೆ ಇರುವಂತೆ ತೋರುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಕೀಬೋರ್ಡ್ ಬಟನ್ ಬಳಸಿ ನಿಮ್ಮ ಉತ್ತರಗಳನ್ನು ಟೈಪ್ ಮಾಡಿ.',
    microphoneProblemVoiceChat:
      'ನಿಮ್ಮ ಮೈಕ್ರೊಫೋನ್‌ನಲ್ಲಿ ಸಮಸ್ಯೆ ಇರುವಂತೆ ತೋರುತ್ತಿದೆ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಅನುಮತಿಗಳನ್ನು ಪರಿಶೀಲಿಸಿ ಮತ್ತು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    responseTimeoutError: 'ಪ್ರತಿಕ್ರಿಯೆಗೆ ಹೆಚ್ಚು ಸಮಯ ತೆಗೆದುಕೊಳ್ಳುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    noResponseError: 'ಯಾವುದೇ ಪ್ರತಿಕ್ರಿಯೆ ಪತ್ತೆಯಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    connectionInterrupted: 'ಸಂಪರ್ಕ ಅಡಚಣೆಯಾಗಿದೆ. ನೀವು ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಮರುಕಳುಹಿಸಬಹುದು.',
    videoStartError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ಪ್ರಾರಂಭಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoCameraNotSupported: 'ಕ್ಯಾಮೆರಾ ಬೆಂಬಲಿತವಾಗಿಲ್ಲ. ದಯವಿಟ್ಟು ಬೇರೆ ಬ್ರೌಸರ್‌ನೊಂದಿಗೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoPauseError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ವಿರಾಮಗೊಳಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    videoResumeError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ಪುನರಾರಂಭಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    videoStopError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ನಿಲ್ಲಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoSwitchCameraError: 'ಕ್ಯಾಮೆರಾ ಬದಲಾಯಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoSendError: 'ವೀಡಿಯೊ ಸಂದೇಶ ಕಳುಹಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    welcomeBack: 'ಮರಳಿ ಸ್ವಾಗತ!',
    unsupportedFileType:
      'ಬೆಂಬಲಿತವಲ್ಲದ ಫೈಲ್ ಪ್ರಕಾರ. ದಯವಿಟ್ಟು JPG, PNG, GIF ಅಥವಾ MP4 ಫೈಲ್ ಅನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.',
    fileTooLarge: 'ಈ ಫೈಲ್ ತುಂಬಾ ದೊಡ್ಡದಾಗಿದೆ. ಗರಿಷ್ಠ ಗಾತ್ರ 200 MB.',
    audioCloseError: 'ಆಡಿಯೊ ಮುಚ್ಚುವಲ್ಲಿ ದೋಷ:',
  },
  kk: {
    telletDoesNotExistTitle: 'Tellet жоқ',
    projectDoesNotExistTitle: 'Жоба жоқ',
    projectDoesNotExistMessage: 'Кешіріңіз, бұл жоба жоқ.',
    maximumParticipantsTitle: 'Қатысушылардың ең көп саны',
    maximumParticipantsMessage:
      'Бұл зерттеу жауаптардың ең көп санына жетті және енді жаңа жауаптар қабылдамайды.',
    somethingMissingTitle: 'Бірдеңе жоқ',
    somethingMissingMessage: 'Бұл бет қолжетімсіз немесе жоқ.',
    projectCompletedTitle: 'Жоба аяқталды',
    projectCompletedMessage: 'Бұл зерттеу аяқталды және енді жаңа жауаптар қабылдамайды.',
    projectInDraftOrReadyStatusTitle: 'Жоба жарияланбаған',
    projectInDraftOrReadyStatusMessage: 'Зерттеу әлі жарияланбағандықтан, жауаптар қабылданбайды.',
    redirectingIn: 'Бағыттау',
    seconds: 'секундтан кейін...',
    cancel: 'Болдырмау',
    returnToPanel: 'Панельге оралу',
    errorBoundaryTitle: 'Бірдеңе дұрыс болмады',
    errorBoundaryMessage: 'Күтпеген қате пайда болды. Жалғастыру үшін бетті қайта жүктеңіз.',
    errorBoundaryReloadButton: 'Бетті қайта жүктеу',
    messageProcessingError:
      'Қап! Хабарламаңызды өңдеу кезінде мәселе туындады. Оны қайта жіберіп көріңіз.',
    microphoneProblemTextChat:
      'Микрофоныңызда ақау бар сияқты. Жауаптарыңызды пернетақта арқылы тере беріңіз.',
    microphoneProblemVoiceChat:
      'Микрофоныңызда ақау бар сияқты. Браузер рұқсаттарын тексеріп, қайталап көріңіз.',
    responseTimeoutError: 'Жауап тым ұзаққа созылуда. Қайталап көріңіз.',
    noResponseError: 'Жауап анықталмады. Қайталап көріңіз.',
    connectionInterrupted: 'Байланыс үзілді. Хабарламаңызды қайта жібере аласыз.',
    videoStartError: 'Бейне жазбаны бастау мүмкін болмады. Қайталап көріңіз.',
    videoCameraNotSupported: 'Камера қолдауға алынбайды. Басқа браузерді қолданып көріңіз.',
    videoPauseError: 'Бейне жазбаны кідірту мүмкін болмады.',
    videoResumeError: 'Бейне жазбаны жалғастыру мүмкін болмады.',
    videoStopError: 'Бейне жазбаны тоқтату мүмкін болмады. Қайталап көріңіз.',
    videoSwitchCameraError: 'Камераны ауыстыру мүмкін болмады. Қайталап көріңіз.',
    videoSendError: 'Бейне хабарламаны жіберу мүмкін болмады.',
    welcomeBack: 'Қайта келуіңізбен!',
    unsupportedFileType:
      'Қолдау көрсетілмейтін файл түрі. JPG, PNG, GIF немесе MP4 файлын жүктеңіз.',
    fileTooLarge: 'Бұл файл тым үлкен. Ең үлкен өлшемі — 200 МБ.',
    audioCloseError: 'Аудионы жабу қатесі:',
  },
  ko: {
    telletDoesNotExistTitle: 'Tellet이 존재하지 않습니다',
    projectDoesNotExistTitle: '프로젝트가 존재하지 않습니다',
    projectDoesNotExistMessage: '죄송합니다. 이 프로젝트는 존재하지 않습니다.',
    maximumParticipantsTitle: '최대 참가자 수 도달',
    maximumParticipantsMessage:
      '이 연구는 최대 응답 수에 도달하여 더 이상 새로운 응답을 받지 않습니다.',
    somethingMissingTitle: '무언가 누락되었습니다',
    somethingMissingMessage: '이 페이지는 사용할 수 없거나 존재하지 않습니다.',
    projectCompletedTitle: '프로젝트가 완료되었습니다',
    projectCompletedMessage: '이 연구는 완료되어 더 이상 새로운 응답을 받지 않습니다.',
    projectInDraftOrReadyStatusTitle: '프로젝트가 게시되지 않았습니다',
    projectInDraftOrReadyStatusMessage: '연구가 아직 게시되지 않아 응답을 받지 않습니다.',
    redirectingIn: '리디렉션까지',
    seconds: '초 남음...',
    cancel: '취소',
    returnToPanel: '패널로 돌아가기',
    errorBoundaryTitle: '문제가 발생했습니다',
    errorBoundaryMessage: '예기치 않은 오류가 발생했습니다. 계속하려면 페이지를 새로 고침하세요.',
    errorBoundaryReloadButton: '페이지 새로 고침',
    messageProcessingError: '앗! 메시지를 처리하는 데 문제가 있는 것 같습니다. 다시 보내주세요.',
    microphoneProblemTextChat:
      '마이크에 문제가 있는 것 같습니다. 키보드 버튼을 사용하여 답변을 입력해주세요.',
    microphoneProblemVoiceChat:
      '마이크에 문제가 있는 것 같습니다. 브라우저 권한을 확인하고 다시 시도해주세요.',
    responseTimeoutError: '응답이 너무 오래 걸립니다. 다시 시도해주세요.',
    noResponseError: '응답이 감지되지 않았습니다. 다시 시도해주세요.',
    connectionInterrupted: '연결이 중단되었습니다. 메시지를 다시 보낼 수 있습니다.',
    videoStartError: '비디오 녹화를 시작하지 못했습니다. 다시 시도해주세요.',
    videoCameraNotSupported: '카메라가 지원되지 않습니다. 다른 브라우저로 시도해주세요.',
    videoPauseError: '비디오 녹화를 일시 중지하지 못했습니다.',
    videoResumeError: '비디오 녹화를 재개하지 못했습니다.',
    videoStopError: '비디오 녹화를 중지하지 못했습니다. 다시 시도해주세요.',
    videoSwitchCameraError: '카메라 전환에 실패했습니다. 다시 시도해주세요.',
    videoSendError: '비디오 메시지를 보내지 못했습니다.',
    welcomeBack: '다시 오신 것을 환영합니다!',
    unsupportedFileType:
      '지원되지 않는 파일 형식입니다. JPG, PNG, GIF 또는 MP4 파일을 업로드해주세요.',
    fileTooLarge: '파일이 너무 큽니다. 최대 크기는 200 MB입니다.',
    audioCloseError: '오디오 닫기 오류:',
  },
  lv: {
    telletDoesNotExistTitle: 'Tellet neeksistē',
    projectDoesNotExistTitle: 'Projekts neeksistē',
    projectDoesNotExistMessage: 'Atvainojiet, šis projekts neeksistē.',
    maximumParticipantsTitle: 'Maksimālais dalībnieku skaits',
    maximumParticipantsMessage:
      'Šis pētījums ir sasniedzis maksimālo atbilžu skaitu un vairs nepieņem jaunas atbildes.',
    somethingMissingTitle: 'Kaut kā trūkst',
    somethingMissingMessage: 'Šī lapa nav pieejama vai neeksistē.',
    projectCompletedTitle: 'Projekts ir pabeigts',
    projectCompletedMessage: 'Šis pētījums ir pabeigts un vairs nepieņem jaunas atbildes.',
    projectInDraftOrReadyStatusTitle: 'Projekts nav publicēts',
    projectInDraftOrReadyStatusMessage: 'Tā kā pētījums vēl nav publicēts, tas nepieņem atbildes.',
    redirectingIn: 'Pāradresācija pēc',
    seconds: 'sekundēm...',
    cancel: 'Atcelt',
    returnToPanel: 'Atgriezties panelī',
    errorBoundaryTitle: 'Kaut kas nogāja greizi',
    errorBoundaryMessage: 'Radās neparedzēta kļūda. Lūdzu, pārlādējiet lapu, lai turpinātu.',
    errorBoundaryReloadButton: 'Pārlādēt lapu',
    messageProcessingError:
      'Ak! Šķiet, ka mums ir problēmas ar jūsu ziņojuma apstrādi. Lūdzu, mēģiniet to nosūtīt vēlreiz.',
    microphoneProblemTextChat:
      'Šķiet, ka ir problēma ar jūsu mikrofonu. Lūdzu, ierakstiet savas atbildes, izmantojot tastatūras pogu.',
    microphoneProblemVoiceChat:
      'Šķiet, ka ir problēma ar jūsu mikrofonu. Lūdzu, pārbaudiet pārlūkprogrammas atļaujas un mēģiniet vēlreiz.',
    responseTimeoutError: 'Atbilde aizņem pārāk ilgu laiku. Lūdzu, mēģiniet vēlreiz.',
    noResponseError: 'Atbilde netika atklāta. Lūdzu, mēģiniet vēlreiz.',
    connectionInterrupted:
      'Savienojums tika pārtraukts. Jūs varat atkārtoti nosūtīt savu ziņojumu.',
    videoStartError: 'Neizdevās sākt video ierakstu. Lūdzu, mēģiniet vēlreiz.',
    videoCameraNotSupported: 'Kamera netiek atbalstīta. Lūdzu, mēģiniet citā pārlūkprogrammā.',
    videoPauseError: 'Neizdevās apturēt video ierakstu.',
    videoResumeError: 'Neizdevās atsākt video ierakstu.',
    videoStopError: 'Neizdevās apturēt video ierakstu. Lūdzu, mēģiniet vēlreiz.',
    videoSwitchCameraError: 'Neizdevās pārslēgt kameru. Lūdzu, mēģiniet vēlreiz.',
    videoSendError: 'Neizdevās nosūtīt video ziņojumu.',
    welcomeBack: 'Laipni lūdzam atpakaļ!',
    unsupportedFileType:
      'Neatbalstīts faila tips. Lūdzu, augšupielādējiet JPG, PNG, GIF vai MP4 failu.',
    fileTooLarge: 'Šis fails ir pārāk liels. Maksimālais izmērs ir 200 MB.',
    audioCloseError: 'Kļūda, aizverot audio:',
  },
  lt: {
    telletDoesNotExistTitle: 'Tellet neegzistuoja',
    projectDoesNotExistTitle: 'Projektas neegzistuoja',
    projectDoesNotExistMessage: 'Atsiprašome, šis projektas neegzistuoja.',
    maximumParticipantsTitle: 'Maksimalus dalyvių skaičius',
    maximumParticipantsMessage:
      'Šis tyrimas pasiekė maksimalų atsakymų skaičių ir nebepriima naujų atsakymų.',
    somethingMissingTitle: 'Kažko trūksta',
    somethingMissingMessage: 'Šis puslapis nepasiekiamas arba neegzistuoja.',
    projectCompletedTitle: 'Projektas baigtas',
    projectCompletedMessage: 'Šis tyrimas baigtas ir nebepriima naujų atsakymų.',
    projectInDraftOrReadyStatusTitle: 'Projektas nepaskelbtas',
    projectInDraftOrReadyStatusMessage: 'Kadangi tyrimas dar nepaskelbtas, jis nepriima atsakymų.',
    redirectingIn: 'Nukreipiama po',
    seconds: 'sekundžių...',
    cancel: 'Atšaukti',
    returnToPanel: 'Grįžti į skydelį',
    errorBoundaryTitle: 'Kažkas nepavyko',
    errorBoundaryMessage: 'Įvyko netikėta klaida. Norėdami tęsti, perkraukite puslapį.',
    errorBoundaryReloadButton: 'Perkrauti puslapį',
    messageProcessingError:
      'Oi! Atrodo, turime problemų apdorodami jūsų žinutę. Pabandykite ją išsiųsti dar kartą.',
    microphoneProblemTextChat:
      'Atrodo, kad yra problema su jūsų mikrofonu. Įveskite atsakymus naudodami klaviatūros mygtuką.',
    microphoneProblemVoiceChat:
      'Atrodo, kad yra problema su jūsų mikrofonu. Patikrinkite naršyklės leidimus ir bandykite dar kartą.',
    responseTimeoutError: 'Atsakymas užtrunka per ilgai. Bandykite dar kartą.',
    noResponseError: 'Atsakymas neaptiktas. Bandykite dar kartą.',
    connectionInterrupted: 'Ryšys nutrūko. Galite iš naujo išsiųsti savo žinutę.',
    videoStartError: 'Nepavyko pradėti vaizdo įrašymo. Bandykite dar kartą.',
    videoCameraNotSupported: 'Kamera nepalaikoma. Pabandykite su kita naršykle.',
    videoPauseError: 'Nepavyko pristabdyti vaizdo įrašymo.',
    videoResumeError: 'Nepavyko atnaujinti vaizdo įrašymo.',
    videoStopError: 'Nepavyko sustabdyti vaizdo įrašymo. Bandykite dar kartą.',
    videoSwitchCameraError: 'Nepavyko perjungti kameros. Bandykite dar kartą.',
    videoSendError: 'Nepavyko išsiųsti vaizdo žinutės.',
    welcomeBack: 'Sveiki sugrįžę!',
    unsupportedFileType: 'Nepalaikomas failo tipas. Įkelkite JPG, PNG, GIF arba MP4 failą.',
    fileTooLarge: 'Šis failas per didelis. Didžiausias dydis — 200 MB.',
    audioCloseError: 'Klaida uždarant garso įrašą:',
  },
  mk: {
    telletDoesNotExistTitle: 'Tellet не постои',
    projectDoesNotExistTitle: 'Проектот не постои',
    projectDoesNotExistMessage: 'Извинете, но овој проект не постои.',
    maximumParticipantsTitle: 'Максимален број на учесници',
    maximumParticipantsMessage:
      'Оваа студија го достигна максималниот број на одговори и повеќе не прифаќа нови одговори.',
    somethingMissingTitle: 'Нешто недостасува',
    somethingMissingMessage: 'Оваа страница е недостапна или не постои.',
    projectCompletedTitle: 'Проектот е завршен',
    projectCompletedMessage: 'Оваа студија е завршена и повеќе не прифаќа нови одговори.',
    projectInDraftOrReadyStatusTitle: 'Проектот не е објавен',
    projectInDraftOrReadyStatusMessage:
      'Бидејќи студијата сè уште не е објавена, не прифаќа одговори.',
    redirectingIn: 'Пренасочување за',
    seconds: 'секунди...',
    cancel: 'Откажи',
    returnToPanel: 'Врати се на панелот',
    errorBoundaryTitle: 'Нешто тргна наопаку',
    errorBoundaryMessage:
      'Се појави неочекувана грешка. Ве молиме, освежете ја страницата за да продолжите.',
    errorBoundaryReloadButton: 'Освежи ја страницата',
    messageProcessingError:
      'Упс! Изгледа дека имаме проблем со обработката на вашата порака. Обидете се повторно да ја испратите.',
    microphoneProblemTextChat:
      'Се чини дека има проблем со вашиот микрофон. Ве молиме, внесете ги вашите одговори користејќи го копчето на тастатурата.',
    microphoneProblemVoiceChat:
      'Се чини дека има проблем со вашиот микрофон. Проверете ги дозволите на прелистувачот и обидете се повторно.',
    responseTimeoutError: 'Одговорот трае премногу долго. Обидете се повторно.',
    noResponseError: 'Не е откриен одговор. Обидете се повторно.',
    connectionInterrupted: 'Врската е прекината. Можете повторно да ја испратите вашата порака.',
    videoStartError: 'Неуспешно стартување на видео снимањето. Обидете се повторно.',
    videoCameraNotSupported: 'Камерата не е поддржана. Обидете се со друг прелистувач.',
    videoPauseError: 'Неуспешно паузирање на видео снимањето.',
    videoResumeError: 'Неуспешно продолжување на видео снимањето.',
    videoStopError: 'Неуспешно запирање на видео снимањето. Обидете се повторно.',
    videoSwitchCameraError: 'Неуспешно менување на камерата. Обидете се повторно.',
    videoSendError: 'Неуспешно испраќање на видео пораката.',
    welcomeBack: 'Добредојдовте назад!',
    unsupportedFileType:
      'Неподдржан тип на датотека. Ве молиме, прикачете JPG, PNG, GIF или MP4 датотека.',
    fileTooLarge: 'Оваа датотека е преголема. Максималната големина е 200 MB.',
    audioCloseError: 'Грешка при затворање на аудио:',
  },
  ms: {
    telletDoesNotExistTitle: 'Tellet tidak wujud',
    projectDoesNotExistTitle: 'Projek tidak wujud',
    projectDoesNotExistMessage: 'Maaf, projek ini tidak wujud.',
    maximumParticipantsTitle: 'Jumlah maksimum peserta',
    maximumParticipantsMessage:
      'Kajian ini telah mencapai jumlah maksimum respons dan tidak lagi menerima respons baharu.',
    somethingMissingTitle: 'Sesuatu hilang',
    somethingMissingMessage: 'Halaman ini tidak tersedia atau tidak wujud.',
    projectCompletedTitle: 'Projek telah selesai',
    projectCompletedMessage: 'Kajian ini telah selesai dan tidak lagi menerima respons baharu.',
    projectInDraftOrReadyStatusTitle: 'Projek belum diterbitkan',
    projectInDraftOrReadyStatusMessage:
      'Memandangkan kajian belum diterbitkan, ia tidak menerima respons.',
    redirectingIn: 'Mengalih semula dalam',
    seconds: 'saat...',
    cancel: 'Batal',
    returnToPanel: 'Kembali ke Panel',
    errorBoundaryTitle: 'Sesuatu telah berlaku',
    errorBoundaryMessage:
      'Ralat tidak dijangka berlaku. Sila muat semula halaman untuk meneruskan.',
    errorBoundaryReloadButton: 'Muat semula halaman',
    messageProcessingError:
      'Alamak! Nampaknya kami menghadapi masalah memproses mesej anda. Sila cuba hantar semula.',
    microphoneProblemTextChat:
      'Nampaknya ada masalah dengan mikrofon anda. Sila taip jawapan anda menggunakan butang papan kekunci.',
    microphoneProblemVoiceChat:
      'Nampaknya ada masalah dengan mikrofon anda. Sila semak kebenaran pelayar anda dan cuba lagi.',
    responseTimeoutError: 'Respons mengambil masa terlalu lama. Sila cuba lagi.',
    noResponseError: 'Tiada respons dikesan. Sila cuba lagi.',
    connectionInterrupted: 'Sambungan terganggu. Anda boleh menghantar semula mesej anda.',
    videoStartError: 'Gagal memulakan rakaman video. Sila cuba lagi.',
    videoCameraNotSupported: 'Kamera tidak disokong. Sila cuba dengan pelayar yang berbeza.',
    videoPauseError: 'Gagal menjeda rakaman video.',
    videoResumeError: 'Gagal menyambung semula rakaman video.',
    videoStopError: 'Gagal menghentikan rakaman video. Sila cuba lagi.',
    videoSwitchCameraError: 'Gagal menukar kamera. Sila cuba lagi.',
    videoSendError: 'Gagal menghantar mesej video.',
    welcomeBack: 'Selamat kembali!',
    unsupportedFileType: 'Jenis fail tidak disokong. Sila muat naik fail JPG, PNG, GIF, atau MP4.',
    fileTooLarge: 'Fail ini terlalu besar. Saiz maksimum ialah 200 MB.',
    audioCloseError: 'Ralat menutup audio:',
  },
  mi: {
    telletDoesNotExistTitle: 'Kāore a Tellet e noho ana',
    projectDoesNotExistTitle: 'Kāore te kaupapa e noho ana',
    projectDoesNotExistMessage: 'Aroha mai, kāore tēnei kaupapa e noho ana.',
    maximumParticipantsTitle: 'Te nui rawa o ngā kaiwhakauru',
    maximumParticipantsMessage:
      'Kua eke tēnei rangahau ki te nui rawa o ngā whakautu, ā, kāore e whakaae ki ngā whakautu hou.',
    somethingMissingTitle: 'He mea e ngaro ana',
    somethingMissingMessage: 'Kāore tēnei whārangi e wātea ana, kāore rānei e noho ana.',
    projectCompletedTitle: 'Kua oti te kaupapa',
    projectCompletedMessage: 'Kua oti tēnei rangahau, ā, kāore e whakaae ki ngā whakautu hou.',
    projectInDraftOrReadyStatusTitle: 'Kāore i te whakaputaina te kaupapa',
    projectInDraftOrReadyStatusMessage:
      'I te mea kāore anō kia whakaputaina te rangahau, kāore i te whakaae ki ngā whakautu.',
    redirectingIn: 'Ka whakaaro anō i roto i',
    seconds: 'hēkona...',
    cancel: 'Whakakore',
    returnToPanel: 'Hoki ki te Papa',
    errorBoundaryTitle: 'I hē tētahi mea',
    errorBoundaryMessage:
      'I puta he hapa ohorere. Tēnā koa, utaina anō te whārangi ki te haere tonu.',
    errorBoundaryReloadButton: 'Utaina anō te whārangi',
    messageProcessingError:
      'Auē! Te āhua nei kei te raru mātou ki te tukatuka i tō karere. Tēnā koa, tukuna anō.',
    microphoneProblemTextChat:
      'Te āhua nei he raru kei tō hopuoro. Tēnā koa, patohia ō whakautu mā te pātuhi.',
    microphoneProblemVoiceChat:
      'Te āhua nei he raru kei tō hopuoro. Tēnā koa, tirohia ngā whakaaetanga a tō tirotiro, ka ngana anō.',
    responseTimeoutError: 'Kei te roa rawa te whakautu. Tēnā koa, ngana anō.',
    noResponseError: 'Kāore he whakautu i kitea. Tēnā koa, ngana anō.',
    connectionInterrupted: 'Kua whakatārewahia te hononga. Ka taea e koe te tuku anō i tō karere.',
    videoStartError: 'I rahua te tīmata i te hopu ataata. Tēnā koa, ngana anō.',
    videoCameraNotSupported:
      'Kāore te kāmera e tautokohia ana. Tēnā koa, ngana ki tētahi tirotiro rerekē.',
    videoPauseError: 'I rahua te whakatārewa i te hopu ataata.',
    videoResumeError: 'I rahua te haere tonu i te hopu ataata.',
    videoStopError: 'I rahua te whakamutu i te hopu ataata. Tēnā koa, ngana anō.',
    videoSwitchCameraError: 'I rahua te whakawhiti kāmera. Tēnā koa, ngana anō.',
    videoSendError: 'I rahua te tuku karere ataata.',
    welcomeBack: 'Nau mai anō!',
    unsupportedFileType:
      'Momo kōnae kāore e tautokohia. Tēnā koa, tukuna he kōnae JPG, PNG, GIF, MP4 rānei.',
    fileTooLarge: 'He rahi rawa tēnei kōnae. Ko te rahi mōrahi he 200 MB.',
    audioCloseError: 'He hapa i te katinga o te hopuoro:',
  },
  mr: {
    telletDoesNotExistTitle: 'Tellet अस्तित्वात नाही',
    projectDoesNotExistTitle: 'प्रकल्प अस्तित्वात नाही',
    projectDoesNotExistMessage: 'आम्हाला माफ करा, हा प्रकल्प अस्तित्वात नाही.',
    maximumParticipantsTitle: 'सहभागींची कमाल संख्या',
    maximumParticipantsMessage:
      'या अभ्यासाने प्रतिसादांची कमाल संख्या गाठली आहे आणि आता नवीन प्रतिसाद स्वीकारत नाही.',
    somethingMissingTitle: 'काहीतरी गहाळ आहे',
    somethingMissingMessage: 'हे पृष्ठ उपलब्ध नाही किंवा अस्तित्वात नाही.',
    projectCompletedTitle: 'प्रकल्प पूर्ण झाला आहे',
    projectCompletedMessage: 'हा अभ्यास पूर्ण झाला आहे आणि आता नवीन प्रतिसाद स्वीकारत नाही.',
    projectInDraftOrReadyStatusTitle: 'प्रकल्प प्रकाशित केलेला नाही',
    projectInDraftOrReadyStatusMessage:
      'अभ्यास अद्याप प्रकाशित न झाल्यामुळे, तो प्रतिसाद स्वीकारत नाही.',
    redirectingIn: 'यामध्ये पुनर्निर्देशित करत आहे',
    seconds: 'सेकंद...',
    cancel: 'रद्द करा',
    returnToPanel: 'पॅनेलवर परत जा',
    errorBoundaryTitle: 'काहीतरी चूक झाली',
    errorBoundaryMessage: 'एक अनपेक्षित त्रुटी आली. पुढे जाण्यासाठी कृपया पृष्ठ पुन्हा लोड करा.',
    errorBoundaryReloadButton: 'पृष्ठ पुन्हा लोड करा',
    messageProcessingError:
      'अरेरे! आपला संदेश प्रक्रिया करण्यात आम्हाला अडचण येत आहे असे दिसते. कृपया तो पुन्हा पाठवण्याचा प्रयत्न करा.',
    microphoneProblemTextChat:
      'आपल्या मायक्रोफोनमध्ये समस्या असल्याचे दिसते. कृपया कीबोर्ड बटण वापरून आपली उत्तरे टाइप करा.',
    microphoneProblemVoiceChat:
      'आपल्या मायक्रोफोनमध्ये समस्या असल्याचे दिसते. कृपया आपल्या ब्राउझर परवानग्या तपासा आणि पुन्हा प्रयत्न करा.',
    responseTimeoutError: 'प्रतिसादाला खूप वेळ लागत आहे. कृपया पुन्हा प्रयत्न करा.',
    noResponseError: 'कोणताही प्रतिसाद आढळला नाही. कृपया पुन्हा प्रयत्न करा.',
    connectionInterrupted: 'कनेक्शन खंडित झाले. आपण आपला संदेश पुन्हा पाठवू शकता.',
    videoStartError: 'व्हिडिओ रेकॉर्डिंग सुरू करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoCameraNotSupported: 'कॅमेरा समर्थित नाही. कृपया वेगळ्या ब्राउझरसह प्रयत्न करा.',
    videoPauseError: 'व्हिडिओ रेकॉर्डिंग थांबवण्यात अयशस्वी.',
    videoResumeError: 'व्हिडिओ रेकॉर्डिंग पुन्हा सुरू करण्यात अयशस्वी.',
    videoStopError: 'व्हिडिओ रेकॉर्डिंग थांबवण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoSwitchCameraError: 'कॅमेरा बदलण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoSendError: 'व्हिडिओ संदेश पाठवण्यात अयशस्वी.',
    welcomeBack: 'पुन्हा स्वागत आहे!',
    unsupportedFileType: 'असमर्थित फाइल प्रकार. कृपया JPG, PNG, GIF किंवा MP4 फाइल अपलोड करा.',
    fileTooLarge: 'ही फाइल खूप मोठी आहे. कमाल आकार 200 MB आहे.',
    audioCloseError: 'ऑडिओ बंद करताना त्रुटी:',
  },
  ne: {
    telletDoesNotExistTitle: 'Tellet अवस्थित छैन',
    projectDoesNotExistTitle: 'परियोजना अवस्थित छैन',
    projectDoesNotExistMessage: 'माफ गर्नुहोस्, यो परियोजना अवस्थित छैन।',
    maximumParticipantsTitle: 'सहभागीहरूको अधिकतम संख्या',
    maximumParticipantsMessage:
      'यो अध्ययनले प्रतिक्रियाहरूको अधिकतम संख्या प्राप्त गरिसकेको छ र अब नयाँ प्रतिक्रियाहरू स्वीकार गर्दैन।',
    somethingMissingTitle: 'केही हराइरहेको छ',
    somethingMissingMessage: 'यो पृष्ठ उपलब्ध छैन वा अवस्थित छैन।',
    projectCompletedTitle: 'परियोजना पूरा भयो',
    projectCompletedMessage: 'यो अध्ययन पूरा भएको छ र अब नयाँ प्रतिक्रियाहरू स्वीकार गर्दैन।',
    projectInDraftOrReadyStatusTitle: 'परियोजना प्रकाशित गरिएको छैन',
    projectInDraftOrReadyStatusMessage:
      'अध्ययन अझै प्रकाशित नभएकोले, यसले प्रतिक्रियाहरू स्वीकार गर्दैन।',
    redirectingIn: 'यसमा पुनर्निर्देशित गर्दै',
    seconds: 'सेकेन्ड...',
    cancel: 'रद्द गर्नुहोस्',
    returnToPanel: 'प्यानलमा फर्कनुहोस्',
    errorBoundaryTitle: 'केही गल्ती भयो',
    errorBoundaryMessage: 'अनपेक्षित त्रुटि भयो। जारी राख्न कृपया पृष्ठ पुनः लोड गर्नुहोस्।',
    errorBoundaryReloadButton: 'पृष्ठ पुनः लोड गर्नुहोस्',
    messageProcessingError:
      'ओहो! तपाईंको सन्देश प्रक्रिया गर्न हामीलाई समस्या भइरहेको जस्तो देखिन्छ। कृपया यसलाई फेरि पठाउने प्रयास गर्नुहोस्।',
    microphoneProblemTextChat:
      'तपाईंको माइक्रोफोनमा समस्या भएको जस्तो देखिन्छ। कृपया किबोर्ड बटन प्रयोग गरेर आफ्ना जवाफहरू टाइप गर्नुहोस्।',
    microphoneProblemVoiceChat:
      'तपाईंको माइक्रोफोनमा समस्या भएको जस्तो देखिन्छ। कृपया आफ्नो ब्राउजर अनुमतिहरू जाँच गर्नुहोस् र फेरि प्रयास गर्नुहोस्।',
    responseTimeoutError: 'प्रतिक्रिया धेरै लामो समय लिइरहेको छ। कृपया फेरि प्रयास गर्नुहोस्।',
    noResponseError: 'कुनै प्रतिक्रिया पत्ता लागेन। कृपया फेरि प्रयास गर्नुहोस्।',
    connectionInterrupted: 'जडान अवरोधित भयो। तपाईं आफ्नो सन्देश पुन: पठाउन सक्नुहुन्छ।',
    videoStartError: 'भिडियो रेकर्डिङ सुरु गर्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoCameraNotSupported: 'क्यामेरा समर्थित छैन। कृपया फरक ब्राउजरसँग प्रयास गर्नुहोस्।',
    videoPauseError: 'भिडियो रेकर्डिङ रोक्न असफल।',
    videoResumeError: 'भिडियो रेकर्डिङ पुनः सुरु गर्न असफल।',
    videoStopError: 'भिडियो रेकर्डिङ रोक्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoSwitchCameraError: 'क्यामेरा स्विच गर्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoSendError: 'भिडियो सन्देश पठाउन असफल।',
    welcomeBack: 'फेरि स्वागत छ!',
    unsupportedFileType: 'असमर्थित फाइल प्रकार। कृपया JPG, PNG, GIF, वा MP4 फाइल अपलोड गर्नुहोस्।',
    fileTooLarge: 'यो फाइल धेरै ठूलो छ। अधिकतम आकार २०० MB हो।',
    audioCloseError: 'अडियो बन्द गर्दा त्रुटि:',
  },
  no: {
    telletDoesNotExistTitle: 'Tellet finnes ikke',
    projectDoesNotExistTitle: 'Prosjektet finnes ikke',
    projectDoesNotExistMessage: 'Beklager, men dette prosjektet finnes ikke.',
    maximumParticipantsTitle: 'Maksimalt antall deltakere',
    maximumParticipantsMessage:
      'Denne studien har nådd maksimalt antall svar og tar ikke lenger imot nye svar.',
    somethingMissingTitle: 'Noe mangler',
    somethingMissingMessage: 'Denne siden er utilgjengelig eller finnes ikke.',
    projectCompletedTitle: 'Prosjektet er fullført',
    projectCompletedMessage: 'Denne studien er fullført og tar ikke lenger imot nye svar.',
    projectInDraftOrReadyStatusTitle: 'Prosjektet er ikke publisert',
    projectInDraftOrReadyStatusMessage:
      'Siden studien ikke er publisert ennå, tar den ikke imot svar.',
    redirectingIn: 'Omdirigerer om',
    seconds: 'sekunder...',
    cancel: 'Avbryt',
    returnToPanel: 'Tilbake til panelet',
    errorBoundaryTitle: 'Noe gikk galt',
    errorBoundaryMessage: 'En uventet feil oppstod. Last inn siden på nytt for å fortsette.',
    errorBoundaryReloadButton: 'Last inn siden på nytt',
    messageProcessingError:
      'Oi! Det ser ut til at vi har problemer med å behandle meldingen din. Prøv å sende den på nytt.',
    microphoneProblemTextChat:
      'Det ser ut til at det er et problem med mikrofonen din. Skriv inn svarene dine ved å bruke tastaturknappen.',
    microphoneProblemVoiceChat:
      'Det ser ut til at det er et problem med mikrofonen din. Sjekk nettlesertillatelsene dine og prøv igjen.',
    responseTimeoutError: 'Svaret tar for lang tid. Vennligst prøv igjen.',
    noResponseError: 'Ingen respons oppdaget. Vennligst prøv igjen.',
    connectionInterrupted: 'Tilkoblingen ble avbrutt. Du kan sende meldingen din på nytt.',
    videoStartError: 'Kunne ikke starte videoopptak. Vennligst prøv igjen.',
    videoCameraNotSupported: 'Kameraet støttes ikke. Prøv med en annen nettleser.',
    videoPauseError: 'Kunne ikke pause videoopptak.',
    videoResumeError: 'Kunne ikke gjenoppta videoopptak.',
    videoStopError: 'Kunne ikke stoppe videoopptak. Vennligst prøv igjen.',
    videoSwitchCameraError: 'Kunne ikke bytte kamera. Vennligst prøv igjen.',
    videoSendError: 'Kunne ikke sende videomelding.',
    welcomeBack: 'Velkommen tilbake!',
    unsupportedFileType: 'Ikke-støttet filtype. Last opp en JPG, PNG, GIF eller MP4 fil.',
    fileTooLarge: 'Denne filen er for stor. Maksimal størrelse er 200 MB.',
    audioCloseError: 'Feil ved lukking av lyd:',
  },
  pa: {
    telletDoesNotExistTitle: 'Tellet ਮੌਜੂਦ ਨਹੀਂ ਹੈ',
    projectDoesNotExistTitle: 'ਪ੍ਰੋਜੈਕਟ ਮੌਜੂਦ ਨਹੀਂ ਹੈ',
    projectDoesNotExistMessage: 'ਸਾਨੂੰ ਅਫ਼ਸੋਸ ਹੈ ਪਰ ਇਹ ਪ੍ਰੋਜੈਕਟ ਮੌਜੂਦ ਨਹੀਂ ਹੈ।',
    maximumParticipantsTitle: 'ਭਾਗੀਦਾਰਾਂ ਦੀ ਵੱਧ ਤੋਂ ਵੱਧ ਸੰਖਿਆ',
    maximumParticipantsMessage:
      'ਇਸ ਅਧਿਐਨ ਨੇ ਜਵਾਬਾਂ ਦੀ ਵੱਧ ਤੋਂ ਵੱਧ ਸੰਖਿਆ ਪ੍ਰਾਪਤ ਕਰ ਲਈ ਹੈ ਅਤੇ ਹੁਣ ਨਵੇਂ ਜਵਾਬ ਸਵੀਕਾਰ ਨਹੀਂ ਕਰ ਰਿਹਾ।',
    somethingMissingTitle: 'ਕੁਝ ਗੁੰਮ ਹੈ',
    somethingMissingMessage: 'ਇਹ ਪੰਨਾ ਉਪਲਬਧ ਨਹੀਂ ਹੈ ਜਾਂ ਮੌਜੂਦ ਨਹੀਂ ਹੈ।',
    projectCompletedTitle: 'ਪ੍ਰੋਜੈਕਟ ਪੂਰਾ ਹੋ ਗਿਆ ਹੈ',
    projectCompletedMessage: 'ਇਹ ਅਧਿਐਨ ਪੂਰਾ ਹੋ ਗਿਆ ਹੈ ਅਤੇ ਹੁਣ ਨਵੇਂ ਜਵਾਬ ਸਵੀਕਾਰ ਨਹੀਂ ਕਰ ਰਿਹਾ।',
    projectInDraftOrReadyStatusTitle: 'ਪ੍ਰੋਜੈਕਟ ਪ੍ਰਕਾਸ਼ਿਤ ਨਹੀਂ ਹੈ',
    projectInDraftOrReadyStatusMessage:
      'ਕਿਉਂਕਿ ਅਧਿਐਨ ਅਜੇ ਪ੍ਰਕਾਸ਼ਿਤ ਨਹੀਂ ਹੋਇਆ ਹੈ, ਇਹ ਜਵਾਬ ਸਵੀਕਾਰ ਨਹੀਂ ਕਰ ਰਿਹਾ।',
    redirectingIn: 'ਇਸ ਵਿੱਚ ਰੀਡਾਇਰੈਕਟ ਹੋ ਰਿਹਾ ਹੈ',
    seconds: 'ਸਕਿੰਟ...',
    cancel: 'ਰੱਦ ਕਰੋ',
    returnToPanel: 'ਪੈਨਲ ਤੇ ਵਾਪਸ ਜਾਓ',
    errorBoundaryTitle: 'ਕੁਝ ਗਲਤ ਹੋ ਗਿਆ',
    errorBoundaryMessage: 'ਇੱਕ ਅਣਕਿਆਸੀ ਗਲਤੀ ਹੋਈ। ਜਾਰੀ ਰੱਖਣ ਲਈ ਕਿਰਪਾ ਕਰਕੇ ਪੰਨਾ ਮੁੜ ਲੋਡ ਕਰੋ।',
    errorBoundaryReloadButton: 'ਪੰਨਾ ਮੁੜ ਲੋਡ ਕਰੋ',
    messageProcessingError:
      'ਓਹੋ! ਲੱਗਦਾ ਹੈ ਕਿ ਸਾਨੂੰ ਤੁਹਾਡੇ ਸੁਨੇਹੇ ਤੇ ਕਾਰਵਾਈ ਕਰਨ ਵਿੱਚ ਮੁਸ਼ਕਲ ਆ ਰਹੀ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਇਸਨੂੰ ਦੁਬਾਰਾ ਭੇਜਣ ਦੀ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    microphoneProblemTextChat:
      'ਲੱਗਦਾ ਹੈ ਕਿ ਤੁਹਾਡੇ ਮਾਈਕ੍ਰੋਫੋਨ ਨਾਲ ਕੋਈ ਸਮੱਸਿਆ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਕੀਬੋਰਡ ਬਟਨ ਦੀ ਵਰਤੋਂ ਕਰਕੇ ਆਪਣੇ ਜਵਾਬ ਟਾਈਪ ਕਰੋ।',
    microphoneProblemVoiceChat:
      'ਲੱਗਦਾ ਹੈ ਕਿ ਤੁਹਾਡੇ ਮਾਈਕ੍ਰੋਫੋਨ ਨਾਲ ਕੋਈ ਸਮੱਸਿਆ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਆਪਣੀਆਂ ਬ੍ਰਾਊਜ਼ਰ ਇਜਾਜ਼ਤਾਂ ਦੀ ਜਾਂਚ ਕਰੋ ਅਤੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    responseTimeoutError: 'ਜਵਾਬ ਬਹੁਤ ਸਮਾਂ ਲੈ ਰਿਹਾ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    noResponseError: 'ਕੋਈ ਜਵਾਬ ਨਹੀਂ ਮਿਲਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    connectionInterrupted: 'ਕਨੈਕਸ਼ਨ ਵਿੱਚ ਵਿਘਨ ਪਿਆ। ਤੁਸੀਂ ਆਪਣਾ ਸੁਨੇਹਾ ਦੁਬਾਰਾ ਭੇਜ ਸਕਦੇ ਹੋ।',
    videoStartError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਸ਼ੁਰੂ ਕਰਨ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoCameraNotSupported: 'ਕੈਮਰਾ ਸਮਰਥਿਤ ਨਹੀਂ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਕਿਸੇ ਹੋਰ ਬ੍ਰਾਊਜ਼ਰ ਨਾਲ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoPauseError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਰੋਕਣ ਵਿੱਚ ਅਸਫਲ।',
    videoResumeError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਮੁੜ ਸ਼ੁਰੂ ਕਰਨ ਵਿੱਚ ਅਸਫਲ।',
    videoStopError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਬੰਦ ਕਰਨ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoSwitchCameraError: 'ਕੈਮਰਾ ਬਦਲਣ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoSendError: 'ਵੀਡੀਓ ਸੁਨੇਹਾ ਭੇਜਣ ਵਿੱਚ ਅਸਫਲ।',
    welcomeBack: 'ਵਾਪਸੀ ਤੇ ਸੁਆਗਤ ਹੈ!',
    unsupportedFileType: 'ਅਸਮਰਥਿਤ ਫਾਈਲ ਕਿਸਮ। ਕਿਰਪਾ ਕਰਕੇ JPG, PNG, GIF, ਜਾਂ MP4 ਫਾਈਲ ਅਪਲੋਡ ਕਰੋ।',
    fileTooLarge: 'ਇਹ ਫ਼ਾਈਲ ਬਹੁਤ ਵੱਡੀ ਹੈ। ਵੱਧ ਤੋਂ ਵੱਧ ਆਕਾਰ 200 MB ਹੈ।',
    audioCloseError: 'ਆਡੀਓ ਬੰਦ ਕਰਨ ਵਿੱਚ ਗਲਤੀ:',
  },
  fa: {
    telletDoesNotExistTitle: 'Tellet وجود ندارد',
    projectDoesNotExistTitle: 'پروژه وجود ندارد',
    projectDoesNotExistMessage: 'متأسفیم، این پروژه وجود ندارد.',
    maximumParticipantsTitle: 'حداکثر تعداد شرکت‌کنندگان',
    maximumParticipantsMessage:
      'این مطالعه به حداکثر تعداد پاسخ‌ها رسیده است و دیگر پاسخ‌های جدید را نمی‌پذیرد.',
    somethingMissingTitle: 'چیزی گم شده است',
    somethingMissingMessage: 'این صفحه در دسترس نیست یا وجود ندارد.',
    projectCompletedTitle: 'پروژه تکمیل شده است',
    projectCompletedMessage: 'این مطالعه تکمیل شده است و دیگر پاسخ‌های جدید را نمی‌پذیرد.',
    projectInDraftOrReadyStatusTitle: 'پروژه منتشر نشده است',
    projectInDraftOrReadyStatusMessage:
      'از آنجا که مطالعه هنوز منتشر نشده است، پاسخ‌ها را نمی‌پذیرد.',
    redirectingIn: 'هدایت مجدد در',
    seconds: 'ثانیه...',
    cancel: 'لغو',
    returnToPanel: 'بازگشت به پنل',
    errorBoundaryTitle: 'مشکلی پیش آمد',
    errorBoundaryMessage:
      'یک خطای غیرمنتظره رخ داد. لطفاً برای ادامه صفحه را دوباره بارگذاری کنید.',
    errorBoundaryReloadButton: 'بارگذاری مجدد صفحه',
    messageProcessingError:
      'اوه! به نظر می‌رسد در پردازش پیام شما مشکل داریم. لطفاً دوباره ارسال کنید.',
    microphoneProblemTextChat:
      'به نظر می‌رسد مشکلی با میکروفون شما وجود دارد. لطفاً پاسخ‌های خود را با استفاده از دکمه صفحه‌کلید تایپ کنید.',
    microphoneProblemVoiceChat:
      'به نظر می‌رسد مشکلی با میکروفون شما وجود دارد. لطفاً مجوزهای مرورگر خود را بررسی کرده و دوباره امتحان کنید.',
    responseTimeoutError: 'پاسخ بیش از حد طول می‌کشد. لطفاً دوباره امتحان کنید.',
    noResponseError: 'هیچ پاسخی شناسایی نشد. لطفاً دوباره امتحان کنید.',
    connectionInterrupted: 'اتصال قطع شد. می‌توانید پیام خود را دوباره ارسال کنید.',
    videoStartError: 'شروع ضبط ویدیو ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoCameraNotSupported: 'دوربین پشتیبانی نمی‌شود. لطفاً با مرورگر دیگری امتحان کنید.',
    videoPauseError: 'توقف ضبط ویدیو ناموفق بود.',
    videoResumeError: 'ادامه ضبط ویدیو ناموفق بود.',
    videoStopError: 'متوقف کردن ضبط ویدیو ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoSwitchCameraError: 'تعویض دوربین ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoSendError: 'ارسال پیام ویدیویی ناموفق بود.',
    welcomeBack: 'خوش آمدید!',
    unsupportedFileType:
      'نوع فایل پشتیبانی نمی‌شود. لطفاً یک فایل JPG، PNG، GIF یا MP4 بارگذاری کنید.',
    fileTooLarge: 'این فایل بسیار بزرگ است. حداکثر اندازه ۲۰۰ مگابایت است.',
    audioCloseError: 'خطا در بستن صدا:',
  },
  pl: {
    telletDoesNotExistTitle: 'Tellet nie istnieje',
    projectDoesNotExistTitle: 'Projekt nie istnieje',
    projectDoesNotExistMessage: 'Przepraszamy, ale ten projekt nie istnieje.',
    maximumParticipantsTitle: 'Maksymalna liczba uczestników',
    maximumParticipantsMessage:
      'To badanie osiągnęło maksymalną liczbę odpowiedzi i nie przyjmuje już nowych odpowiedzi.',
    somethingMissingTitle: 'Czegoś brakuje',
    somethingMissingMessage: 'Ta strona jest niedostępna lub nie istnieje.',
    projectCompletedTitle: 'Projekt został ukończony',
    projectCompletedMessage: 'To badanie zostało ukończone i nie przyjmuje już nowych odpowiedzi.',
    projectInDraftOrReadyStatusTitle: 'Projekt nie został opublikowany',
    projectInDraftOrReadyStatusMessage:
      'Ponieważ badanie nie zostało jeszcze opublikowane, nie przyjmuje odpowiedzi.',
    redirectingIn: 'Przekierowanie za',
    seconds: 'sekund...',
    cancel: 'Anuluj',
    returnToPanel: 'Powrót do panelu',
    errorBoundaryTitle: 'Coś poszło nie tak',
    errorBoundaryMessage: 'Wystąpił nieoczekiwany błąd. Aby kontynuować, odśwież stronę.',
    errorBoundaryReloadButton: 'Odśwież stronę',
    messageProcessingError:
      'Ups! Wygląda na to, że mamy problem z przetworzeniem Twojej wiadomości. Spróbuj wysłać ją ponownie.',
    microphoneProblemTextChat:
      'Wygląda na to, że jest problem z Twoim mikrofonem. Wpisz odpowiedzi za pomocą przycisku klawiatury.',
    microphoneProblemVoiceChat:
      'Wygląda na to, że jest problem z Twoim mikrofonem. Sprawdź uprawnienia przeglądarki i spróbuj ponownie.',
    responseTimeoutError: 'Odpowiedź trwa zbyt długo. Spróbuj ponownie.',
    noResponseError: 'Nie wykryto odpowiedzi. Spróbuj ponownie.',
    connectionInterrupted: 'Połączenie zostało przerwane. Możesz ponownie wysłać swoją wiadomość.',
    videoStartError: 'Nie udało się rozpocząć nagrywania wideo. Spróbuj ponownie.',
    videoCameraNotSupported: 'Kamera nie jest obsługiwana. Spróbuj z inną przeglądarką.',
    videoPauseError: 'Nie udało się wstrzymać nagrywania wideo.',
    videoResumeError: 'Nie udało się wznowić nagrywania wideo.',
    videoStopError: 'Nie udało się zatrzymać nagrywania wideo. Spróbuj ponownie.',
    videoSwitchCameraError: 'Nie udało się przełączyć kamery. Spróbuj ponownie.',
    videoSendError: 'Nie udało się wysłać wiadomości wideo.',
    welcomeBack: 'Witaj ponownie!',
    unsupportedFileType: 'Nieobsługiwany typ pliku. Prześlij plik JPG, PNG, GIF lub MP4.',
    fileTooLarge: 'Ten plik jest za duży. Maksymalny rozmiar to 200 MB.',
    audioCloseError: 'Błąd podczas zamykania dźwięku:',
  },
  pt: {
    telletDoesNotExistTitle: 'Tellet não existe',
    projectDoesNotExistTitle: 'O projeto não existe',
    projectDoesNotExistMessage: 'Lamentamos, mas este projeto não existe.',
    maximumParticipantsTitle: 'Número máximo de participantes',
    maximumParticipantsMessage:
      'Este estudo atingiu o número máximo de respostas e já não aceita novas respostas.',
    somethingMissingTitle: 'Algo está em falta',
    somethingMissingMessage: 'Esta página não está disponível ou não existe.',
    projectCompletedTitle: 'O projeto foi concluído',
    projectCompletedMessage: 'Este estudo foi concluído e já não aceita novas respostas.',
    projectInDraftOrReadyStatusTitle: 'O projeto não foi publicado',
    projectInDraftOrReadyStatusMessage:
      'Como o estudo ainda não foi publicado, não está a aceitar respostas.',
    redirectingIn: 'A redirecionar em',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Voltar ao Painel',
    errorBoundaryTitle: 'Algo correu mal',
    errorBoundaryMessage:
      'Ocorreu um erro inesperado. Por favor, recarregue a página para continuar.',
    errorBoundaryReloadButton: 'Recarregar página',
    messageProcessingError:
      'Ups! Parece que estamos a ter problemas em processar a sua mensagem. Por favor, tente reenviá-la.',
    microphoneProblemTextChat:
      'Parece haver um problema com o seu microfone. Por favor, escreva as suas respostas usando o botão do teclado.',
    microphoneProblemVoiceChat:
      'Parece haver um problema com o seu microfone. Por favor, verifique as permissões do seu navegador e tente novamente.',
    responseTimeoutError: 'A resposta está a demorar demasiado. Por favor, tente novamente.',
    noResponseError: 'Nenhuma resposta detetada. Por favor, tente novamente.',
    connectionInterrupted: 'Ligação interrompida. Pode reenviar a sua mensagem.',
    videoStartError: 'Falha ao iniciar a gravação de vídeo. Por favor, tente novamente.',
    videoCameraNotSupported:
      'A câmara não é suportada. Por favor, tente com um navegador diferente.',
    videoPauseError: 'Falha ao pausar a gravação de vídeo.',
    videoResumeError: 'Falha ao retomar a gravação de vídeo.',
    videoStopError: 'Falha ao parar a gravação de vídeo. Por favor, tente novamente.',
    videoSwitchCameraError: 'Falha ao mudar de câmara. Por favor, tente novamente.',
    videoSendError: 'Falha ao enviar a mensagem de vídeo.',
    welcomeBack: 'Bem-vindo de volta!',
    unsupportedFileType:
      'Tipo de ficheiro não suportado. Por favor, carregue um ficheiro JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Este ficheiro é demasiado grande. O tamanho máximo é de 200 MB.',
    audioCloseError: 'Erro ao fechar o áudio:',
  },
  tl: {
    telletDoesNotExistTitle: 'Hindi umiiral ang Tellet',
    projectDoesNotExistTitle: 'Hindi umiiral ang proyekto',
    projectDoesNotExistMessage: 'Pasensya na, ngunit hindi umiiral ang proyektong ito.',
    maximumParticipantsTitle: 'Maximum na bilang ng mga kalahok',
    maximumParticipantsMessage:
      'Naabot na ng pag-aaral na ito ang maximum na bilang ng mga tugon at hindi na tumatanggap ng mga bagong tugon.',
    somethingMissingTitle: 'May kulang',
    somethingMissingMessage: 'Hindi magamit o hindi umiiral ang pahinang ito.',
    projectCompletedTitle: 'Natapos na ang proyekto',
    projectCompletedMessage:
      'Natapos na ang pag-aaral na ito at hindi na tumatanggap ng mga bagong tugon.',
    projectInDraftOrReadyStatusTitle: 'Hindi pa nai-publish ang proyekto',
    projectInDraftOrReadyStatusMessage:
      'Dahil hindi pa nai-publish ang pag-aaral, hindi ito tumatanggap ng mga tugon.',
    redirectingIn: 'Ida-redirect sa loob ng',
    seconds: 'segundo...',
    cancel: 'Kanselahin',
    returnToPanel: 'Bumalik sa Panel',
    errorBoundaryTitle: 'May nangyaring mali',
    errorBoundaryMessage:
      'May naganap na hindi inaasahang error. Mangyaring i-reload ang pahina upang magpatuloy.',
    errorBoundaryReloadButton: 'I-reload ang pahina',
    messageProcessingError:
      'Naku! Mukhang may problema kami sa pagproseso ng iyong mensahe. Mangyaring subukang ipadala muli.',
    microphoneProblemTextChat:
      'Mukhang may problema sa iyong mikropono. Mangyaring i-type ang iyong mga sagot gamit ang pindutan ng keyboard.',
    microphoneProblemVoiceChat:
      'Mukhang may problema sa iyong mikropono. Mangyaring suriin ang mga pahintulot ng iyong browser at subukang muli.',
    responseTimeoutError: 'Masyadong mahaba ang pagtugon. Mangyaring subukang muli.',
    noResponseError: 'Walang natukoy na tugon. Mangyaring subukang muli.',
    connectionInterrupted: 'Naantala ang koneksyon. Maaari mong ipadala muli ang iyong mensahe.',
    videoStartError: 'Nabigong simulan ang pag-record ng video. Mangyaring subukang muli.',
    videoCameraNotSupported: 'Hindi suportado ang kamera. Mangyaring subukan sa ibang browser.',
    videoPauseError: 'Nabigong i-pause ang pag-record ng video.',
    videoResumeError: 'Nabigong ipagpatuloy ang pag-record ng video.',
    videoStopError: 'Nabigong ihinto ang pag-record ng video. Mangyaring subukang muli.',
    videoSwitchCameraError: 'Nabigong magpalit ng kamera. Mangyaring subukang muli.',
    videoSendError: 'Nabigong ipadala ang video message.',
    welcomeBack: 'Maligayang pagbabalik!',
    unsupportedFileType:
      'Hindi suportadong uri ng file. Mangyaring mag-upload ng JPG, PNG, GIF, o MP4 file.',
    fileTooLarge: 'Masyadong malaki ang file na ito. Ang maximum na laki ay 200 MB.',
    audioCloseError: 'Error sa pagsasara ng audio:',
  },
  ro: {
    telletDoesNotExistTitle: 'Tellet nu există',
    projectDoesNotExistTitle: 'Proiectul nu există',
    projectDoesNotExistMessage: 'Ne pare rău, dar acest proiect nu există.',
    maximumParticipantsTitle: 'Numărul maxim de participanți',
    maximumParticipantsMessage:
      'Acest studiu a atins numărul maxim de răspunsuri și nu mai acceptă răspunsuri noi.',
    somethingMissingTitle: 'Ceva lipsește',
    somethingMissingMessage: 'Această pagină nu este disponibilă sau nu există.',
    projectCompletedTitle: 'Proiectul este finalizat',
    projectCompletedMessage: 'Acest studiu a fost finalizat și nu mai acceptă răspunsuri noi.',
    projectInDraftOrReadyStatusTitle: 'Proiectul nu este publicat',
    projectInDraftOrReadyStatusMessage:
      'Deoarece studiul nu a fost încă publicat, nu acceptă răspunsuri.',
    redirectingIn: 'Redirecționare în',
    seconds: 'secunde...',
    cancel: 'Anulează',
    returnToPanel: 'Înapoi la Panou',
    errorBoundaryTitle: 'Ceva nu a mers bine',
    errorBoundaryMessage:
      'A apărut o eroare neașteptată. Vă rugăm să reîncărcați pagina pentru a continua.',
    errorBoundaryReloadButton: 'Reîncarcă pagina',
    messageProcessingError:
      'Ups! Se pare că avem probleme în procesarea mesajului tău. Te rugăm să încerci să-l retrimiți.',
    microphoneProblemTextChat:
      'Se pare că există o problemă cu microfonul tău. Te rugăm să scrii răspunsurile folosind butonul tastaturii.',
    microphoneProblemVoiceChat:
      'Se pare că există o problemă cu microfonul tău. Te rugăm să verifici permisiunile browserului și să încerci din nou.',
    responseTimeoutError: 'Răspunsul durează prea mult. Te rugăm să încerci din nou.',
    noResponseError: 'Niciun răspuns detectat. Te rugăm să încerci din nou.',
    connectionInterrupted: 'Conexiunea a fost întreruptă. Poți retrimite mesajul tău.',
    videoStartError: 'Nu s-a putut porni înregistrarea video. Te rugăm să încerci din nou.',
    videoCameraNotSupported: 'Camera nu este acceptată. Te rugăm să încerci cu un alt browser.',
    videoPauseError: 'Nu s-a putut întrerupe înregistrarea video.',
    videoResumeError: 'Nu s-a putut relua înregistrarea video.',
    videoStopError: 'Nu s-a putut opri înregistrarea video. Te rugăm să încerci din nou.',
    videoSwitchCameraError: 'Nu s-a putut schimba camera. Te rugăm să încerci din nou.',
    videoSendError: 'Nu s-a putut trimite mesajul video.',
    welcomeBack: 'Bine ai revenit!',
    unsupportedFileType:
      'Tip de fișier neacceptat. Te rugăm să încarci un fișier JPG, PNG, GIF sau MP4.',
    fileTooLarge: 'Acest fișier este prea mare. Dimensiunea maximă este de 200 MB.',
    audioCloseError: 'Eroare la închiderea audio:',
  },
  ru: {
    telletDoesNotExistTitle: 'Tellet не существует',
    projectDoesNotExistTitle: 'Проект не существует',
    projectDoesNotExistMessage: 'К сожалению, этот проект не существует.',
    maximumParticipantsTitle: 'Достигнуто максимальное количество участников',
    maximumParticipantsMessage:
      'Это исследование достигло максимального количества ответов и больше не принимает новые ответы.',
    somethingMissingTitle: 'Что-то не найдено',
    somethingMissingMessage: 'Эта страница недоступна или не существует.',
    projectCompletedTitle: 'Проект завершён',
    projectCompletedMessage: 'Это исследование завершено и больше не принимает новые ответы.',
    projectInDraftOrReadyStatusTitle: 'Проект не опубликован',
    projectInDraftOrReadyStatusMessage:
      'Поскольку исследование ещё не опубликовано, оно не принимает ответы.',
    redirectingIn: 'Перенаправление через',
    seconds: 'секунд...',
    cancel: 'Отмена',
    returnToPanel: 'Вернуться на панель',
    errorBoundaryTitle: 'Что-то пошло не так',
    errorBoundaryMessage:
      'Произошла непредвиденная ошибка. Пожалуйста, перезагрузите страницу, чтобы продолжить.',
    errorBoundaryReloadButton: 'Перезагрузить страницу',
    messageProcessingError:
      'Упс! Похоже, возникли проблемы с обработкой вашего сообщения. Пожалуйста, попробуйте отправить его снова.',
    microphoneProblemTextChat:
      'Похоже, возникла проблема с вашим микрофоном. Пожалуйста, введите свои ответы с помощью клавиатуры.',
    microphoneProblemVoiceChat:
      'Похоже, возникла проблема с вашим микрофоном. Пожалуйста, проверьте разрешения браузера и попробуйте снова.',
    responseTimeoutError: 'Ответ занимает слишком много времени. Пожалуйста, попробуйте снова.',
    noResponseError: 'Ответ не обнаружен. Пожалуйста, попробуйте снова.',
    connectionInterrupted: 'Соединение прервано. Вы можете повторно отправить своё сообщение.',
    videoStartError: 'Не удалось начать запись видео. Пожалуйста, попробуйте снова.',
    videoCameraNotSupported: 'Камера не поддерживается. Пожалуйста, попробуйте в другом браузере.',
    videoPauseError: 'Не удалось приостановить запись видео.',
    videoResumeError: 'Не удалось возобновить запись видео.',
    videoStopError: 'Не удалось остановить запись видео. Пожалуйста, попробуйте снова.',
    videoSwitchCameraError: 'Не удалось переключить камеру. Пожалуйста, попробуйте снова.',
    videoSendError: 'Не удалось отправить видеосообщение.',
    welcomeBack: 'С возвращением!',
    unsupportedFileType:
      'Неподдерживаемый тип файла. Пожалуйста, загрузите файл JPG, PNG, GIF или MP4.',
    fileTooLarge: 'Этот файл слишком большой. Максимальный размер — 200 МБ.',
    audioCloseError: 'Ошибка закрытия аудио:',
  },
  sr: {
    telletDoesNotExistTitle: 'Tellet ne postoji',
    projectDoesNotExistTitle: 'Projekat ne postoji',
    projectDoesNotExistMessage: 'Žao nam je, ali ovaj projekat ne postoji.',
    maximumParticipantsTitle: 'Maksimalan broj učesnika',
    maximumParticipantsMessage:
      'Ova studija je dostigla maksimalan broj odgovora i više ne prihvata nove odgovore.',
    somethingMissingTitle: 'Nešto nedostaje',
    somethingMissingMessage: 'Ova stranica nije dostupna ili ne postoji.',
    projectCompletedTitle: 'Projekat je završen',
    projectCompletedMessage: 'Ova studija je završena i više ne prihvata nove odgovore.',
    projectInDraftOrReadyStatusTitle: 'Projekat nije objavljen',
    projectInDraftOrReadyStatusMessage: 'Pošto studija još nije objavljena, ne prihvata odgovore.',
    redirectingIn: 'Preusmeravanje za',
    seconds: 'sekundi...',
    cancel: 'Otkaži',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane greške. Molimo vas da ponovo učitate stranicu da biste nastavili.',
    errorBoundaryReloadButton: 'Ponovo učitaj stranicu',
    messageProcessingError:
      'Ups! Izgleda da imamo problem sa obradom vaše poruke. Molimo vas da je ponovo pošaljete.',
    microphoneProblemTextChat:
      'Izgleda da postoji problem sa vašim mikrofonom. Molimo vas da otkucate odgovore koristeći tastaturu.',
    microphoneProblemVoiceChat:
      'Izgleda da postoji problem sa vašim mikrofonom. Molimo vas da proverite dozvole pregledača i pokušate ponovo.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo vas da pokušate ponovo.',
    noResponseError: 'Odgovor nije otkriven. Molimo vas da pokušate ponovo.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovo poslati svoju poruku.',
    videoStartError: 'Nije uspelo pokretanje video snimanja. Molimo vas da pokušate ponovo.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo vas da pokušate sa drugim pregledačem.',
    videoPauseError: 'Nije uspelo pauziranje video snimanja.',
    videoResumeError: 'Nije uspelo nastavljanje video snimanja.',
    videoStopError: 'Nije uspelo zaustavljanje video snimanja. Molimo vas da pokušate ponovo.',
    videoSwitchCameraError: 'Nije uspela promena kamere. Molimo vas da pokušate ponovo.',
    videoSendError: 'Nije uspelo slanje video poruke.',
    welcomeBack: 'Dobrodošli nazad!',
    unsupportedFileType: 'Nepodržan tip fajla. Molimo vas da otpremite JPG, PNG, GIF ili MP4 fajl.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Greška pri zatvaranju audio zapisa:',
  },
  sk: {
    telletDoesNotExistTitle: 'Tellet neexistuje',
    projectDoesNotExistTitle: 'Projekt neexistuje',
    projectDoesNotExistMessage: 'Je nám ľúto, ale tento projekt neexistuje.',
    maximumParticipantsTitle: 'Maximálny počet účastníkov',
    maximumParticipantsMessage:
      'Táto štúdia dosiahla maximálny počet odpovedí a už neprijíma nové odpovede.',
    somethingMissingTitle: 'Niečo chýba',
    somethingMissingMessage: 'Táto stránka nie je dostupná alebo neexistuje.',
    projectCompletedTitle: 'Projekt je dokončený',
    projectCompletedMessage: 'Táto štúdia bola dokončená a už neprijíma nové odpovede.',
    projectInDraftOrReadyStatusTitle: 'Projekt nie je zverejnený',
    projectInDraftOrReadyStatusMessage: 'Keďže štúdia ešte nebola zverejnená, neprijíma odpovede.',
    redirectingIn: 'Presmerovanie o',
    seconds: 'sekúnd...',
    cancel: 'Zrušiť',
    returnToPanel: 'Späť na panel',
    errorBoundaryTitle: 'Niečo sa pokazilo',
    errorBoundaryMessage:
      'Vyskytla sa neočakávaná chyba. Pre pokračovanie prosím znovu načítajte stránku.',
    errorBoundaryReloadButton: 'Znovu načítať stránku',
    messageProcessingError:
      'Ups! Vyzerá to tak, že máme problém so spracovaním vašej správy. Skúste ju prosím poslať znova.',
    microphoneProblemTextChat:
      'Zdá sa, že je problém s vaším mikrofónom. Zadajte svoje odpovede pomocou klávesnice.',
    microphoneProblemVoiceChat:
      'Zdá sa, že je problém s vaším mikrofónom. Skontrolujte prosím povolenia prehliadača a skúste to znova.',
    responseTimeoutError: 'Odpoveď trvá príliš dlho. Skúste to prosím znova.',
    noResponseError: 'Nezistila sa žiadna odpoveď. Skúste to prosím znova.',
    connectionInterrupted: 'Spojenie bolo prerušené. Svoju správu môžete znova odoslať.',
    videoStartError: 'Nepodarilo sa spustiť nahrávanie videa. Skúste to prosím znova.',
    videoCameraNotSupported: 'Kamera nie je podporovaná. Skúste to prosím v inom prehliadači.',
    videoPauseError: 'Nepodarilo sa pozastaviť nahrávanie videa.',
    videoResumeError: 'Nepodarilo sa obnoviť nahrávanie videa.',
    videoStopError: 'Nepodarilo sa zastaviť nahrávanie videa. Skúste to prosím znova.',
    videoSwitchCameraError: 'Nepodarilo sa prepnúť kameru. Skúste to prosím znova.',
    videoSendError: 'Nepodarilo sa odoslať video správu.',
    welcomeBack: 'Vitajte späť!',
    unsupportedFileType: 'Nepodporovaný typ súboru. Nahrajte prosím súbor JPG, PNG, GIF alebo MP4.',
    fileTooLarge: 'Tento súbor je príliš veľký. Maximálna veľkosť je 200 MB.',
    audioCloseError: 'Chyba pri zatváraní zvuku:',
  },
  sl: {
    telletDoesNotExistTitle: 'Tellet ne obstaja',
    projectDoesNotExistTitle: 'Projekt ne obstaja',
    projectDoesNotExistMessage: 'Žal nam je, a ta projekt ne obstaja.',
    maximumParticipantsTitle: 'Največje število udeležencev',
    maximumParticipantsMessage:
      'Ta študija je dosegla največje število odgovorov in ne sprejema več novih odgovorov.',
    somethingMissingTitle: 'Nekaj manjka',
    somethingMissingMessage: 'Ta stran ni na voljo ali ne obstaja.',
    projectCompletedTitle: 'Projekt je zaključen',
    projectCompletedMessage: 'Ta študija je zaključena in ne sprejema več novih odgovorov.',
    projectInDraftOrReadyStatusTitle: 'Projekt ni objavljen',
    projectInDraftOrReadyStatusMessage: 'Ker študija še ni objavljena, ne sprejema odgovorov.',
    redirectingIn: 'Preusmeritev čez',
    seconds: 'sekund...',
    cancel: 'Prekliči',
    returnToPanel: 'Nazaj na panel',
    errorBoundaryTitle: 'Nekaj je šlo narobe',
    errorBoundaryMessage:
      'Prišlo je do nepričakovane napake. Za nadaljevanje znova naložite stran.',
    errorBoundaryReloadButton: 'Znova naloži stran',
    messageProcessingError:
      'Ups! Videti je, da imamo težave z obdelavo vašega sporočila. Poskusite ga znova poslati.',
    microphoneProblemTextChat:
      'Videti je, da je težava z vašim mikrofonom. Prosimo, vtipkajte svoje odgovore s tipkovnico.',
    microphoneProblemVoiceChat:
      'Videti je, da je težava z vašim mikrofonom. Preverite dovoljenja brskalnika in poskusite znova.',
    responseTimeoutError: 'Odziv traja predolgo. Poskusite znova.',
    noResponseError: 'Odziv ni bil zaznan. Poskusite znova.',
    connectionInterrupted: 'Povezava je bila prekinjena. Svoje sporočilo lahko znova pošljete.',
    videoStartError: 'Snemanja videa ni bilo mogoče začeti. Poskusite znova.',
    videoCameraNotSupported: 'Kamera ni podprta. Poskusite z drugim brskalnikom.',
    videoPauseError: 'Snemanja videa ni bilo mogoče začasno ustaviti.',
    videoResumeError: 'Snemanja videa ni bilo mogoče nadaljevati.',
    videoStopError: 'Snemanja videa ni bilo mogoče zaustaviti. Poskusite znova.',
    videoSwitchCameraError: 'Kamere ni bilo mogoče preklopiti. Poskusite znova.',
    videoSendError: 'Video sporočila ni bilo mogoče poslati.',
    welcomeBack: 'Dobrodošli nazaj!',
    unsupportedFileType: 'Nepodprta vrsta datoteke. Naložite datoteko JPG, PNG, GIF ali MP4.',
    fileTooLarge: 'Ta datoteka je prevelika. Največja velikost je 200 MB.',
    audioCloseError: 'Napaka pri zapiranju zvoka:',
  },
  es: {
    telletDoesNotExistTitle: 'Tellet no existe',
    projectDoesNotExistTitle: 'El proyecto no existe',
    projectDoesNotExistMessage: 'Lo sentimos, pero este proyecto no existe.',
    maximumParticipantsTitle: 'Número máximo de participantes',
    maximumParticipantsMessage:
      'Este estudio ha alcanzado el número máximo de respuestas y ya no acepta nuevas respuestas.',
    somethingMissingTitle: 'Falta algo',
    somethingMissingMessage: 'Esta página no está disponible o no existe.',
    projectCompletedTitle: 'El proyecto se ha completado',
    projectCompletedMessage: 'Este estudio se ha completado y ya no acepta nuevas respuestas.',
    projectInDraftOrReadyStatusTitle: 'El proyecto no está publicado',
    projectInDraftOrReadyStatusMessage:
      'Como el estudio aún no se ha publicado, no acepta respuestas.',
    redirectingIn: 'Redirigiendo en',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Volver al panel',
    errorBoundaryTitle: 'Algo salió mal',
    errorBoundaryMessage:
      'Se produjo un error inesperado. Por favor, recarga la página para continuar.',
    errorBoundaryReloadButton: 'Recargar página',
    messageProcessingError:
      '¡Vaya! Parece que tenemos problemas para procesar tu mensaje. Por favor, intenta enviarlo de nuevo.',
    microphoneProblemTextChat:
      'Parece que hay un problema con tu micrófono. Por favor, escribe tus respuestas con el teclado.',
    microphoneProblemVoiceChat:
      'Parece que hay un problema con tu micrófono. Por favor, comprueba los permisos de tu navegador e inténtalo de nuevo.',
    responseTimeoutError: 'La respuesta está tardando demasiado. Por favor, inténtalo de nuevo.',
    noResponseError: 'No se detectó ninguna respuesta. Por favor, inténtalo de nuevo.',
    connectionInterrupted: 'Conexión interrumpida. Puedes volver a enviar tu mensaje.',
    videoStartError: 'No se pudo iniciar la grabación de vídeo. Por favor, inténtalo de nuevo.',
    videoCameraNotSupported:
      'La cámara no es compatible. Por favor, inténtalo con un navegador diferente.',
    videoPauseError: 'No se pudo pausar la grabación de vídeo.',
    videoResumeError: 'No se pudo reanudar la grabación de vídeo.',
    videoStopError: 'No se pudo detener la grabación de vídeo. Por favor, inténtalo de nuevo.',
    videoSwitchCameraError: 'No se pudo cambiar de cámara. Por favor, inténtalo de nuevo.',
    videoSendError: 'No se pudo enviar el mensaje de vídeo.',
    welcomeBack: '¡Bienvenido de nuevo!',
    unsupportedFileType:
      'Tipo de archivo no compatible. Por favor, sube un archivo JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Este archivo es demasiado grande. El tamaño máximo es de 200 MB.',
    audioCloseError: 'Error al cerrar el audio:',
  },
  sv: {
    telletDoesNotExistTitle: 'Tellet finns inte',
    projectDoesNotExistTitle: 'Projektet finns inte',
    projectDoesNotExistMessage: 'Vi är ledsna, men detta projekt finns inte.',
    maximumParticipantsTitle: 'Maximalt antal deltagare',
    maximumParticipantsMessage:
      'Denna studie har nått sitt maximala antal svar och tar inte längre emot nya svar.',
    somethingMissingTitle: 'Något saknas',
    somethingMissingMessage: 'Den här sidan är inte tillgänglig eller finns inte.',
    projectCompletedTitle: 'Projektet är slutfört',
    projectCompletedMessage: 'Denna studie har slutförts och tar inte längre emot nya svar.',
    projectInDraftOrReadyStatusTitle: 'Projektet är inte publicerat',
    projectInDraftOrReadyStatusMessage:
      'Eftersom studien ännu inte har publicerats tar den inte emot svar.',
    redirectingIn: 'Omdirigerar om',
    seconds: 'sekunder...',
    cancel: 'Avbryt',
    returnToPanel: 'Tillbaka till panelen',
    errorBoundaryTitle: 'Något gick fel',
    errorBoundaryMessage: 'Ett oväntat fel uppstod. Vänligen ladda om sidan för att fortsätta.',
    errorBoundaryReloadButton: 'Ladda om sidan',
    messageProcessingError:
      'Hoppsan! Det verkar som att vi har problem med att bearbeta ditt meddelande. Försök att skicka det igen.',
    microphoneProblemTextChat:
      'Det verkar finnas ett problem med din mikrofon. Vänligen skriv dina svar med tangentbordet.',
    microphoneProblemVoiceChat:
      'Det verkar finnas ett problem med din mikrofon. Vänligen kontrollera dina webbläsarbehörigheter och försök igen.',
    responseTimeoutError: 'Svaret tar för lång tid. Försök igen.',
    noResponseError: 'Inget svar upptäcktes. Försök igen.',
    connectionInterrupted: 'Anslutningen avbröts. Du kan skicka ditt meddelande igen.',
    videoStartError: 'Det gick inte att starta videoinspelningen. Försök igen.',
    videoCameraNotSupported: 'Kameran stöds inte. Försök med en annan webbläsare.',
    videoPauseError: 'Det gick inte att pausa videoinspelningen.',
    videoResumeError: 'Det gick inte att återuppta videoinspelningen.',
    videoStopError: 'Det gick inte att stoppa videoinspelningen. Försök igen.',
    videoSwitchCameraError: 'Det gick inte att byta kamera. Försök igen.',
    videoSendError: 'Det gick inte att skicka videomeddelandet.',
    welcomeBack: 'Välkommen tillbaka!',
    unsupportedFileType:
      'Filtypen stöds inte. Vänligen ladda upp en JPG-, PNG-, GIF- eller MP4-fil.',
    fileTooLarge: 'Den här filen är för stor. Maximal storlek är 200 MB.',
    audioCloseError: 'Fel vid stängning av ljud:',
  },
  sw: {
    telletDoesNotExistTitle: 'Tellet haipo',
    projectDoesNotExistTitle: 'Mradi haupo',
    projectDoesNotExistMessage: 'Samahani, mradi huu haupo.',
    maximumParticipantsTitle: 'Idadi ya juu ya washiriki',
    maximumParticipantsMessage:
      'Utafiti huu umefikia idadi ya juu ya majibu na haukubali tena majibu mapya.',
    somethingMissingTitle: 'Kuna kitu kinakosekana',
    somethingMissingMessage: 'Ukurasa huu haupatikani au haupo.',
    projectCompletedTitle: 'Mradi umekamilika',
    projectCompletedMessage: 'Utafiti huu umekamilika na haukubali tena majibu mapya.',
    projectInDraftOrReadyStatusTitle: 'Mradi haujachapishwa',
    projectInDraftOrReadyStatusMessage: 'Kwa kuwa utafiti bado haujachapishwa, haukubali majibu.',
    redirectingIn: 'Inaelekeza upya baada ya',
    seconds: 'sekunde...',
    cancel: 'Ghairi',
    returnToPanel: 'Rudi kwenye Paneli',
    errorBoundaryTitle: 'Hitilafu imetokea',
    errorBoundaryMessage:
      'Hitilafu isiyotarajiwa imetokea. Tafadhali pakia tena ukurasa ili kuendelea.',
    errorBoundaryReloadButton: 'Pakia ukurasa upya',
    messageProcessingError:
      'Lo! Inaonekana tuna tatizo la kuchakata ujumbe wako. Tafadhali jaribu kuutuma tena.',
    microphoneProblemTextChat:
      'Inaonekana kuna tatizo na maikrofoni yako. Tafadhali andika majibu yako kwa kutumia kibodi.',
    microphoneProblemVoiceChat:
      'Inaonekana kuna tatizo na maikrofoni yako. Tafadhali angalia ruhusa za kivinjari chako na ujaribu tena.',
    responseTimeoutError: 'Jibu linachukua muda mrefu sana. Tafadhali jaribu tena.',
    noResponseError: 'Hakuna jibu lililogunduliwa. Tafadhali jaribu tena.',
    connectionInterrupted: 'Muunganisho umekatizwa. Unaweza kutuma tena ujumbe wako.',
    videoStartError: 'Imeshindwa kuanza kurekodi video. Tafadhali jaribu tena.',
    videoCameraNotSupported: 'Kamera haitumiki. Tafadhali jaribu kwa kivinjari kingine.',
    videoPauseError: 'Imeshindwa kusitisha kurekodi video.',
    videoResumeError: 'Imeshindwa kuendelea kurekodi video.',
    videoStopError: 'Imeshindwa kusimamisha kurekodi video. Tafadhali jaribu tena.',
    videoSwitchCameraError: 'Imeshindwa kubadilisha kamera. Tafadhali jaribu tena.',
    videoSendError: 'Imeshindwa kutuma ujumbe wa video.',
    welcomeBack: 'Karibu tena!',
    unsupportedFileType: 'Aina ya faili haitumiki. Tafadhali pakia faili ya JPG, PNG, GIF au MP4.',
    fileTooLarge: 'Faili hili ni kubwa mno. Ukubwa wa juu zaidi ni MB 200.',
    audioCloseError: 'Hitilafu ya kufunga sauti:',
  },
  ta: {
    telletDoesNotExistTitle: 'Tellet இல்லை',
    projectDoesNotExistTitle: 'திட்டம் இல்லை',
    projectDoesNotExistMessage: 'மன்னிக்கவும், இந்தத் திட்டம் இல்லை.',
    maximumParticipantsTitle: 'அதிகபட்ச பங்கேற்பாளர்கள் எண்ணிக்கை',
    maximumParticipantsMessage:
      'இந்த ஆய்வு அதிகபட்ச பதில்களின் எண்ணிக்கையை அடைந்துள்ளது, இனி புதிய பதில்களை ஏற்காது.',
    somethingMissingTitle: 'ஏதோ காணவில்லை',
    somethingMissingMessage: 'இந்தப் பக்கம் கிடைக்கவில்லை அல்லது இல்லை.',
    projectCompletedTitle: 'திட்டம் முடிக்கப்பட்டது',
    projectCompletedMessage: 'இந்த ஆய்வு முடிக்கப்பட்டது, இனி புதிய பதில்களை ஏற்காது.',
    projectInDraftOrReadyStatusTitle: 'திட்டம் வெளியிடப்படவில்லை',
    projectInDraftOrReadyStatusMessage: 'ஆய்வு இன்னும் வெளியிடப்படாததால், இது பதில்களை ஏற்காது.',
    redirectingIn: 'திசைதிருப்பப்படுகிறது',
    seconds: 'வினாடிகளில்...',
    cancel: 'ரத்துசெய்',
    returnToPanel: 'பேனலுக்குத் திரும்பு',
    errorBoundaryTitle: 'ஏதோ தவறு நடந்துவிட்டது',
    errorBoundaryMessage: 'எதிர்பாராத பிழை ஏற்பட்டது. தொடர பக்கத்தை மீண்டும் ஏற்றவும்.',
    errorBoundaryReloadButton: 'பக்கத்தை மீண்டும் ஏற்று',
    messageProcessingError:
      'அச்சச்சோ! உங்கள் செய்தியைச் செயலாக்குவதில் சிக்கல் உள்ளது போல் தெரிகிறது. தயவுசெய்து அதை மீண்டும் அனுப்ப முயற்சிக்கவும்.',
    microphoneProblemTextChat:
      'உங்கள் மைக்ரோஃபோனில் சிக்கல் இருப்பதாகத் தெரிகிறது. விசைப்பலகையைப் பயன்படுத்தி உங்கள் பதில்களைத் தட்டச்சு செய்யவும்.',
    microphoneProblemVoiceChat:
      'உங்கள் மைக்ரோஃபோனில் சிக்கல் இருப்பதாகத் தெரிகிறது. உங்கள் உலாவி அனுமதிகளைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.',
    responseTimeoutError: 'பதிலளிக்க அதிக நேரம் எடுக்கிறது. மீண்டும் முயற்சிக்கவும்.',
    noResponseError: 'எந்தப் பதிலும் கண்டறியப்படவில்லை. மீண்டும் முயற்சிக்கவும்.',
    connectionInterrupted: 'இணைப்பு தடைபட்டது. உங்கள் செய்தியை மீண்டும் அனுப்பலாம்.',
    videoStartError: 'வீடியோ பதிவைத் தொடங்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoCameraNotSupported: 'கேமரா ஆதரிக்கப்படவில்லை. வேறு உலாவியில் முயற்சிக்கவும்.',
    videoPauseError: 'வீடியோ பதிவை இடைநிறுத்த முடியவில்லை.',
    videoResumeError: 'வீடியோ பதிவை மீண்டும் தொடங்க முடியவில்லை.',
    videoStopError: 'வீடியோ பதிவை நிறுத்த முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoSwitchCameraError: 'கேமராவை மாற்ற முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoSendError: 'வீடியோ செய்தியை அனுப்ப முடியவில்லை.',
    welcomeBack: 'மீண்டும் வரவேற்கிறோம்!',
    unsupportedFileType:
      'ஆதரிக்கப்படாத கோப்பு வகை. JPG, PNG, GIF அல்லது MP4 கோப்பைப் பதிவேற்றவும்.',
    fileTooLarge: 'இந்தக் கோப்பு மிகப் பெரியது. அதிகபட்ச அளவு 200 MB.',
    audioCloseError: 'ஆடியோவை மூடுவதில் பிழை:',
  },
  te: {
    telletDoesNotExistTitle: 'Tellet లేదు',
    projectDoesNotExistTitle: 'ప్రాజెక్ట్ లేదు',
    projectDoesNotExistMessage: 'క్షమించండి, ఈ ప్రాజెక్ట్ లేదు.',
    maximumParticipantsTitle: 'గరిష్ట పాల్గొనేవారి సంఖ్య',
    maximumParticipantsMessage:
      'ఈ అధ్యయనం గరిష్ట సమాధానాల సంఖ్యను చేరుకుంది మరియు ఇకపై కొత్త సమాధానాలను స్వీకరించడం లేదు.',
    somethingMissingTitle: 'ఏదో లోపించింది',
    somethingMissingMessage: 'ఈ పేజీ అందుబాటులో లేదు లేదా లేదు.',
    projectCompletedTitle: 'ప్రాజెక్ట్ పూర్తయింది',
    projectCompletedMessage: 'ఈ అధ్యయనం పూర్తయింది మరియు ఇకపై కొత్త సమాధానాలను స్వీకరించడం లేదు.',
    projectInDraftOrReadyStatusTitle: 'ప్రాజెక్ట్ ప్రచురించబడలేదు',
    projectInDraftOrReadyStatusMessage:
      'అధ్యయనం ఇంకా ప్రచురించబడనందున, ఇది సమాధానాలను స్వీకరించడం లేదు.',
    redirectingIn: 'దారి మళ్లించబడుతోంది',
    seconds: 'సెకన్లలో...',
    cancel: 'రద్దు చేయి',
    returnToPanel: 'ప్యానెల్‌కు తిరిగి వెళ్లండి',
    errorBoundaryTitle: 'ఏదో తప్పు జరిగింది',
    errorBoundaryMessage:
      'ఊహించని లోపం సంభవించింది. కొనసాగించడానికి దయచేసి పేజీని మళ్లీ లోడ్ చేయండి.',
    errorBoundaryReloadButton: 'పేజీని మళ్లీ లోడ్ చేయండి',
    messageProcessingError:
      'అయ్యో! మీ సందేశాన్ని ప్రాసెస్ చేయడంలో మాకు సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి దాన్ని మళ్లీ పంపడానికి ప్రయత్నించండి.',
    microphoneProblemTextChat:
      'మీ మైక్రోఫోన్‌లో సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి కీబోర్డ్ బటన్ ఉపయోగించి మీ సమాధానాలను టైప్ చేయండి.',
    microphoneProblemVoiceChat:
      'మీ మైక్రోఫోన్‌లో సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి మీ బ్రౌజర్ అనుమతులను తనిఖీ చేసి మళ్లీ ప్రయత్నించండి.',
    responseTimeoutError: 'ప్రతిస్పందించడానికి ఎక్కువ సమయం పడుతోంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    noResponseError: 'ఎలాంటి ప్రతిస్పందన కనుగొనబడలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
    connectionInterrupted: 'కనెక్షన్ అంతరాయం కలిగింది. మీరు మీ సందేశాన్ని మళ్లీ పంపవచ్చు.',
    videoStartError: 'వీడియో రికార్డింగ్ ప్రారంభించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoCameraNotSupported: 'కెమెరా సపోర్ట్ చేయబడదు. దయచేసి వేరే బ్రౌజర్‌తో ప్రయత్నించండి.',
    videoPauseError: 'వీడియో రికార్డింగ్‌ను విరామం చేయడంలో విఫలమైంది.',
    videoResumeError: 'వీడియో రికార్డింగ్‌ను మళ్లీ ప్రారంభించడంలో విఫలమైంది.',
    videoStopError: 'వీడియో రికార్డింగ్‌ను ఆపడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoSwitchCameraError: 'కెమెరాను మార్చడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoSendError: 'వీడియో సందేశం పంపడంలో విఫలమైంది.',
    welcomeBack: 'తిరిగి స్వాగతం!',
    unsupportedFileType:
      'సపోర్ట్ చేయని ఫైల్ రకం. దయచేసి JPG, PNG, GIF లేదా MP4 ఫైల్‌ను అప్‌లోడ్ చేయండి.',
    fileTooLarge: 'ఈ ఫైల్ చాలా పెద్దది. గరిష్ఠ పరిమాణం 200 MB.',
    audioCloseError: 'ఆడియో మూసివేయడంలో లోపం:',
  },
  th: {
    telletDoesNotExistTitle: 'ไม่พบ Tellet',
    projectDoesNotExistTitle: 'ไม่พบโครงการ',
    projectDoesNotExistMessage: 'ขออภัย ไม่พบโครงการนี้',
    maximumParticipantsTitle: 'จำนวนผู้เข้าร่วมสูงสุด',
    maximumParticipantsMessage:
      'การศึกษานี้มีจำนวนคำตอบถึงขีดจำกัดสูงสุดแล้ว และไม่รับคำตอบใหม่อีกต่อไป',
    somethingMissingTitle: 'มีบางอย่างขาดหายไป',
    somethingMissingMessage: 'หน้านี้ไม่พร้อมใช้งานหรือไม่มีอยู่',
    projectCompletedTitle: 'โครงการเสร็จสมบูรณ์แล้ว',
    projectCompletedMessage: 'การศึกษานี้เสร็จสมบูรณ์แล้ว และไม่รับคำตอบใหม่อีกต่อไป',
    projectInDraftOrReadyStatusTitle: 'โครงการยังไม่ได้เผยแพร่',
    projectInDraftOrReadyStatusMessage: 'เนื่องจากการศึกษายังไม่ได้เผยแพร่ จึงไม่รับคำตอบ',
    redirectingIn: 'กำลังเปลี่ยนเส้นทางใน',
    seconds: 'วินาที...',
    cancel: 'ยกเลิก',
    returnToPanel: 'กลับไปยังแผงควบคุม',
    errorBoundaryTitle: 'เกิดข้อผิดพลาด',
    errorBoundaryMessage: 'เกิดข้อผิดพลาดที่ไม่คาดคิด โปรดรีโหลดหน้าเว็บเพื่อดำเนินการต่อ',
    errorBoundaryReloadButton: 'รีโหลดหน้า',
    messageProcessingError:
      'อุ๊ปส์! ดูเหมือนเรากำลังมีปัญหาในการประมวลผลข้อความของคุณ โปรดลองส่งอีกครั้ง',
    microphoneProblemTextChat:
      'ดูเหมือนจะมีปัญหากับไมโครโฟนของคุณ โปรดพิมพ์คำตอบของคุณโดยใช้แป้นพิมพ์',
    microphoneProblemVoiceChat:
      'ดูเหมือนจะมีปัญหากับไมโครโฟนของคุณ โปรดตรวจสอบการอนุญาตของเบราว์เซอร์และลองอีกครั้ง',
    responseTimeoutError: 'การตอบกลับใช้เวลานานเกินไป โปรดลองอีกครั้ง',
    noResponseError: 'ไม่พบการตอบกลับ โปรดลองอีกครั้ง',
    connectionInterrupted: 'การเชื่อมต่อขาดหาย คุณสามารถส่งข้อความของคุณอีกครั้งได้',
    videoStartError: 'ไม่สามารถเริ่มบันทึกวิดีโอได้ โปรดลองอีกครั้ง',
    videoCameraNotSupported: 'ไม่รองรับกล้อง โปรดลองใช้เบราว์เซอร์อื่น',
    videoPauseError: 'ไม่สามารถหยุดบันทึกวิดีโอชั่วคราวได้',
    videoResumeError: 'ไม่สามารถบันทึกวิดีโอต่อได้',
    videoStopError: 'ไม่สามารถหยุดบันทึกวิดีโอได้ โปรดลองอีกครั้ง',
    videoSwitchCameraError: 'ไม่สามารถสลับกล้องได้ โปรดลองอีกครั้ง',
    videoSendError: 'ไม่สามารถส่งข้อความวิดีโอได้',
    welcomeBack: 'ยินดีต้อนรับกลับ!',
    unsupportedFileType: 'ประเภทไฟล์ไม่รองรับ โปรดอัปโหลดไฟล์ JPG, PNG, GIF หรือ MP4',
    fileTooLarge: 'ไฟล์นี้มีขนาดใหญ่เกินไป ขนาดสูงสุดคือ 200 MB',
    audioCloseError: 'ข้อผิดพลาดในการปิดเสียง:',
  },
  tr: {
    telletDoesNotExistTitle: 'Tellet mevcut değil',
    projectDoesNotExistTitle: 'Proje mevcut değil',
    projectDoesNotExistMessage: 'Üzgünüz, bu proje mevcut değil.',
    maximumParticipantsTitle: 'Maksimum katılımcı sayısı',
    maximumParticipantsMessage:
      'Bu çalışma maksimum yanıt sayısına ulaştı ve artık yeni yanıtları kabul etmiyor.',
    somethingMissingTitle: 'Bir şeyler eksik',
    somethingMissingMessage: 'Bu sayfa kullanılamıyor veya mevcut değil.',
    projectCompletedTitle: 'Proje tamamlandı',
    projectCompletedMessage: 'Bu çalışma tamamlandı ve artık yeni yanıtları kabul etmiyor.',
    projectInDraftOrReadyStatusTitle: 'Proje yayınlanmadı',
    projectInDraftOrReadyStatusMessage: 'Çalışma henüz yayınlanmadığı için yanıt kabul etmiyor.',
    redirectingIn: 'Yönlendiriliyor:',
    seconds: 'saniye...',
    cancel: 'İptal',
    returnToPanel: 'Panele Dön',
    errorBoundaryTitle: 'Bir şeyler ters gitti',
    errorBoundaryMessage:
      'Beklenmeyen bir hata oluştu. Devam etmek için lütfen sayfayı yeniden yükleyin.',
    errorBoundaryReloadButton: 'Sayfayı yeniden yükle',
    messageProcessingError:
      'Hoppala! Mesajınızı işlemede sorun yaşıyoruz gibi görünüyor. Lütfen tekrar göndermeyi deneyin.',
    microphoneProblemTextChat:
      'Mikrofonunuzla ilgili bir sorun var gibi görünüyor. Lütfen cevaplarınızı klavye ile yazın.',
    microphoneProblemVoiceChat:
      'Mikrofonunuzla ilgili bir sorun var gibi görünüyor. Lütfen tarayıcı izinlerinizi kontrol edip tekrar deneyin.',
    responseTimeoutError: 'Yanıt çok uzun sürüyor. Lütfen tekrar deneyin.',
    noResponseError: 'Yanıt algılanmadı. Lütfen tekrar deneyin.',
    connectionInterrupted: 'Bağlantı kesildi. Mesajınızı tekrar gönderebilirsiniz.',
    videoStartError: 'Video kaydı başlatılamadı. Lütfen tekrar deneyin.',
    videoCameraNotSupported: 'Kamera desteklenmiyor. Lütfen farklı bir tarayıcı ile deneyin.',
    videoPauseError: 'Video kaydı duraklatılamadı.',
    videoResumeError: 'Video kaydı devam ettirilemedi.',
    videoStopError: 'Video kaydı durdurulamadı. Lütfen tekrar deneyin.',
    videoSwitchCameraError: 'Kamera değiştirilemedi. Lütfen tekrar deneyin.',
    videoSendError: 'Video mesajı gönderilemedi.',
    welcomeBack: 'Tekrar hoş geldiniz!',
    unsupportedFileType:
      'Desteklenmeyen dosya türü. Lütfen JPG, PNG, GIF veya MP4 dosyası yükleyin.',
    fileTooLarge: 'Bu dosya çok büyük. Maksimum boyut 200 MB’tır.',
    audioCloseError: 'Ses kapatılırken hata:',
  },
  uk: {
    telletDoesNotExistTitle: 'Tellet не існує',
    projectDoesNotExistTitle: 'Проєкт не існує',
    projectDoesNotExistMessage: 'На жаль, цей проєкт не існує.',
    maximumParticipantsTitle: 'Максимальна кількість учасників',
    maximumParticipantsMessage:
      'Це дослідження досягло максимальної кількості відповідей і більше не приймає нові відповіді.',
    somethingMissingTitle: 'Щось відсутнє',
    somethingMissingMessage: 'Ця сторінка недоступна або не існує.',
    projectCompletedTitle: 'Проєкт завершено',
    projectCompletedMessage: 'Це дослідження завершено і більше не приймає нові відповіді.',
    projectInDraftOrReadyStatusTitle: 'Проєкт не опубліковано',
    projectInDraftOrReadyStatusMessage:
      'Оскільки дослідження ще не опубліковано, воно не приймає відповідей.',
    redirectingIn: 'Перенаправлення через',
    seconds: 'секунд...',
    cancel: 'Скасувати',
    returnToPanel: 'Повернутися на панель',
    errorBoundaryTitle: 'Щось пішло не так',
    errorBoundaryMessage:
      'Сталася непередбачена помилка. Будь ласка, перезавантажте сторінку, щоб продовжити.',
    errorBoundaryReloadButton: 'Перезавантажити сторінку',
    messageProcessingError:
      'Ой! Схоже, у нас проблеми з обробкою вашого повідомлення. Будь ласка, спробуйте надіслати його ще раз.',
    microphoneProblemTextChat:
      'Схоже, виникла проблема з вашим мікрофоном. Будь ласка, введіть свої відповіді за допомогою клавіатури.',
    microphoneProblemVoiceChat:
      'Схоже, виникла проблема з вашим мікрофоном. Будь ласка, перевірте дозволи браузера та спробуйте знову.',
    responseTimeoutError: 'Відповідь займає забагато часу. Будь ласка, спробуйте ще раз.',
    noResponseError: 'Відповідь не виявлено. Будь ласка, спробуйте ще раз.',
    connectionInterrupted: "З'єднання перервано. Ви можете повторно надіслати своє повідомлення.",
    videoStartError: 'Не вдалося розпочати запис відео. Будь ласка, спробуйте ще раз.',
    videoCameraNotSupported: 'Камера не підтримується. Будь ласка, спробуйте інший браузер.',
    videoPauseError: 'Не вдалося призупинити запис відео.',
    videoResumeError: 'Не вдалося відновити запис відео.',
    videoStopError: 'Не вдалося зупинити запис відео. Будь ласка, спробуйте ще раз.',
    videoSwitchCameraError: 'Не вдалося переключити камеру. Будь ласка, спробуйте ще раз.',
    videoSendError: 'Не вдалося надіслати відеоповідомлення.',
    welcomeBack: 'З поверненням!',
    unsupportedFileType:
      'Непідтримуваний тип файлу. Будь ласка, завантажте файл JPG, PNG, GIF або MP4.',
    fileTooLarge: 'Цей файл завеликий. Максимальний розмір — 200 МБ.',
    audioCloseError: 'Помилка закриття аудіо:',
  },
  ur: {
    telletDoesNotExistTitle: 'Tellet موجود نہیں ہے',
    projectDoesNotExistTitle: 'پروجیکٹ موجود نہیں ہے',
    projectDoesNotExistMessage: 'معذرت، یہ پروجیکٹ موجود نہیں ہے۔',
    maximumParticipantsTitle: 'شرکاء کی زیادہ سے زیادہ تعداد',
    maximumParticipantsMessage:
      'یہ مطالعہ جوابات کی زیادہ سے زیادہ تعداد تک پہنچ چکا ہے اور اب نئے جوابات قبول نہیں کر رہا۔',
    somethingMissingTitle: 'کچھ غائب ہے',
    somethingMissingMessage: 'یہ صفحہ دستیاب نہیں ہے یا موجود نہیں ہے۔',
    projectCompletedTitle: 'پروجیکٹ مکمل ہو گیا',
    projectCompletedMessage: 'یہ مطالعہ مکمل ہو چکا ہے اور اب نئے جوابات قبول نہیں کر رہا۔',
    projectInDraftOrReadyStatusTitle: 'پروجیکٹ شائع نہیں ہوا',
    projectInDraftOrReadyStatusMessage:
      'چونکہ مطالعہ ابھی شائع نہیں ہوا، یہ جوابات قبول نہیں کر رہا۔',
    redirectingIn: 'ری ڈائریکٹ ہو رہا ہے',
    seconds: 'سیکنڈ میں...',
    cancel: 'منسوخ کریں',
    returnToPanel: 'پینل پر واپس جائیں',
    errorBoundaryTitle: 'کچھ غلط ہو گیا',
    errorBoundaryMessage:
      'ایک غیر متوقع خرابی پیش آ گئی۔ براہ کرم جاری رکھنے کے لیے صفحہ دوبارہ لوڈ کریں۔',
    errorBoundaryReloadButton: 'صفحہ دوبارہ لوڈ کریں',
    messageProcessingError:
      'اوہ! لگتا ہے ہمیں آپ کے پیغام پر کارروائی کرنے میں دشواری ہو رہی ہے۔ براہ کرم اسے دوبارہ بھیجنے کی کوشش کریں۔',
    microphoneProblemTextChat:
      'لگتا ہے آپ کے مائیکروفون میں کوئی مسئلہ ہے۔ براہ کرم کی بورڈ کا استعمال کرتے ہوئے اپنے جوابات ٹائپ کریں۔',
    microphoneProblemVoiceChat:
      'لگتا ہے آپ کے مائیکروفون میں کوئی مسئلہ ہے۔ براہ کرم اپنے براؤزر کی اجازتیں چیک کریں اور دوبارہ کوشش کریں۔',
    responseTimeoutError: 'جواب میں بہت زیادہ وقت لگ رہا ہے۔ براہ کرم دوبارہ کوشش کریں۔',
    noResponseError: 'کوئی جواب موصول نہیں ہوا۔ براہ کرم دوبارہ کوشش کریں۔',
    connectionInterrupted: 'کنکشن منقطع ہو گیا۔ آپ اپنا پیغام دوبارہ بھیج سکتے ہیں۔',
    videoStartError: 'ویڈیو ریکارڈنگ شروع کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoCameraNotSupported:
      'کیمرہ تعاون یافتہ نہیں ہے۔ براہ کرم کسی مختلف براؤزر کے ساتھ کوشش کریں۔',
    videoPauseError: 'ویڈیو ریکارڈنگ روکنے میں ناکامی۔',
    videoResumeError: 'ویڈیو ریکارڈنگ دوبارہ شروع کرنے میں ناکامی۔',
    videoStopError: 'ویڈیو ریکارڈنگ بند کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoSwitchCameraError: 'کیمرہ تبدیل کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoSendError: 'ویڈیو پیغام بھیجنے میں ناکامی۔',
    welcomeBack: 'واپسی پر خوش آمدید!',
    unsupportedFileType:
      'غیر تعاون یافتہ فائل کی قسم۔ براہ کرم JPG، PNG، GIF یا MP4 فائل اپ لوڈ کریں۔',
    fileTooLarge: 'یہ فائل بہت بڑی ہے۔ زیادہ سے زیادہ سائز 200 MB ہے۔',
    audioCloseError: 'آڈیو بند کرنے میں خرابی:',
  },
  uz: {
    telletDoesNotExistTitle: 'Tellet mavjud emas',
    projectDoesNotExistTitle: 'Loyiha mavjud emas',
    projectDoesNotExistMessage: 'Kechirasiz, bu loyiha mavjud emas.',
    maximumParticipantsTitle: 'Ishtirokchilarning maksimal soni',
    maximumParticipantsMessage:
      'Ushbu tadqiqot javoblarning maksimal soniga yetdi va endi yangi javoblarni qabul qilmaydi.',
    somethingMissingTitle: 'Nimadir yetishmayapti',
    somethingMissingMessage: 'Ushbu sahifa mavjud emas yoki mavjud emas.',
    projectCompletedTitle: 'Loyiha yakunlandi',
    projectCompletedMessage: 'Ushbu tadqiqot yakunlandi va endi yangi javoblarni qabul qilmaydi.',
    projectInDraftOrReadyStatusTitle: 'Loyiha nashr etilmagan',
    projectInDraftOrReadyStatusMessage:
      'Tadqiqot hali nashr etilmaganligi sababli, u javoblarni qabul qilmaydi.',
    redirectingIn: "Qayta yo'naltirish",
    seconds: "soniyadan so'ng...",
    cancel: 'Bekor qilish',
    returnToPanel: 'Panelga qaytish',
    errorBoundaryTitle: "Nimadir noto'g'ri ketdi",
    errorBoundaryMessage: 'Kutilmagan xatolik yuz berdi. Davom etish uchun sahifani qayta yuklang.',
    errorBoundaryReloadButton: 'Sahifani qayta yuklash',
    messageProcessingError:
      "Voy! Xabaringizni qayta ishlashda muammo bor shekilli. Iltimos, uni qayta yuborib ko'ring.",
    microphoneProblemTextChat:
      'Mikrofoningizda muammo bor shekilli. Iltimos, javoblaringizni klaviatura yordamida yozing.',
    microphoneProblemVoiceChat:
      "Mikrofoningizda muammo bor shekilli. Iltimos, brauzer ruxsatlarini tekshiring va qayta urinib ko'ring.",
    responseTimeoutError: "Javob juda uzoq vaqt olmoqda. Iltimos, qayta urinib ko'ring.",
    noResponseError: "Hech qanday javob aniqlanmadi. Iltimos, qayta urinib ko'ring.",
    connectionInterrupted: 'Aloqa uzildi. Xabaringizni qayta yuborishingiz mumkin.',
    videoStartError: "Video yozishni boshlab bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoCameraNotSupported:
      "Kamera qo'llab-quvvatlanmaydi. Iltimos, boshqa brauzer bilan urinib ko'ring.",
    videoPauseError: "Video yozishni to'xtatib bo'lmadi.",
    videoResumeError: "Video yozishni davom ettirib bo'lmadi.",
    videoStopError: "Video yozishni to'xtatib bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoSwitchCameraError: "Kamerani almashtirib bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoSendError: "Video xabarni yuborib bo'lmadi.",
    welcomeBack: 'Xush kelibsiz!',
    unsupportedFileType:
      "Qo'llab-quvvatlanmaydigan fayl turi. Iltimos, JPG, PNG, GIF yoki MP4 faylini yuklang.",
    fileTooLarge: 'Bu fayl juda katta. Maksimal hajm — 200 MB.',
    audioCloseError: 'Audioni yopishda xatolik:',
  },
  vi: {
    telletDoesNotExistTitle: 'Tellet không tồn tại',
    projectDoesNotExistTitle: 'Dự án không tồn tại',
    projectDoesNotExistMessage: 'Chúng tôi xin lỗi nhưng dự án này không tồn tại.',
    maximumParticipantsTitle: 'Số lượng người tham gia tối đa',
    maximumParticipantsMessage:
      'Nghiên cứu này đã đạt đến số lượng phản hồi tối đa và không còn chấp nhận phản hồi mới nữa.',
    somethingMissingTitle: 'Có gì đó bị thiếu',
    somethingMissingMessage: 'Trang này không khả dụng hoặc không tồn tại.',
    projectCompletedTitle: 'Dự án đã hoàn tất',
    projectCompletedMessage: 'Nghiên cứu này đã hoàn tất và không còn chấp nhận phản hồi mới nữa.',
    projectInDraftOrReadyStatusTitle: 'Dự án chưa được xuất bản',
    projectInDraftOrReadyStatusMessage:
      'Vì nghiên cứu chưa được xuất bản, nên không chấp nhận phản hồi.',
    redirectingIn: 'Đang chuyển hướng sau',
    seconds: 'giây...',
    cancel: 'Hủy',
    returnToPanel: 'Quay lại Bảng điều khiển',
    errorBoundaryTitle: 'Đã xảy ra lỗi',
    errorBoundaryMessage: 'Đã xảy ra lỗi không mong muốn. Vui lòng tải lại trang để tiếp tục.',
    errorBoundaryReloadButton: 'Tải lại trang',
    messageProcessingError:
      'Ối! Có vẻ như chúng tôi đang gặp sự cố khi xử lý tin nhắn của bạn. Vui lòng thử gửi lại.',
    microphoneProblemTextChat:
      'Có vẻ như có vấn đề với micrô của bạn. Vui lòng nhập câu trả lời của bạn bằng bàn phím.',
    microphoneProblemVoiceChat:
      'Có vẻ như có vấn đề với micrô của bạn. Vui lòng kiểm tra quyền trình duyệt và thử lại.',
    responseTimeoutError: 'Phản hồi mất quá nhiều thời gian. Vui lòng thử lại.',
    noResponseError: 'Không phát hiện phản hồi. Vui lòng thử lại.',
    connectionInterrupted: 'Kết nối bị gián đoạn. Bạn có thể gửi lại tin nhắn của mình.',
    videoStartError: 'Không thể bắt đầu ghi video. Vui lòng thử lại.',
    videoCameraNotSupported: 'Máy ảnh không được hỗ trợ. Vui lòng thử với trình duyệt khác.',
    videoPauseError: 'Không thể tạm dừng ghi video.',
    videoResumeError: 'Không thể tiếp tục ghi video.',
    videoStopError: 'Không thể dừng ghi video. Vui lòng thử lại.',
    videoSwitchCameraError: 'Không thể chuyển máy ảnh. Vui lòng thử lại.',
    videoSendError: 'Không thể gửi tin nhắn video.',
    welcomeBack: 'Chào mừng bạn trở lại!',
    unsupportedFileType: 'Loại tệp không được hỗ trợ. Vui lòng tải lên tệp JPG, PNG, GIF hoặc MP4.',
    fileTooLarge: 'Tệp này quá lớn. Kích thước tối đa là 200 MB.',
    audioCloseError: 'Lỗi khi đóng âm thanh:',
  },
  cy: {
    telletDoesNotExistTitle: 'Nid yw Tellet yn bodoli',
    projectDoesNotExistTitle: "Nid yw'r prosiect yn bodoli",
    projectDoesNotExistMessage: "Mae'n ddrwg gennym ond nid yw'r prosiect hwn yn bodoli.",
    maximumParticipantsTitle: 'Uchafswm nifer y cyfranogwyr',
    maximumParticipantsMessage:
      "Mae'r astudiaeth hon wedi cyrraedd ei huchafswm o ymatebion ac nid yw bellach yn derbyn ymatebion newydd.",
    somethingMissingTitle: 'Mae rhywbeth ar goll',
    somethingMissingMessage: "Nid yw'r dudalen hon ar gael neu nid yw'n bodoli.",
    projectCompletedTitle: "Mae'r prosiect wedi'i gwblhau",
    projectCompletedMessage:
      "Mae'r astudiaeth hon wedi'i chwblhau ac nid yw bellach yn derbyn ymatebion newydd.",
    projectInDraftOrReadyStatusTitle: "Nid yw'r prosiect wedi'i gyhoeddi",
    projectInDraftOrReadyStatusMessage:
      "Gan nad yw'r astudiaeth wedi'i chyhoeddi eto, nid yw'n derbyn ymatebion.",
    redirectingIn: 'Ailgyfeirio ymhen',
    seconds: 'eiliad...',
    cancel: 'Canslo',
    returnToPanel: "Dychwelyd i'r Panel",
    errorBoundaryTitle: "Aeth rhywbeth o'i le",
    errorBoundaryMessage: 'Digwyddodd gwall annisgwyl. Ailymlwythwch y dudalen i barhau.',
    errorBoundaryReloadButton: "Ailymlwytho'r dudalen",
    messageProcessingError:
      "Wps! Mae'n ymddangos ein bod yn cael trafferth prosesu eich neges. Ceisiwch ei hailanfon.",
    microphoneProblemTextChat:
      "Mae'n ymddangos bod problem gyda'ch meicroffon. Teipiwch eich atebion gan ddefnyddio'r bysellfwrdd.",
    microphoneProblemVoiceChat:
      "Mae'n ymddangos bod problem gyda'ch meicroffon. Gwiriwch ganiatâd eich porwr a cheisiwch eto.",
    responseTimeoutError: "Mae'r ymateb yn cymryd gormod o amser. Ceisiwch eto.",
    noResponseError: 'Ni chanfuwyd unrhyw ymateb. Ceisiwch eto.',
    connectionInterrupted: 'Torrwyd y cysylltiad. Gallwch ailanfon eich neges.',
    videoStartError: 'Methwyd dechrau recordio fideo. Ceisiwch eto.',
    videoCameraNotSupported: "Nid yw'r camera yn cael ei gefnogi. Ceisiwch gyda phorwr gwahanol.",
    videoPauseError: 'Methwyd oedi recordiad fideo.',
    videoResumeError: 'Methwyd ailddechrau recordiad fideo.',
    videoStopError: 'Methwyd stopio recordiad fideo. Ceisiwch eto.',
    videoSwitchCameraError: 'Methwyd newid camera. Ceisiwch eto.',
    videoSendError: 'Methwyd anfon neges fideo.',
    welcomeBack: "Croeso'n ôl!",
    unsupportedFileType: 'Math o ffeil heb ei gefnogi. Llwythwch ffeil JPG, PNG, GIF neu MP4.',
    fileTooLarge: 'Mae’r ffeil hon yn rhy fawr. Y maint mwyaf yw 200 MB.',
    audioCloseError: 'Gwall wrth gau sain:',
  },
  id: {
    telletDoesNotExistTitle: 'Tellet tidak ada',
    projectDoesNotExistTitle: 'Proyek tidak ada',
    projectDoesNotExistMessage: 'Mohon maaf, proyek ini tidak ada.',
    maximumParticipantsTitle: 'Jumlah maksimum peserta',
    maximumParticipantsMessage:
      'Studi ini telah mencapai jumlah maksimum tanggapan dan tidak lagi menerima tanggapan baru.',
    somethingMissingTitle: 'Ada yang hilang',
    somethingMissingMessage: 'Halaman ini tidak tersedia atau tidak ada.',
    projectCompletedTitle: 'Proyek telah selesai',
    projectCompletedMessage: 'Studi ini telah selesai dan tidak lagi menerima tanggapan baru.',
    projectInDraftOrReadyStatusTitle: 'Proyek belum dipublikasikan',
    projectInDraftOrReadyStatusMessage:
      'Karena studi belum dipublikasikan, proyek ini tidak menerima tanggapan.',
    redirectingIn: 'Mengalihkan dalam',
    seconds: 'detik...',
    cancel: 'Batal',
    returnToPanel: 'Kembali ke Panel',
    errorBoundaryTitle: 'Terjadi kesalahan',
    errorBoundaryMessage:
      'Terjadi kesalahan tak terduga. Silakan muat ulang halaman untuk melanjutkan.',
    errorBoundaryReloadButton: 'Muat ulang halaman',
    messageProcessingError:
      'Ups! Sepertinya kami mengalami masalah dalam memproses pesan Anda. Silakan coba kirim ulang.',
    microphoneProblemTextChat:
      'Sepertinya ada masalah dengan mikrofon Anda. Silakan ketik jawaban Anda menggunakan keyboard.',
    microphoneProblemVoiceChat:
      'Sepertinya ada masalah dengan mikrofon Anda. Silakan periksa izin browser Anda dan coba lagi.',
    responseTimeoutError: 'Respons terlalu lama. Silakan coba lagi.',
    noResponseError: 'Tidak ada respons terdeteksi. Silakan coba lagi.',
    connectionInterrupted: 'Koneksi terputus. Anda dapat mengirim ulang pesan Anda.',
    videoStartError: 'Gagal memulai perekaman video. Silakan coba lagi.',
    videoCameraNotSupported: 'Kamera tidak didukung. Silakan coba dengan browser lain.',
    videoPauseError: 'Gagal menjeda perekaman video.',
    videoResumeError: 'Gagal melanjutkan perekaman video.',
    videoStopError: 'Gagal menghentikan perekaman video. Silakan coba lagi.',
    videoSwitchCameraError: 'Gagal mengganti kamera. Silakan coba lagi.',
    videoSendError: 'Gagal mengirim pesan video.',
    welcomeBack: 'Selamat datang kembali!',
    unsupportedFileType: 'Jenis file tidak didukung. Silakan unggah file JPG, PNG, GIF, atau MP4.',
    fileTooLarge: 'File ini terlalu besar. Ukuran maksimum adalah 200 MB.',
    audioCloseError: 'Kesalahan saat menutup audio:',
  },
} satisfies Record<TranslationLanguage, RespondentUiTranslation>;

/**
 * Look up a localised `RespondentUiTranslation` for the given
 * languageCode. Falls back to English if the code is missing or a
 * non-canonical value (e.g. arriving from legacy data or a URL param).
 */
export function getRespondentUiTranslation(
  languageCode: string | undefined | null,
): RespondentUiTranslation {
  if (!languageCode) return RESPONDENT_UI_TRANSLATION.en;
  return (
    RESPONDENT_UI_TRANSLATION[languageCode as TranslationLanguage] ?? RESPONDENT_UI_TRANSLATION.en
  );
}
